import { component } from "picoapp";
import React, { useState, useEffect, useRef } from "react";
import { createRoot } from "react-dom/client";
import { createPortal } from "react-dom";
// import { Stage, Layer, Image as KonvaImage, Text as KonvaText, Group, Transformer } from "react-konva";
// import useImage from "use-image";
import { Cloudinary } from '@cloudinary/url-gen';
import { auto, scale } from '@cloudinary/url-gen/actions/resize';
import { autoGravity } from '@cloudinary/url-gen/qualifiers/gravity';
import { AdvancedImage } from '@cloudinary/react';
import opentype from 'opentype.js';
import { getViewportConfig } from './etch-viewport-config';
import {
  formatMoney,
  parseMoneyAmount,
  getCustomizerDesignFlags,
  calculateCustomizerTotal,
} from './pricing';
// Retail-parity styling (full customizer look) + brand tokens. These replace
// the legacy wholesale customizer.css look; the render below uses retail class
// names (hj-*, nc-*, customizer--mobile, etch-placement-guide, etc.).
import '../../../../../assets/customizer-brand-overrides.css';
import '../../../../../assets/customizer-retail.css';

let mysteryImg = "https://cdn.shopify.com/s/files/1/0110/7827/1033/files/iconmonstr-help-1.svg?v=1732310678";


// Instructions Component
const InstructionsComponent = () => (
  <div className="customizer-instructions-container">
    <div className="customizer-video-section">
      <video
        className="customizer-instructions-video"
        autoPlay
        muted
        loop
        playsInline
        src="https://cdn.shopify.com/videos/c/o/v/1d13754fd6a14d69b9cce92863e30d3d.mp4"
      />
    </div>
    <div className="customizer-instructions-content">
      <h2 className="customizer-instructions-title">HOW TO CUSTOMIZE:</h2>
      <div className="customizer-instruction-step">
        <div className="customizer-step-number">1</div>
        <p className="customizer-step-text">
          Pick your product – Choose from our collection of HydroJug bottles, tumblers, and accessories.
        </p>
      </div>
      <div className="customizer-instruction-step">
        <div className="customizer-step-number">2</div>
        <p className="customizer-step-text">
          Select your color and size to see your product preview.
        </p>
      </div>
      <div className="customizer-instruction-step">
        <div className="customizer-step-number">3</div>
        <p className="customizer-step-text">
          Add custom text, jersey designs, preset designs, or upload your own logo. Create your unique design with our customization tools.
        </p>
      </div>
      <div className="customizer-instruction-step">
        <div className="customizer-step-number">4</div>
        <p className="customizer-step-text">
          Review your design and add to cart to complete your custom HydroJug order.
        </p>
      </div>
    </div>
  </div>
);

// Global styles for scroll lock and animations (replaces styled-components createGlobalStyle)
function GlobalScrollLock() {
  return (
    <style>{`
      html.customizer-scroll-lock { overflow: hidden !important; }
      body.customizer-scroll-lock { overflow: hidden !important; }
      @keyframes slideDown {
        from { opacity: 0; transform: translateX(-50%) translateY(-20px); }
        to { opacity: 1; transform: translateX(-50%) translateY(0); }
      }
    `}</style>
  );
}

// Inline style helpers (replaces styled-components)
const ProductCard = React.forwardRef(function ProductCard({ $selected, children, ...props }, ref) {
  return (
    <button
      ref={ref}
      {...props}
      className={`hj-product-card ${$selected ? "hj-product-card--selected" : ""}`}
      style={{
        display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
        padding: "0.5rem",
        border: `1.5px solid ${$selected ? "var(--color-rich-black, #1A1A1A)" : "var(--color-stone, #E8E6E1)"}`,
        borderRadius: "8px",
        background: $selected ? "var(--color-rich-black, #1A1A1A)" : "rgba(245, 244, 242, 0.96)",
        cursor: "pointer", transition: "all 0.2s ease", minWidth: "90px", flexShrink: 1,
        ...props.style,
      }}
    >
      {children}
    </button>
  );
});

const ColorCard = React.forwardRef(function ColorCard({ $selected, children, ...props }, ref) {
  return (
    <button
      ref={ref}
      {...props}
      className={`hj-color-card ${$selected ? "hj-color-card--selected" : ""}`}
      style={{
        width: "50px", height: "50px",
        border: `2px solid ${$selected ? "var(--color-rich-black, #1A1A1A)" : "var(--color-stone, #E8E6E1)"}`,
        borderRadius: "50%", background: "rgba(245, 244, 242, 0.96)",
        cursor: "pointer", transition: "all 0.2s ease",
        display: "flex", alignItems: "center", justifyContent: "center",
        overflow: "hidden", flexShrink: 0,
        ...props.style,
      }}
    >
      {children}
    </button>
  );
});

function ProgressFill({ progress, ...props }) {
  return (
    <div
      {...props}
      style={{
        height: "100%",
        background: "linear-gradient(90deg, var(--color-charcoal, #333), var(--color-rich-black, #1A1A1A))",
        width: `${progress}%`, transition: "width 0.3s ease",
        ...props.style,
      }}
    />
  );
}

const ReviewButton = React.forwardRef(function ReviewButton({ $primary, children, ...props }, ref) {
  return (
    <button
      ref={ref}
      {...props}
      className={`hj-review-btn ${$primary ? "hj-review-btn--primary" : "hj-review-btn--secondary"}`}
      style={{
        padding: "0.875rem 2.5rem", fontSize: "14px", fontWeight: 600, borderRadius: "8px",
        cursor: "pointer", textTransform: "uppercase", letterSpacing: "0.08em",
        transition: "all 0.15s ease", fontFamily: "var(--font-sans, Inter, sans-serif)",
        ...($primary
          ? { background: "var(--color-rich-black, #1A1A1A)", color: "var(--color-off-white, #F5F4F2)", border: "none" }
          : { background: "rgba(245, 244, 242, 0.96)", color: "var(--color-rich-black, #1A1A1A)", border: "1px solid var(--color-stone, #E8E6E1)" }),
        ...props.style,
      }}
    >
      {children}
    </button>
  );
});

function FontPreviewCard({ $selected, children, ...props }) {
  return (
    <div
      {...props}
      className={`hj-font-card ${$selected ? "hj-font-card--selected" : ""}`}
      style={{
        display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
        padding: "1rem",
        border: `2px solid ${$selected ? "var(--color-rich-black, #1A1A1A)" : "var(--color-stone, #E8E6E1)"}`,
        borderRadius: "8px",
        background: $selected ? "var(--color-off-white, #F5F4F2)" : "rgba(245, 244, 242, 0.96)",
        cursor: "pointer", transition: "all 0.2s ease", textAlign: "center",
        ...props.style,
      }}
    >
      {children}
    </div>
  );
}

function PresetDesignCard({ $selected, children, ...props }) {
  return (
    <div
      {...props}
      className={`hj-preset-card ${$selected ? "hj-preset-card--selected" : ""}`}
      style={{
        padding: "0.25rem",
        border: `2px solid ${$selected ? "var(--color-rich-black, #1A1A1A)" : "var(--color-stone, #E8E6E1)"}`,
        borderRadius: "8px",
        background: $selected ? "var(--color-off-white, #F5F4F2)" : "rgba(245, 244, 242, 0.96)",
        cursor: "pointer", transition: "all 0.2s ease", textAlign: "center",
        ...props.style,
      }}
    >
      {children}
    </div>
  );
}

function FontCard({ $selected, children, ...props }) {
  return (
    <div
      {...props}
      className={`hj-font-card ${$selected ? "hj-font-card--selected" : ""}`}
      style={{
        padding: "1rem",
        border: `2px solid ${$selected ? "var(--color-rich-black, #1A1A1A)" : "var(--color-stone, #E8E6E1)"}`,
        borderRadius: "8px",
        background: $selected ? "var(--color-off-white, #F5F4F2)" : "rgba(245, 244, 242, 0.96)",
        cursor: "pointer", transition: "all 0.2s ease", textAlign: "center",
        ...props.style,
      }}
    >
      {children}
    </div>
  );
}

function SlideDrawer({ $isOpen, $isMobile, children, ...props }) {
  const mobileStyle = $isMobile
    ? {
        position: "fixed", top: "auto", bottom: 108, left: 0, right: 0,
        width: "100%", maxWidth: "100%", height: "auto", maxHeight: "45vh",
        borderRadius: "16px 16px 0 0", borderLeft: "none",
        boxShadow: "0 -4px 24px rgba(0, 0, 0, 0.12)",
        transform: $isOpen ? "translateY(0)" : "translateY(calc(100% + 108px))",
        transition: "transform 0.35s cubic-bezier(0.32, 0.72, 0, 1)",
        zIndex: 1000, overflowY: "auto", WebkitOverflowScrolling: "touch",
        background: "var(--color-off-white, #F5F4F2)",
      }
    : {
        position: "fixed", top: 0, right: $isOpen ? "0" : "-100%",
        width: "500px", maxWidth: "100%", height: "100vh",
        background: "var(--color-off-white, #F5F4F2)",
        borderLeft: "1px solid var(--color-stone, #E8E6E1)",
        boxShadow: "-4px 0 20px rgba(0, 0, 0, 0.1)",
        transition: "right 0.3s ease", zIndex: 1000, overflowY: "auto",
      };

  return (
    <div
      {...props}
      className={`hj-slide-drawer ${$isOpen ? "hj-slide-drawer--open" : ""} ${$isMobile ? "hj-slide-drawer--mobile" : ""}`}
      style={{ ...mobileStyle, ...props.style }}
    >
      {$isMobile && (
        <div style={{ display: "flex", justifyContent: "center", padding: "8px 0 0" }}>
          <div style={{ width: 36, height: 4, borderRadius: 2, background: "var(--color-stone, #D4D2CD)" }} />
        </div>
      )}
      {children}
    </div>
  );
}

function Overlay({ $isOpen, ...props }) {
  return (
    <div
      {...props}
      style={{
        position: "fixed", top: 0, left: 0, width: "100%", height: "100%",
        background: "rgba(0, 0, 0, 0.15)", zIndex: 999,
        opacity: $isOpen ? "1" : "0", visibility: $isOpen ? "visible" : "hidden",
        transition: "all 0.3s ease",
        ...props.style,
      }}
    />
  );
}

// ─── MOBILE STEP TAB BAR ────────────────────────────────────────────────────
function MobileStepTabBar({ activeTab, isOpen, onStepSelect, hasAnyDesign }) {
  const isStep1Active = isOpen && activeTab === "product";
  const isStep2Active =
    isOpen &&
    (activeTab === "design" || activeTab === "upload" || activeTab === "text" ||
      activeTab === "jersey" || activeTab === "preset");

  const stepButtonStyle = (active) => ({
    flex: 1, height: 44, display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
    background: active ? "var(--color-rich-black, #1A1A1A)" : "var(--color-off-white, #F5F4F2)",
    color: active ? "var(--color-off-white, #F5F4F2)" : "var(--color-rich-black, #1A1A1A)",
    border: active ? "1px solid var(--color-rich-black, #1A1A1A)" : "1px solid var(--color-stone, #E8E6E1)",
    borderRadius: 8, fontFamily: "var(--font-sans, 'Inter', sans-serif)",
    fontSize: 12, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase",
    cursor: "pointer", padding: "0 12px",
    transition: "background 0.15s ease, color 0.15s ease", position: "relative", whiteSpace: "nowrap",
  });

  return (
    <div
      className="customizer-mobile-tab-bar"
      style={{
        position: "fixed", bottom: 56, left: 0, right: 0, height: 52,
        display: "flex", alignItems: "center", gap: 8,
        background: "var(--color-off-white, #F5F4F2)",
        borderTop: "1px solid var(--color-stone, #E8E6E1)", zIndex: 1001, padding: "4px 8px",
      }}
    >
      <button type="button" onClick={() => onStepSelect("product")} style={stepButtonStyle(isStep1Active)}>
        Step 1: Product
      </button>
      <button type="button" onClick={() => onStepSelect("design")} style={stepButtonStyle(isStep2Active)}>
        <span>Step 2: Design</span>
        {hasAnyDesign && (
          <span aria-hidden="true" style={{ width: 7, height: 7, borderRadius: "50%", background: "#10B981", display: "inline-block" }} />
        )}
      </button>
    </div>
  );
}

// ─── MOBILE STICKY CART BAR ─────────────────────────────────────────────────
// Wholesale variant: shows the min-order-6 quantity stepper + Add to Bag.
function MobileStickyCart({ price, currencyCode, quantity, onQtyChange, onAddToCart, disabled, processing, addingToCart }) {
  const displayPrice = formatMoney(parseMoneyAmount(price), currencyCode);
  const isProcessing = processing === "processing" || processing === "threshold";
  const isDisabled = disabled || isProcessing || addingToCart;

  const qtyBtn = {
    width: 34, height: 34, borderRadius: 6, border: "1px solid var(--color-stone, #E8E6E1)",
    background: "var(--color-off-white, #F5F4F2)", fontSize: 18, lineHeight: 1, cursor: "pointer",
    display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
  };

  return (
    <div
      className="customizer-mobile-cart-bar"
      style={{
        position: "fixed", bottom: 0, left: 0, right: 0, height: 56,
        display: "flex", alignItems: "center", gap: 8, padding: "8px 16px",
        background: "var(--color-off-white, #F5F4F2)",
        borderTop: "1px solid var(--color-stone, #E8E6E1)", zIndex: 1002,
      }}
    >
      <button type="button" style={{ ...qtyBtn, opacity: quantity <= 6 ? 0.4 : 1 }} disabled={quantity <= 6} onClick={() => onQtyChange(Math.max(6, quantity - 1))}>−</button>
      <span style={{ minWidth: 24, textAlign: "center", fontSize: 14, fontWeight: 700 }}>{quantity}</span>
      <button type="button" style={qtyBtn} onClick={() => onQtyChange(quantity + 1)}>+</button>
      <button
        onClick={onAddToCart}
        disabled={isDisabled}
        style={{
          flex: 1, height: 40, borderRadius: 8, border: "none",
          background: isDisabled ? "#9CA3AF" : "var(--color-rich-black, #1A1A1A)",
          color: "var(--color-off-white, #F5F4F2)", fontSize: 12, fontWeight: 700,
          letterSpacing: "0.10em", textTransform: "uppercase",
          fontFamily: "var(--font-sans, 'Inter', sans-serif)",
          cursor: isDisabled ? "not-allowed" : "pointer",
          display: "flex", alignItems: "center", justifyContent: "center", transition: "background 0.15s ease",
        }}
      >
        {isProcessing ? "⏳ Processing..." : addingToCart ? "⏳ Adding..." : `Add to Bag — ${displayPrice}`}
      </button>
    </div>
  );
}

// Prefer a black-colored product variant; fall back to the first available.
function pickBlackOrFirst(products) {
  if (!products || products.length === 0) return null;
  const black = products.find(
    (p) =>
      /(^|[-_\s])black([-_\s]|$)/i.test(p.handle || "") ||
      /(^|[-_\s])black([-_\s]|$)/i.test(p.title || ""),
  );
  return black || products[0];
}

// Extract the color slug from a storefront PDP handle.
function extractColorSlug(handle) {
  if (!handle) return "";
  const TYPES =
    "(traveler|sport|coffee|coffee-traveler|coffee-travelers|everyday|everyday-tumbler|tumbler|ease2o|shaker|insulated-shaker|can-cooler|can-coolers|stainless)";
  let slug = handle.toLowerCase();
  slug = slug.replace(new RegExp(`-\\d+oz-${TYPES}$`, "i"), "");
  slug = slug.replace(new RegExp(`-${TYPES}-\\d+oz$`, "i"), "");
  slug = slug.replace(new RegExp(`-${TYPES}$`, "i"), "");
  slug = slug.replace(/-\d+oz$/i, "");
  slug = slug.replace(/-(slim|standard)$/i, "");
  return slug === handle.toLowerCase() ? "" : slug;
}

// Find the customizer product whose color matches a storefront PDP handle.
function findColorMatch(products, initialColorHandle) {
  if (!products?.length || !initialColorHandle) return null;
  const direct = products.find(
    (p) => p.handle === initialColorHandle || p.handle?.includes(initialColorHandle),
  );
  if (direct) return direct;
  const colorSlug = extractColorSlug(initialColorHandle);
  if (!colorSlug) return null;
  const handlePattern = new RegExp(
    `(^|-)${colorSlug.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}(-|$)`, "i",
  );
  const titlePhrase = colorSlug.replace(/-/g, " ").trim();
  return (
    products.find((p) => handlePattern.test(p.handle || "")) ||
    products.find((p) => (p.title || "").toLowerCase().includes(titlePhrase)) ||
    null
  );
}

// Etch Placement Guide — shows where the design will be etched
const EtchPlacementGuide = ({ productType, hasDesign, focusMode, etchZoneOverride }) => {
  const config = getViewportConfig(productType);
  const { width, height, top, left, right, margin } = etchZoneOverride || config.etchZone;
  return (
    <div
      className={`etch-placement-guide${hasDesign ? " etch-placement-guide--faded" : ""}${focusMode ? " etch-placement-guide--prominent" : ""}`}
      style={{
        position: "absolute", width, height: height || "auto", top,
        left: left || 0, right: right || 0, margin: margin || "0 auto",
        aspectRatio: height ? undefined : "1 / 1", pointerEvents: "none",
      }}
    />
  );
};

// Canvas Component
const ProductCanvas = ({ productImageUrl, customElements, focusMode, productType, hasDesign, etchZoneOverride }) => {
  const [canvasSize, setCanvasSize] = useState({ width: 600, height: 600 });
  const [isMobile, setIsMobile] = useState(false);
  const [imageSize, setImageSize] = useState({ width: 480, height: 480, x: 60, y: 60 });

  useEffect(() => {
    const updateCanvasSize = () => {
      const isMobileView = window.innerWidth <= 768;
      setIsMobile(isMobileView);
      if (isMobileView) {
        setCanvasSize({ width: 400, height: 400 });
      } else {
        setCanvasSize({ width: 600, height: 600 });
      }
    };
    updateCanvasSize();
    window.addEventListener('resize', updateCanvasSize);
    return () => window.removeEventListener('resize', updateCanvasSize);
  }, []);

  return (
    <div
      className={`customizer-canvas customizer-canvas-zoom${focusMode ? " customizer-canvas-zoom--focused" : ""}`}
      style={{
        // Fluid on mobile so the bottle stays centered inside the responsive
        // wrapper; fixed 600px on desktop where the side rail reserves space.
        width: isMobile ? "100%" : "600px",
        maxWidth: isMobile ? 400 : 600,
        aspectRatio: "1 / 1",
        height: isMobile ? "auto" : "600px",
        position: "relative",
        margin: "0 auto",
        overflow: "visible",
        transformOrigin: productType ? getViewportConfig(productType).focus.transformOrigin : "50% 50%",
      }}
    >
      {productImageUrl && (
        <img
          src={productImageUrl}
          alt="Product"
          className="customizer-product-image selectedImg"
          style={{ position: "absolute", width: "100%", height: "100%", objectFit: "contain" }}
        />
      )}

      {customElements.map((element, index) => {
        return (
          <div key={`element-${index}`} className={`custom-element-wrapper custom-element-${element.type}-${index}`}>
            {element.type === 'text' && (
              <div
                className={`custom-text-element custom-text-element-${index}`}
                style={{
                  position: "absolute",
                  left: element.x, top: element.y,
                  fontSize: element.fontSize, fontFamily: element.fontFamily, color: element.fill,
                  transformOrigin: "center", textAlign: "center", width: "auto", height: "auto",
                  transform: `translate(-50%, -50%) rotate(${element.rotation || 0}deg) translateZ(0)`,
                  WebkitTransform: `translate(-50%, -50%) rotate(${element.rotation || 0}deg) translateZ(0)`,
                  willChange: "transform",
                }}
              >
                {element.text}
              </div>
            )}
            {element.type === 'image' && (
              <img
                key={`img-${index}`}
                src={element.imageUrl}
                alt="Custom Element"
                className={`custom-image-element custom-image-element-${index}`}
                data-width={element.width}
                data-height={element.height}
                data-rotation={element.rotation}
                style={{
                  position: "absolute",
                  width: element.width || '150px', height: element.height || '150px',
                  top: element.top || '15%', left: element.left || 0, right: element.right || 0,
                  margin: element.margin || '0 auto',
                  transform: `rotate(${element.rotation || 0}deg)`,
                  WebkitTransform: `rotate(${element.rotation || 0}deg)`,
                  transformOrigin: "center", objectFit: "contain",
                  imageRendering: "-webkit-optimize-contrast",
                  filter: "contrast(1.05) brightness(1.02) saturate(1.1)",
                  backfaceVisibility: "hidden", WebkitBackfaceVisibility: "hidden",
                }}
                onError={(e) => { console.error('Image failed to load:', element.imageUrl, e); }}
                onLoad={() => { console.log('Image loaded successfully:', element.imageUrl); }}
              />
            )}
          </div>
        );
      })}

      {productType && (
        <EtchPlacementGuide
          productType={productType}
          hasDesign={hasDesign}
          focusMode={focusMode}
          etchZoneOverride={etchZoneOverride}
        />
      )}
    </div>
  );
};

// Custom Image Element Component - Commented out for static positioning
// const CustomImageElement = ({ element, index, onClick, onUpdate }) => {
//   const [image] = useImage(element.src || '');

//   return (
//     <KonvaImage
//       image={image}
//       x={element.x}
//       y={element.y}
//       width={element.width}
//       height={element.height}
//       rotation={element.rotation || 0}
//       onClick={onClick}
//       draggable
//       onDragEnd={(e) => {
//         onUpdate(index, {
//           ...element,
//           x: e.target.x(),
//           y: element.y()
//         });
//       }}
//       onTransformEnd={(e) => {
//         const node = e.target;
//         onUpdate(index, {
//           ...element,
//           x: node.x(),
//           y: node.y(),
//           scaleX: node.scaleX(),
//           scaleY: node.scaleY(),
//           rotation: node.rotation()
//         });
//       }}
//       className={`custom-image-element-${index}`}
//     />
//   );
// };

/**
 * Convert text into SVG paths (straight baseline or curved).
 * Returns only <path> and <g> elements, no <text>.
 */
export async function textToSVGPaths(text, fontUrl, fontSize = 100, options = {}) {
  const { x = 0, y = 0, fill = 'black', curve = null } = options;
  const font = await opentype.load(fontUrl);

  let paths = [];

  if (!curve) {
    // --- Straight baseline ---
    const path = font.getPath(text, x, y, fontSize);
    paths.push(`<path d="${path.toPathData(2)}" fill="${fill}" />`);
  } else {
    // --- Curved baseline using quadratic Bézier ---
    const { startX, startY, endX, endY, controlX, controlY } = curve;

    for (let i = 0; i < text.length; i++) {
      const t = text.length === 1 ? 0.5 : i / (text.length - 1);

      // Quadratic Bézier interpolation
      const bx = (1 - t) * (1 - t) * startX + 2 * (1 - t) * t * controlX + t * t * endX;
      const by = (1 - t) * (1 - t) * startY + 2 * (1 - t) * t * controlY + t * t * endY;

      // Tangent vector → rotation angle
      const dx = 2 * (1 - t) * (controlX - startX) + 2 * t * (endX - controlX);
      const dy = 2 * (1 - t) * (controlY - startY) + 2 * t * (endY - controlY);
      const angle = Math.atan2(dy, dx);

      // Glyph path for this character
      const glyph = font.charToGlyph(text[i]);
      const glyphPath = glyph.getPath(0, 0, fontSize);
      const d = glyphPath.toPathData(2);

      paths.push(`
        <g transform="translate(${bx}, ${by}) rotate(${(angle * 180) / Math.PI})">
          <path d="${d}" fill="${fill}" />
        </g>
      `);
    }
  }

  return paths.join('\n');
}

async function urlOrBlobToBase64(url) {
  const response = await fetch(url);
  const blob = await response.blob();
  
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const base64 = reader.result.split(',')[1]; // strip prefix
      resolve(base64);
    };
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}


// Main Customizer Component
function HydroCustomizer() {
  // Retail-render prop shims. The retail HydroCustomizer took these as props
  // (PDP deep-linking + external cart/price integration). The wholesale picoapp
  // mounts inline with no props, so they're inert defaults here.
  const pricing = {};
  const onProductChange = null;
  const onClose = null;
  const onCartAdd = null;
  const onCustomizationApplied = () => {};

  // State management
  const [collections, setCollections] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [selectedColor, setSelectedColor] = useState(null);
  const [productImageUrl, setProductImageUrl] = useState(null);
  const [customElements, setCustomElements] = useState([]);
  // Mirror of customElements as a ref so async regenerators (color-change
  // re-render, jersey debounce) can read the CURRENT value — in particular
  // the user's latest rotation — instead of a stale closure snapshot when
  // replacing the element. Kept in sync via the useEffect below.
  const customElementsRef = useRef(customElements);
  useEffect(() => {
    customElementsRef.current = customElements;
  }, [customElements]);
  // const [selectedElement, setSelectedElement] = useState(null); // Commented out for static positioning
  const [customText, setCustomText] = useState("");
  const [selectedFont, setSelectedFont] = useState("Arial");


  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("product"); // product (step 1), then upload (wholesale design step)
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  // Custom-Studio focus zoom (canvas zoom-to-etch) + responsive mobile layout.
  const [focusMode, setFocusMode] = useState(false);
  const [isMobileLayout, setIsMobileLayout] = useState(false);
  const [mobileStep, setMobileStep] = useState(1);
  // One-shot guard so rotating the device doesn't re-open a closed drawer.
  const didAutoOpenStep1Ref = useRef(false);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [showModerationErrorModal, setShowModerationErrorModal] = useState(false);
  const [isModerating, setIsModerating] = useState(false);
  const [originalLogoBase64, setOriginalLogoBase64] = useState(null);



  // Cloudinary setup
  const cld = new Cloudinary({ cloud: { cloudName: 'dgepygozk' } });

  // Logo processing state
  const [logoProcessingState, setLogoProcessingState] = useState('idle'); // idle, processing, processed, error
  const [processedLogoUrl, setProcessedLogoUrl] = useState(null);
  // Removed threshold slider - using fixed threshold for better UX
  const [originalLogoUrl, setOriginalLogoUrl] = useState(null);
  const [processingProgress, setProcessingProgress] = useState(0);
  // Store the processed canvas (without metallic effect) for fast color updates
  const processedLogoCanvasRef = useRef(null);
  // Store the base canvas for preset designs (without metallic effect) for fast color updates
  const presetDesignCanvasRef = useRef(null);
  // Cache the original Image element to avoid reloading on threshold/invert changes
  const originalLogoImageRef = useRef(null);
  // Store original image dimensions for consistent quality during threshold changes
  const originalLogoDimensionsRef = useRef(null);
  // Store display dimensions for consistent SVG sizing (prevents scaling on threshold changes)
  const displayDimensionsRef = useRef(null);
  // Ref for drawer element (for scroll locking)
  const drawerRef = useRef(null);
  // Cloudinary processing only happens when adding to cart
  const [processedBlob, setProcessedBlob] = useState(null);
  const [processedSize, setProcessedSize] = useState({ width: 0, height: 0 });
  const [addingToCart, setAddingToCart] = useState(false);
  const [thresholdValue, setThresholdValue] = useState(128);
  const [invertEtch, setInvertEtch] = useState(false);
  const [quantity, setQuantity] = useState(6); // Minimum quantity of 6
  const [cartError, setCartError] = useState(null); // Error message from cart
  
  // Loading modal state
  const [showLoadingModal, setShowLoadingModal] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  // Smooth, time-based fallback so the loading bar always climbs visibly even
  // when the scattered setUploadProgress() milestones don't paint as a fill.
  const [smoothUploadProgress, setSmoothUploadProgress] = useState(0);
  const thresholdDebounceRef = useRef(null);
  const fullResBlobDebounceRef = useRef(null);
  const isProcessingThresholdRef = useRef(false); // Lock to prevent concurrent processing
  // Precomputed grayscale data for instant slider preview (avoids full re-render per keystroke)
  const precomputedGrayscaleRef = useRef(null);
  // RAF handle + latest pending value for slider throttling (one render per frame max)
  const rafThresholdRef = useRef(null);
  const pendingThresholdValueRef = useRef(null);
  // Direct canvas ref for the review modal preview — avoids toDataURL/PNG encoding overhead
  const previewCanvasRef = useRef(null);

  // Review modal state (for processed logo review)
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [showThresholdSlider, setShowThresholdSlider] = useState(false);
  const [isAcceptingLogo, setIsAcceptingLogo] = useState(false);
  const jerseyDebounceRef = useRef(null);

  // Quality warning state - for low resolution or upscaled images
  const [qualityWarning, setQualityWarning] = useState(null);
  const [qualityAcknowledged, setQualityAcknowledged] = useState(false);

  // Track if uploaded file is SVG (affects quality warnings and processing)
  const [isSourceSVG, setIsSourceSVG] = useState(false);
  
  // Jersey state
  const [jerseyNumber, setJerseyNumber] = useState("");
  const [jerseyText, setJerseyText] = useState("");
  const [jerseyTextPosition, setJerseyTextPosition] = useState("above"); // "above" or "below"
  const [jerseyCurveText, setJerseyCurveText] = useState(false); // Whether to curve the text
  
  // Preset designs state
  const [presetDesigns, setPresetDesigns] = useState([]);
  const [selectedPresetDesign, setSelectedPresetDesign] = useState(null);
  const [presetDesignCategories, setPresetDesignCategories] = useState([]);
  const [selectedPresetCategory, setSelectedPresetCategory] = useState(null);

  // Wholesale offers only the upload-logo add-on; the full flag set is computed
  // so the retail-parity render works unchanged (non-upload flags stay false).
  const customizerDesignFlags = getCustomizerDesignFlags({
    customElements,
    customText,
    jerseyNumber,
    jerseyText,
    selectedPresetDesign,
    processedBlob,
  });
  const customizerCurrency = selectedColor?.variants?.[0]?.currencyCode || "USD";
  // Wholesale: the etch is FREE, so the total is simply the base variant price
  // times the (min-6) quantity — no per-design upcharge. The retail add-on
  // upcharge helper (calculateCustomizerTotal) is intentionally not used here.
  const wholesaleUnitPrice = parseMoneyAmount(selectedColor?.variants?.[0]?.price);
  const customizerTotal = (wholesaleUnitPrice * quantity).toFixed(2);
  const fmtMoney = (value) => formatMoney(parseMoneyAmount(value), customizerCurrency);
  const hasAnyDesign = Object.values(customizerDesignFlags).some(Boolean);

  useEffect(() => {
    console.log("customElements", customElements);
  }, [customElements]);

  // Lazy-load the retail Typekit families so the customizer typography matches
  // the retail site. Injected once; left in place between opens.
  useEffect(() => {
    if (typeof document === "undefined") return;
    const id = "customizer-typekit-css";
    if (document.getElementById(id)) return;
    const link = document.createElement("link");
    link.id = id;
    link.rel = "stylesheet";
    link.href = "https://use.typekit.net/ylf8fio.css";
    document.head.appendChild(link);
  }, []);

  // Smoothly ease a fallback progress value 0→95 while the loading modal is
  // open, so the bar always fills visibly. The real per-step milestones still
  // count via Math.max in displayUploadProgress; this just fills the gaps.
  useEffect(() => {
    if (!showLoadingModal) {
      setSmoothUploadProgress(0);
      return;
    }
    setSmoothUploadProgress(3);
    const id = setInterval(() => {
      setSmoothUploadProgress((p) =>
        p >= 95 ? 95 : Math.min(95, p + Math.max(0.5, (95 - p) * 0.05)),
      );
    }, 90);
    return () => clearInterval(id);
  }, [showLoadingModal]);

  // Detect mobile viewport for bottom-sheet layout
  useEffect(() => {
    const checkMobile = () => setIsMobileLayout(window.innerWidth <= 768);
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  // Reset mobile step when tab changes or drawer closes
  useEffect(() => {
    setMobileStep(1);
  }, [activeTab, isDrawerOpen]);

  // One-shot: auto-open the Step 1 drawer on mobile so first-time customers land
  // in the product picker instead of a bare bottle preview.
  useEffect(() => {
    if (didAutoOpenStep1Ref.current) return;
    if (!isMobileLayout) return;
    didAutoOpenStep1Ref.current = true;
    setActiveTab("product");
    setIsDrawerOpen(true);
    setFocusMode(false);
  }, [isMobileLayout]);

  
  // Track last bronze etch result to avoid excessive logging
  const lastBronzeEtchResultRef = useRef(null);
  
  // Helper function to check if bronze-etch tag exists on selectedColor or selectedProduct
  const isBronzeEtch = () => {
    let result = false;
    let foundLocation = null;
    
    // Check selectedColor tags
    if (selectedColor?.tags && Array.isArray(selectedColor.tags)) {
      const hasBronze = selectedColor.tags.some(tag => tag.includes('bronze-etch'));
      if (hasBronze) {
        result = true;
        foundLocation = 'selectedColor.tags';
      }
    }
    // Check selectedColor.tags if it's a string array
    else if (selectedColor?.tags && typeof selectedColor.tags === 'string') {
      if (selectedColor.tags.includes('bronze-etch')) {
        result = true;
        foundLocation = 'selectedColor.tags (string)';
      }
    }
    // Check selectedProduct tags
    if (!result && selectedProduct?.tags && Array.isArray(selectedProduct.tags)) {
      const hasBronze = selectedProduct.tags.some(tag => tag.includes('bronze-etch'));
      if (hasBronze) {
        result = true;
        foundLocation = 'selectedProduct.tags';
      }
    }
    // Check selectedProduct.tags if it's a string array
    if (!result && selectedProduct?.tags && typeof selectedProduct.tags === 'string') {
      if (selectedProduct.tags.includes('bronze-etch')) {
        result = true;
        foundLocation = 'selectedProduct.tags (string)';
      }
    }
    // Check selectedProduct.product?.tags
    if (!result && selectedProduct?.product?.tags && Array.isArray(selectedProduct.product.tags)) {
      const hasBronze = selectedProduct.product.tags.some(tag => tag.includes('bronze-etch'));
      if (hasBronze) {
        result = true;
        foundLocation = 'selectedProduct.product.tags';
      }
    }
    
    // Only log when result changes to avoid console flooding
    if (lastBronzeEtchResultRef.current !== result) {
      if (result) {
        console.log('🔍 [Bronze Etch] Found bronze-etch tag on', foundLocation);
      } else {
        console.log('🔍 [Bronze Etch] No bronze-etch tag found. Using silver.');
      }
      lastBronzeEtchResultRef.current = result;
    }
    
    return result;
  };
  
  // Color patterns state
  const [colorPatterns, setColorPatterns] = useState({});

  // Function to get color pattern for a product
  const getColorPattern = (product) => {
    if (!product || !colorPatterns) return null;
    
    // Extract color name from product title (normalize to lowercase)
    const productTitle = product.title?.toLowerCase() || '';
    
    // Sort patterns by label length (longest first) to prioritize more specific matches
    const sortedPatterns = Object.entries(colorPatterns)
      .filter(([handle, pattern]) => pattern.label)
      .sort(([, a], [, b]) => b.label.length - a.label.length);
    
    // Look for matching color pattern using the label field for exact matching
    for (const [handle, pattern] of sortedPatterns) {
      const patternLabel = pattern.label.toLowerCase();
      
      // Check if the product title contains the exact label
      if (productTitle.includes(patternLabel)) {
        return pattern;
      }
    }
    
    return null;
  };

  // Product-type / color selection handlers used by the retail-parity render
  // (mobile Step-1 drawer + product/color pickers).
  const handleSelectProductType = (collection) => {
    if (!collection || collection.products.length === 0) return;
    const firstProduct = collection.products[0];
    const newProduct = {
      ...firstProduct,
      collectionHandle: collection.handle,
      allProducts: collection.products,
      productType: collection.title,
    };
    setSelectedProduct(newProduct);
    setSelectedColor(pickBlackOrFirst(collection.products));
    if (onProductChange) onProductChange(collection.handle);
  };

  const handleSelectColor = (product) => {
    setSelectedColor(product);
  };

  // Fonts available
  const fonts = [
    // { name: "Arial", value: "Arial" },
    { name: "Times New Roman", value: "Times New Roman" },
    // { name: "Verdana", value: "Verdana" },
    // { name: "Impact", value: "Impact" },
    // { name: "Cursive", value: "Snell Roundhand" },
    // { name: "Lemon Jelly", value: "lemon jelly personal use" },
    // { name: "Comic Sans", value: "Comic Sans MS" },
    // { name: "Lovely Coffee", value: "lovely coffee" },
    { name: "Freshman", value: "Freshman" },
    { name: "Montserrat", value: "Montserrat" },
    // { name: "Litoland Regular", value: "Litoland_Regular" },
    // { name: "Steelfish Bd", value: "Steelfish_Bd" },
    // { name: "Magnolia Script", value: "Magnolia_Script" },
    // Adobe Typekit fonts
    { name: "Soap", value: "soap" },
    // { name: "Fisterra Fora", value: "fisterra-fora" },
    { name: "Fisterra", value: "fisterra-morte" },
    // { name: "Sloop Script One", value: "sloop-script-one" },
    // { name: "Sloop Script Pro", value: "sloop-script-pro" },
    { name: "Sloop Script", value: "sloop-script-two" },
    // { name: "Sloop Script Three", value: "sloop-script-three" },
  ];

  // Prevent navigation/refresh during moderation
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (isModerating) {
        e.preventDefault();
        e.returnValue = 'Image moderation is in progress. Are you sure you want to leave?';
        return e.returnValue;
      }
    };

    const handlePopState = (e) => {
      if (isModerating) {
        e.preventDefault();
        window.history.pushState(null, '', window.location.href);
        alert('Please wait for image moderation to complete before navigating away.');
      }
    };

    if (isModerating) {
      window.history.pushState(null, '', window.location.href);
      window.addEventListener('beforeunload', handleBeforeUnload);
      window.addEventListener('popstate', handlePopState);
    }

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('popstate', handlePopState);
    };
  }, [isModerating]);

  // Lock body scroll when drawer or review modal is open
  useEffect(() => {
    const shouldLock = isDrawerOpen || showReviewModal;
    
    if (shouldLock) {
      // Add class to lock body scroll
      document.documentElement.classList.add('customizer-scroll-lock');
      document.body.classList.add('customizer-scroll-lock');
    } else {
      // Remove all scroll lock classes
      document.documentElement.classList.remove('customizer-scroll-lock');
      document.body.classList.remove('customizer-scroll-lock');
      document.documentElement.classList.remove('ps-scroll-lock');
      document.body.classList.remove('ps-scroll-lock');
    }
    
    // Cleanup on unmount
    return () => {
      document.documentElement.classList.remove('customizer-scroll-lock');
      document.body.classList.remove('customizer-scroll-lock');
      document.documentElement.classList.remove('ps-scroll-lock');
      document.body.classList.remove('ps-scroll-lock');
    };
  }, [isDrawerOpen, showReviewModal]);

  // When the review modal opens (or processedLogoUrl updates), draw the initial
  // preview into the canvas so it shows the image before the user touches the
  // slider. Uses precomputed grayscale when ready (instant), else decodes the
  // processed URL as a fallback.
  useEffect(() => {
    if (!showReviewModal || !previewCanvasRef.current) return;
    if (precomputedGrayscaleRef.current) {
      renderThresholdFast(thresholdValue);
    } else if (processedLogoUrl) {
      const img = new Image();
      img.onload = () => {
        const c = previewCanvasRef.current;
        if (!c) return;
        c.width = img.naturalWidth || 1200;
        c.height = img.naturalHeight || 1200;
        c.getContext("2d").drawImage(img, 0, 0);
      };
      img.src = processedLogoUrl;
    }
  }, [showReviewModal, processedLogoUrl]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch products on component mount
  useEffect(() => {
    const initializeData = async () => {
      const patterns = await fetchColorPatterns();
      setColorPatterns(patterns);
      fetchProducts();
      await fetchPresetDesigns();
    };
    
    initializeData();
  }, []);

  // Parse URL query parameter to auto-select product type
  useEffect(() => {
    // Only run if collections are loaded and no product is selected yet
    if (collections.length === 0 || selectedProduct) return;

    // Parse URL query parameters — fall back to traveler-40oz (retail default),
    // then the first available collection, so the customizer opens on a product
    // instead of an empty picker.
    const urlParams = new URLSearchParams(window.location.search);
    const requested = urlParams.get('default') || 'traveler-40oz';

    const matchingCollection =
      collections.find((collection) => collection.handle === requested) ||
      collections.find((collection) => collection.products.length > 0);

    if (matchingCollection && matchingCollection.products.length > 0) {
      const newProduct = {
        ...matchingCollection.products[0],
        collectionHandle: matchingCollection.handle,
        allProducts: matchingCollection.products,
      };

      setSelectedProduct(newProduct);

      // Prefer a black colorway, else first available (matches retail).
      setSelectedColor(pickBlackOrFirst(newProduct.allProducts));

      console.log(`✅ Auto-selected product type: ${matchingCollection.handle}`);
    } else {
      console.warn(`⚠️ No collection available to auto-select`);
    }
  }, [collections, selectedProduct]);

  // Update product image when product/color changes
  useEffect(() => {
    if (selectedColor) {
      // Find the customizer image for the selected product
      const customizerImage = selectedColor.images.find(img => 
        img.alt && img.alt.toLowerCase().includes('customizer')
      );
      
      if (customizerImage?.src) {
        setProductImageUrl(customizerImage.src);
      } else {
        // Priority 2: back/etch-side image (WPI-4 or _4 suffix in filename) —
        // the standard HydroJug etch-side view used in the customizer. Replaces
        // the old hard-coded images[3] magic index.
        const etchSideImage = selectedColor.images.find((img) => {
          if (!img.src) return false;
          const filename = img.src.split("/").pop()?.split("?")[0]?.toLowerCase() || "";
          return filename.includes("wpi-4") || filename.match(/_4[._]/);
        });

        if (etchSideImage?.src) {
          setProductImageUrl(etchSideImage.src);
        } else if (selectedColor.images[0]?.src) {
          // Priority 3: first image as final fallback
          setProductImageUrl(selectedColor.images[0].src);
        }
      }
    }
  }, [selectedColor]);

  // Track last processed color to prevent infinite loops
  const lastProcessedColorRef = useRef(null);
  
  // Reset ref when customElements becomes empty
  useEffect(() => {
    if (customElements.length === 0) {
      lastProcessedColorRef.current = null;
      processedLogoCanvasRef.current = null;
    }
  }, [customElements.length]);
  
  // Fast update metallic effect for uploaded logos when color/product changes
  useEffect(() => {
    if (processedLogoCanvasRef.current && processedBlob && originalLogoUrl && customElements.length > 0) {
      const element = customElements[0];
      // Only update if it's an uploaded logo (not preset, not text, not jersey)
      if (element.type === 'image' && !element.isPresetDesign && !element.text && !(jerseyNumber || jerseyText)) {
        console.log('⚡ Fast updating logo metallic effect for color/product change...');
        try {
          const updatedCanvas = updateMetallicEffect(processedLogoCanvasRef.current);
          if (updatedCanvas) {
            // Convert to SVG for display
            const svgString = canvasToSVG(updatedCanvas, updatedCanvas.width, updatedCanvas.height);
            const svgBlob = new Blob([svgString], { type: 'image/svg+xml' });
            const displayUrl = URL.createObjectURL(svgBlob);
            
            // Update the processed logo URL
            if (processedLogoUrl) {
              URL.revokeObjectURL(processedLogoUrl);
            }
            setProcessedLogoUrl(displayUrl);
            
            // Update the custom element with new URL
            setCustomElements(prev => 
              prev.map(el => ({
                ...el,
                imageUrl: displayUrl
              }))
            );
          }
        } catch (error) {
          console.error('Error fast updating logo metallic effect:', error);
        }
      }
    }
  }, [selectedColor?.id, selectedColor?.title, selectedProduct?.id, selectedProduct?.handle]);
  
  // Regenerate custom elements when color changes to update etch color
  useEffect(() => {
    const regenerateDesignsForColor = async () => {
      if (!selectedColor || customElements.length === 0) {
        lastProcessedColorRef.current = null;
        return;
      }
      
      // Prevent infinite loop: only regenerate if color actually changed
      const currentColorId = selectedColor.id || selectedColor.title;
      if (lastProcessedColorRef.current === currentColorId) {
        console.log('⏭️ Skipping regeneration - color already processed:', currentColorId);
        return;
      }
      
      // Skip fast update for uploaded logos - handled by separate useEffect
      if (processedLogoCanvasRef.current && processedBlob && originalLogoUrl) {
        const element = customElements[0];
        if (element.type === 'image' && !element.isPresetDesign && !element.text && !(jerseyNumber || jerseyText)) {
          console.log('⏭️ Skipping slow regeneration - using fast update for uploaded logo');
          return;
        }
      }
      
      console.log('🔄 Starting regeneration for color:', currentColorId);
      lastProcessedColorRef.current = currentColorId;
      
      const element = customElements[0];
      
      // Only regenerate if it's a preset design or text element
      if (element.isPresetDesign && selectedPresetDesign) {
        console.log('🔄 Fast updating preset design for new color...');
        try {
          // Use fast update if base canvas is cached
          if (presetDesignCanvasRef.current) {
            // Use fast mode for UI updates (lower resolution, faster processing)
            const updatedCanvas = updateMetallicEffect(presetDesignCanvasRef.current, { fastMode: true });
            if (updatedCanvas) {
              // Use data URL directly - much faster than blob
              const metallicUrl = updatedCanvas.toDataURL('image/png');
              setCustomElements(prev => 
                prev.map(el => ({
                  ...el,
                  imageUrl: metallicUrl
                }))
              );
            }
          } else {
            // Fallback to full reprocess if base canvas not available
            const originalImageUrl = selectedPresetDesign.image?.src || selectedPresetDesign.image?.url;
            if (originalImageUrl) {
              const metallicImageUrl = await processPresetDesignWithMetallic(originalImageUrl, { storeBaseCanvas: true });
              
              setCustomElements(prev => 
                prev.map(el => ({
                  ...el,
                  imageUrl: metallicImageUrl
                }))
              );
            }
          }
        } catch (error) {
          console.error('Error regenerating design for color:', error);
        }
      } else if (element.type === 'image' && customText) {
        // Regenerate metallic text for the new color
        console.log('🔄 Regenerating text for new color...');
        const canvasWidth = window.innerWidth <= 768 ? 400 : 600;
        const canvasHeight = window.innerWidth <= 768 ? 400 : 600;
        
        try {
          // Use the existing createMetallicText function
          await new Promise((resolve) => {
            createMetallicText(element.text || customText, element.fontFamily || selectedFont, canvasWidth, canvasHeight);
            setTimeout(resolve, 100); // Give it time to process
          });
        } catch (error) {
          console.error('Error regenerating text for color:', error);
        }
      } else if (element.type === 'image' && (jerseyNumber || jerseyText)) {
        // Regenerate jersey for the new color
        console.log('🔄 Regenerating jersey for new color...');
        try {
          const canvasWidth = window.innerWidth <= 768 ? 400 : 600;
          const canvasHeight = window.innerWidth <= 768 ? 400 : 600;
          await createJerseyDesign(jerseyNumber, jerseyText, jerseyTextPosition, canvasWidth, canvasHeight);
        } catch (error) {
          console.error('Error regenerating jersey for color:', error);
        }
      }
      // Note: Uploaded logos are now handled by the fast update useEffect above
    };
    
    regenerateDesignsForColor();
  }, [selectedColor?.id, selectedColor?.title]); // Only depend on color identity, not the whole object

  // Update custom elements positioning when product type changes
  useEffect(() => {
    if (customElements.length > 0 && selectedProduct) {
      const productType = selectedProduct.collectionHandle || 'traveler-40oz';
      
      setCustomElements(prevElements => 
        prevElements.map(element => {
          if (element.type === 'image') {
            // Determine element type based on how it was created
            const elementType = element.isPresetDesign ? 'design' : 'text';
            const textLength = element.textLength || 0;
            const styles = getCustomElementStyles(productType, elementType, textLength);
            
            return {
              ...element,
              width: styles.width,
              height: styles.height,
              top: styles.top,
              left: styles.left,
              right: styles.right,
              margin: styles.margin
            };
          }
          return element;
        })
      );
    }
  }, [selectedProduct]);

  const fetchColorPatterns = async () => {
    try {
      const query = `
        query getColorPatterns {
          metaobjects(type: "shopify--color-pattern", first: 200) {
            nodes {
              handle
              fields {
                value
                key
                reference {
                  ... on MediaImage {
                    image {
                      altText
                      url
                    }
                  }
                }
              }
            }
          }
        }
      `;

      const response = await fetch('/api/2024-01/graphql.json', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': '76f729d661f807e63d48527685061915'
        },
        body: JSON.stringify({ query })
      });

      const result = await response.json();
      if (result.errors) {
        throw new Error('GraphQL Error: ' + JSON.stringify(result.errors));
      }

      
      // Process metaobjects into a lookup map
      const colorPatterns = {};
      result.data.metaobjects.nodes.forEach(node => {
        const pattern = { handle: node.handle };
        node.fields.forEach(field => {
          if (field.key === 'image' && field.reference?.image) {
            // Handle MediaImage reference
            pattern[field.key] = field.reference.image.url;
            pattern[`${field.key}_alt`] = field.reference.image.altText;
          } else {
            // Handle regular value fields (like color hex codes)
            pattern[field.key] = field.value;
          }
        });
        colorPatterns[node.handle] = pattern;
      });

      return colorPatterns;
    } catch (error) {
      console.error('Error fetching color patterns:', error);
      return {};
    }
  };

  const fetchProducts = async () => {
    console.log('🔄 Starting to fetch products from collections...');
    setLoading(true);
    const collectionHandles = [
      "traveler-40oz", "traveler-32oz", "traveler-20oz", "coffee-travelers",
      "sport-40oz", "sport-32oz", "sport-20oz", "sport-14oz",
      "can-cooler-16oz", "everyday-tumbler-14oz", "everyday-tumbler-20oz",
      "stainless-64oz", "can-coolers-slim", "can-coolers-standard",
      "insulated-shaker",
      "ease2o-40oz", "ease2o-32oz", "ease2o-24oz", "ease2o-14oz",
    ];

    try {
      for (let handle of collectionHandles) {
          console.log(`📦 Fetching collection: ${handle}`);
          const query = `
            query getCollection($handle: String!) {
              collectionByHandle(handle: $handle) {
                id
                handle
                title
                products(first: 150) {
                  nodes {
                    id
                    title
                    handle
                    onlineStoreUrl
                    totalInventory
                  productType
                    tags
                    images(first: 10) {
                      nodes {
                        url
                        altText
                      }
                    }
                    variants(first: 10) {
                      nodes {
                        id
                        title
                        price {
                          amount
                          currencyCode
                        }
                        availableForSale
                        image {
                          url
                          altText
                        }
                      }
                    }
                  }
                }
              }
            }
          `;

          const response = await fetch('/api/2024-01/graphql.json', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'X-Shopify-Storefront-Access-Token': '76f729d661f807e63d48527685061915'
            },
            body: JSON.stringify({
              query,
              variables: { handle }
            })
          });

          if (!response.ok) {
            console.error(`❌ GraphQL request failed for ${handle}:`, response.status, response.statusText);
            const errorText = await response.text();
            console.error('Error response:', errorText);
            continue; // Skip this collection and continue with others
          }

          const result = await response.json();
          
          if (result.errors) {
            console.error(`❌ GraphQL errors for ${handle}:`, result.errors);
            continue; // Skip this collection and continue with others
          }
          
          if (!result.data?.collectionByHandle) {
            console.warn(`⚠️ Collection not found: ${handle}`);
            continue; // Skip this collection
          }

          const collection = {
            handle: result.data.collectionByHandle.handle,
            title: result.data.collectionByHandle.title,
            products: result.data.collectionByHandle.products.nodes
              // Keep the in-stock + storefront-visible requirement, and also
              // exclude any product tagged "no-etch" (not customizable).
              .filter(p => p.totalInventory > 0 && p.onlineStoreUrl && !(p.tags || []).includes('no-etch'))
              .map(product => ({
                id: product.id,
                title: product.title,
                handle: product.handle,
                images: product.images.nodes.map(img => ({ src: img.url, alt: img.altText })),
              productType: product.productType,
                tags: product.tags,
                variants: product.variants.nodes
                  .filter(variant => variant.availableForSale)
                  .map(variant => ({
                    id: variant.id.replace(/^gid:\/\/shopify\/ProductVariant\//, ""),
                    title: variant.title,
                    price: variant.price.amount,
                    currencyCode: variant.price.currencyCode,
                    available: variant.availableForSale,
                    featured_image: variant.image ? { src: variant.image.url } : null
                  }))
              }))
              .filter(p => p.variants.length > 0)
          };

          console.log(`✅ Successfully fetched ${collection.products.length} products from ${handle}`);
          setCollections(prev => [...prev, collection]);
      }
      
      console.log('✅ Finished fetching all collections');
    } catch (err) {
      console.error("❌ Product fetch error:", err);
    }
    setLoading(false);
  };

    // Fetch preset designs from metaobject
  const fetchPresetDesigns = async () => {
    try {
      const query = `
        query getEtchesMetaobject {
          metaobjects(type: "etches", first: 1) {
            nodes {
              handle
              fields {
                key
                value
                references(first: 50) {
                  nodes {
                    ... on MediaImage {
                      image {
                        altText
                        url
                      }
                    }
                    ... on Metaobject {
                      __typename
                      handle
                      fields {
                        key
                        value
                        reference {
                          ... on MediaImage {
                            image {
                              altText
                              url
                            }
                          }
                        }
                      }
                    }
                  }
                }
                reference {
                  ... on MediaImage {
                    image {
                      altText
                      url
                    }
                  }
                  ... on Metaobject {
                    __typename
                    handle
                    fields {
                      key
                      value
                      reference {
                        ... on MediaImage {
                          image {
                            altText
                            url
                          }
                        }
                      }
                    }
                  }
                }
              }
            }
          }
        }
      `;

      const response = await fetch('/api/2024-01/graphql.json', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': '76f729d661f807e63d48527685061915'
        },
        body: JSON.stringify({ query })
      });

      const result = await response.json();
      
      if (result.errors) {
        console.error('❌ [Preset Designs] GraphQL errors:', result.errors);
        throw new Error('GraphQL Error: ' + JSON.stringify(result.errors));
      }

      if (!result.data) {
        console.error('❌ [Preset Designs] No data in response');
        return;
      }

      if (!result.data.metaobjects) {
        console.error('❌ [Preset Designs] No metaobjects in data');
        return;
      }


      // Process metaobject fields into categories
      const categories = {};
      
      if (result.data.metaobjects.nodes.length > 0) {
        const etchesMetaobject = result.data.metaobjects.nodes[0];
        
        etchesMetaobject.fields.forEach((field, fieldIndex) => {
          // Each field represents a category of designs
          const categoryName = field.key;
          
          if (!categories[categoryName]) {
            categories[categoryName] = [];
          }
          
          // Handle multiple references (lists of metaobjects or images)
          if (field.references && field.references.nodes) {
            field.references.nodes.forEach((ref, index) => {
              
              let imageUrl = null;
              let altText = null;
              let etchName = null;
              
              // Handle Metaobject references (nested structure)
              // Check for handle and fields properties (__typename may not be available)
              if ((ref.__typename === 'Metaobject' || (ref.handle && ref.fields)) && ref.fields) {
                ref.fields.forEach(metaField => {
                  const fieldKey = metaField.key.toLowerCase();
                  
                  // Extract Etch Name
                  if (fieldKey === 'etch_name' || fieldKey === 'etch name') {
                    etchName = metaField.value;
                  }
                  
                  // Extract Etch Image (MediaImage reference)
                  if ((fieldKey === 'etch_image' || fieldKey === 'etch image') && metaField.reference) {
                    if (metaField.reference.image) {
                      imageUrl = metaField.reference.image.url;
                      altText = metaField.reference.image.altText;
                    }
                  }
                });
                
                // Use etch name as title, fallback to altText
                const title = etchName || altText || `${categoryName} Design ${index + 1}`;
                
                if (imageUrl) {
                  const design = {
                    id: `${categoryName}-${index}`,
                    title: title,
                    handle: categoryName.toLowerCase().replace(/\s+/g, '-'),
                    image: { src: imageUrl, alt: altText || etchName },
                    price: '6.00',
                    variantId: null,
                    productId: null
                  };
                  categories[categoryName].push(design);
                }
              }
              // Handle direct MediaImage references (fallback for old structure)
              else if (ref.image) {
                imageUrl = ref.image.url;
                altText = ref.image.altText;
                
                const design = {
                  id: `${categoryName}-${index}`,
                  title: altText || `${categoryName} Design ${index + 1}`,
                  handle: categoryName.toLowerCase().replace(/\s+/g, '-'),
                  image: { src: imageUrl, alt: altText },
                  price: '6.00',
                  variantId: null,
                  productId: null
                };
                categories[categoryName].push(design);
              }
            });
          }
          // Handle single reference (fallback)
          else if (field.reference) {
            
            let imageUrl = null;
            let altText = null;
            let etchName = null;
            
            // Handle Metaobject reference (nested structure)
            // Check for handle and fields properties (__typename may not be available)
            if ((field.reference.__typename === 'Metaobject' || (field.reference.handle && field.reference.fields)) && field.reference.fields) {
              field.reference.fields.forEach(metaField => {
                const fieldKey = metaField.key.toLowerCase();
                
                // Extract Etch Name
                if (fieldKey === 'etch_name' || fieldKey === 'etch name') {
                  etchName = metaField.value;
                }
                
                // Extract Etch Image (MediaImage reference)
                if ((fieldKey === 'etch_image' || fieldKey === 'etch image') && metaField.reference) {
                  if (metaField.reference.image) {
                    imageUrl = metaField.reference.image.url;
                    altText = metaField.reference.image.altText;
                  }
                }
              });
              
              // Use etch name as title, fallback to altText
              const title = etchName || altText || `${categoryName} Design 1`;
              
              if (imageUrl) {
                const design = {
                  id: `${categoryName}-0`,
                  title: title,
                  handle: categoryName.toLowerCase().replace(/\s+/g, '-'),
                  image: { src: imageUrl, alt: altText || etchName },
                  price: '6.00',
                  variantId: null,
                  productId: null
                };
                categories[categoryName].push(design);
              }
            }
            // Handle direct MediaImage reference (fallback for old structure)
            else if (field.reference.image) {
              imageUrl = field.reference.image.url;
              altText = field.reference.image.altText;
              
              const design = {
                id: `${categoryName}-0`,
                title: altText || `${categoryName} Design 1`,
                handle: categoryName.toLowerCase().replace(/\s+/g, '-'),
                image: { src: imageUrl, alt: altText },
                price: '6.00',
                variantId: null,
                productId: null
              };
              categories[categoryName].push(design);
            } 
          }
        });
      } else {
        console.warn('⚠️ [Preset Designs] No metaobjects found');
      }
      
      
      // Convert categories object to array format
      const categoriesArray = Object.keys(categories).map(categoryName => ({
        name: categoryName,
        designs: categories[categoryName]
      }));
      
      setPresetDesignCategories(categoriesArray);
      setPresetDesigns(categoriesArray);
    } catch (error) {
      console.error('❌ [Preset Designs] Error fetching preset designs from metaobject:', error);
      console.error('❌ [Preset Designs] Error stack:', error.stack);
    }
  };

  // Logo processing function - local preview only, no Cloudinary upload
  const processLogoForEtching = async (file) => {
    if (!file) return;

    // Clear any cached image before processing new one
    originalLogoImageRef.current = null;
    originalLogoDimensionsRef.current = null;
    displayDimensionsRef.current = null;
    processedLogoCanvasRef.current = null;
    precomputedGrayscaleRef.current = null;

    // Detect if file is SVG - affects quality warnings and processing
    const isSVG =
      file.type === "image/svg+xml" || file.name.toLowerCase().endsWith(".svg");
    setIsSourceSVG(isSVG);
    console.log(`📁 File type: ${file.type}, isSVG: ${isSVG}`);

    setLogoProcessingState('processing');
    setProcessingProgress(0);

    let localUrl;

    // SVGs need preprocessing to handle CSS variables and ensure they render properly
    if (isSVG) {
      try {
        const svgText = await file.text();

        // Preprocess SVG to fix common rendering issues:
        // 1. Add explicit width/height from viewBox (CRITICAL for canvas rendering)
        // 2. Replace CSS variables with their fallback values
        // 3. Replace currentColor with black
        // 4. Add explicit fill if missing
        let processedSvg = svgText;

        // CRITICAL: Add explicit width/height if missing
        const hasWidth = /\swidth\s*=/.test(processedSvg);
        const hasHeight = /\sheight\s*=/.test(processedSvg);

        if (!hasWidth || !hasHeight) {
          const viewBoxMatch = processedSvg.match(/viewBox\s*=\s*["']([^"']+)["']/);
          if (viewBoxMatch) {
            const viewBoxParts = viewBoxMatch[1].split(/[\s,]+/);
            if (viewBoxParts.length >= 4) {
              const vbWidth = parseFloat(viewBoxParts[2]);
              const vbHeight = parseFloat(viewBoxParts[3]);

              if (vbWidth > 0 && vbHeight > 0) {
                // Scale up to a reasonable size for canvas rendering (at least 800px on longest side)
                const maxDim = Math.max(vbWidth, vbHeight);
                const svgScale = maxDim < 800 ? 800 / maxDim : 1;
                const width = Math.round(vbWidth * svgScale);
                const height = Math.round(vbHeight * svgScale);

                console.log(`📐 Adding dimensions from viewBox: ${vbWidth}x${vbHeight} → ${width}x${height}`);
                processedSvg = processedSvg.replace(/<svg/, `<svg width="${width}" height="${height}"`);
              }
            }
          } else {
            console.log("📐 No viewBox found, adding default 800x800 dimensions");
            processedSvg = processedSvg.replace(/<svg/, '<svg width="800" height="800"');
          }
        }

        // Replace var(--name, fallback) with just the fallback value
        processedSvg = processedSvg.replace(/var\(--[^,]+,\s*([^)]+)\)/g, "$1");

        // Replace currentColor with black (common in SVGs)
        processedSvg = processedSvg.replace(/currentColor/gi, "#000000");

        // If SVG has no fill specified on paths, add fill="black"
        if (processedSvg.includes("<path") && !processedSvg.includes("fill=")) {
          processedSvg = processedSvg.replace(/<svg([^>]*)>/, '<svg$1 fill="#000000">');
        }

        // Only remove <style> blocks that contain CSS variables (var())
        // Keep style blocks that define normal CSS classes (like .st1{fill:#FFFFFF})
        processedSvg = processedSvg.replace(
          /<style[^>]*>([\s\S]*?)<\/style>/gi,
          (match, styleContent) => {
            if (styleContent.includes("var(")) {
              console.log("🗑️ Removing style block with CSS variables");
              return "";
            }
            return match;
          },
        );

        const processedSvgBlob = new Blob([processedSvg], { type: "image/svg+xml" });
        localUrl = URL.createObjectURL(processedSvgBlob);
        console.log("✅ SVG preprocessed successfully");
      } catch (svgError) {
        console.error("⚠️ SVG preprocessing failed, using original:", svgError);
        localUrl = URL.createObjectURL(file);
      }
    } else {
      localUrl = URL.createObjectURL(file);
    }

    setOriginalLogoUrl(localUrl);

    // Clear quality warning for new image (will be re-evaluated during processing)
    setQualityWarning(null);
    setQualityAcknowledged(false);

    try {
      setProcessingProgress(40);
      // Only do local processing for UI preview - no Cloudinary upload yet
      // Use preview: true for consistent sizing with slider/invert updates
      // Pass isSVG flag for appropriate handling
      await processImageLocally(localUrl, {
        thresholdValue,
        invert: invertEtch,
        keepDrawerOpen: true,
        preview: true,
        isSVG,
      });
      setProcessingProgress(100);
      return;
    } catch (error) {
      console.error("Local etch processing error:", error);
      setLogoProcessingState('error');
      setProcessingProgress(0);
    }
  };

  // Add processed logo to canvas
  const addProcessedLogoToCanvas = (logoUrl, { keepDrawerOpen = false, preserveTransform = false } = {}) => {
    
    // Clear all other style states when adding logo
    setSelectedPresetDesign(null);
    // Clear text states
    setCustomText('');
    // Clear jersey states
    setJerseyNumber('');
    setJerseyText('');
    setJerseyTextPosition('below');
    
    const img = new Image();
    img.onload = () => {

      // Get dynamic styles based on selected product
      const productType = selectedProduct?.collectionHandle || 'traveler-40oz';
      const baseStyles = getCustomElementStyles(productType, 'design');

      // Calculate image aspect ratio for rotation-aware sizing
      // For SVG blob URLs, use processedSize if available as fallback
      let imageWidth = img.naturalWidth;
      let imageHeight = img.naturalHeight;

      if (
        (!imageWidth || !imageHeight || imageWidth === 0 || imageHeight === 0) &&
        processedSize &&
        processedSize.width > 0 &&
        processedSize.height > 0
      ) {
        imageWidth = processedSize.width;
        imageHeight = processedSize.height;
      }

      const imageAspectRatio = imageWidth / imageHeight;

      // If we already have an element and want to preserve its transform, reuse it
      const existing = customElements && customElements.length > 0 ? customElements[0] : null;
      const currentRotation = existing?.rotation || 0;

      // Use base etching area dimensions as the original dimensions
      // These will be swapped when rotated 90°/270°
      const originalWidth = baseStyles.width;
      const originalHeight = baseStyles.height;

      const isRotated90or270 = currentRotation === 90 || currentRotation === 270;
      const displayWidth = isRotated90or270 ? originalHeight : originalWidth;
      const displayHeight = isRotated90or270 ? originalWidth : originalHeight;

      if (preserveTransform && existing && existing.type === 'image') {
        const newElement = {
          ...existing,
          designType: 'custom-upload',
          isUploadedLogo: true,
          imageUrl: logoUrl,
          imageAspectRatio: imageAspectRatio,
          baseStyles: baseStyles,
          width: displayWidth,
          height: displayHeight,
          originalWidth: originalWidth,
          originalHeight: originalHeight,
          productType: productType,
        };
        setCustomElements([newElement]);
      } else {
        // Use dynamic styles for positioning and sizing
        const newElement = {
          type: 'image',
          designType: 'custom-upload',
          isUploadedLogo: true,
          imageUrl: logoUrl,
          width: displayWidth,
          height: displayHeight,
          top: baseStyles.top,
          left: baseStyles.left,
          right: baseStyles.right,
          margin: baseStyles.margin,
          rotation: currentRotation,
          isPresetDesign: false,
          textLength: 0,
          imageAspectRatio: imageAspectRatio,
          baseStyles: baseStyles,
          originalWidth: originalWidth,
          originalHeight: originalHeight,
          productType: productType,
        };

        setCustomElements([newElement]);
      }

      if (!keepDrawerOpen) {
        setIsDrawerOpen(false);
        setFocusMode(false);
      }
    };

    img.onerror = (error) => {
      console.error("Error loading processed image:", error);
    };

    img.src = logoUrl;
  };



  // Threshold is now fixed for optimal results

  // Function to render image at higher resolution for smoother edges
  const renderHighRes = (img, targetWidth, targetHeight, threshold, invert = false, rotation = 0, highResScale = 4) => {
    // Use configurable resolution scale (default 4x for final, 1x for preview)
    const scale = highResScale;
    
    // Calculate rotated dimensions if needed
    let canvasWidth = targetWidth;
    let canvasHeight = targetHeight;
    
    if (rotation !== 0) {
      // Calculate the bounding box for the rotated image
      const radians = (rotation * Math.PI) / 180;
      const cos = Math.abs(Math.cos(radians));
      const sin = Math.abs(Math.sin(radians));
      
      canvasWidth = Math.ceil(targetWidth * cos + targetHeight * sin);
      canvasHeight = Math.ceil(targetWidth * sin + targetHeight * cos);
    }
    
    const highResWidth = canvasWidth * scale;
    const highResHeight = canvasHeight * scale;
    const imgHighResWidth = targetWidth * scale;
    const imgHighResHeight = targetHeight * scale;
    
    // Create high-resolution canvas
    const highResCanvas = document.createElement('canvas');
    const highResCtx = highResCanvas.getContext('2d');
    highResCanvas.width = highResWidth;
    highResCanvas.height = highResHeight;
    
    // Enable high-quality rendering
    highResCtx.imageSmoothingEnabled = true;
    highResCtx.imageSmoothingQuality = 'high';
    
    // Apply rotation if specified
    if (rotation !== 0) {
      // Move to center of canvas, rotate, then draw image centered
      highResCtx.translate(highResWidth / 2, highResHeight / 2);
      highResCtx.rotate((rotation * Math.PI) / 180);
      highResCtx.drawImage(img, -imgHighResWidth / 2, -imgHighResHeight / 2, imgHighResWidth, imgHighResHeight);
      // Reset transform for image processing
      highResCtx.setTransform(1, 0, 0, 1, 0, 0);
    } else {
      // Draw image at high resolution without rotation
      highResCtx.drawImage(img, 0, 0, highResWidth, highResHeight);
    }
    
    // Process at high resolution
    const imageData = highResCtx.getImageData(0, 0, highResWidth, highResHeight);
    const data = imageData.data;
    
    // ============================================================
    // SIMPLE GRAYSCALE CONVERSION FOR UI PREVIEW
    // ============================================================
    // The backend applies complex preprocessing (median, normalize, sharpen)
    // that's hard to match exactly. Instead of trying to replicate it,
    // we use simple grayscale for the UI preview.
    // 
    // The threshold slider lets users adjust until the preview looks right,
    // and that same threshold is sent to the backend which applies its own
    // preprocessing. The key insight is that the USER adjusts the slider
    // until the UI looks correct - we just need to ensure the backend
    // produces the same visual result for that threshold value.
    //
    // To achieve this, we REMOVE preprocessing from the backend threshold
    // and apply it BEFORE the threshold step, OR we match preprocessing here.
    // For now, we use simple grayscale which is more predictable.
    // ============================================================
    
    // Convert to grayscale using Rec. 709 luminosity formula (same as Sharp/libvips)
    // This ensures the threshold produces the same result as the backend
    const grayscaleData = new Uint8ClampedArray(highResWidth * highResHeight);
    for (let i = 0, j = 0; i < data.length; i += 4, j++) {
      const r = data[i];
      const g = data[i + 1];
      const b = data[i + 2];
      const a = data[i + 3];
      
      if (a > 0) {
        // Rec. 709 luminosity formula (same as Sharp's grayscale)
        grayscaleData[j] = Math.round(0.2126 * r + 0.7152 * g + 0.0722 * b);
      } else {
        // Transparent becomes white (background)
        grayscaleData[j] = 255;
      }
    }
    
    // Use grayscale directly for thresholding (no additional preprocessing)
    // This ensures the UI preview matches what the user expects from the slider
    const sharpened = grayscaleData;
        
    // First pass: detect background color by sampling corners and edges
    const samplePoints = [];
    const sampleSize = Math.min(highResWidth, highResHeight) * 0.1; // 10% of smaller dimension
    
    // Sample corners
    for (let x = 0; x < sampleSize; x += 2) {
      for (let y = 0; y < sampleSize; y += 2) {
        samplePoints.push({ x, y });
        samplePoints.push({ x: highResWidth - 1 - x, y });
        samplePoints.push({ x, y: highResHeight - 1 - y });
        samplePoints.push({ x: highResWidth - 1 - x, y: highResHeight - 1 - y });
      }
    }
    
    // Calculate average background grayscale from preprocessed data
    // IMPORTANT: For SVGs with transparent backgrounds, we need to count
    // transparent pixels as white (255) for background detection
    let bgGray = 0, bgCount = 0;
    let transparentCornerPixels = 0;
    samplePoints.forEach(point => {
      const idx = point.y * highResWidth + point.x;
      const rgbaIdx = (point.y * highResWidth + point.x) * 4;
      const a = data[rgbaIdx + 3];

      // Count ALL corner pixels for background detection
      if (a > 0) {
        // Opaque pixel - use its grayscale value
        bgGray += sharpened[idx];
      } else {
        // Transparent pixel - treat as white background
        bgGray += 255;
        transparentCornerPixels++;
      }
      bgCount++;
    });

    const avgBgGray = bgCount > 0 ? bgGray / bgCount : 255;
    const bgThreshold = 20; // How close to background grayscale to consider as background

    // If most corner pixels are transparent, this is likely an SVG/PNG with transparent bg
    const transparentRatio = bgCount > 0 ? transparentCornerPixels / bgCount : 0;
    const hasTransparentBackground = transparentRatio > 0.5;

    // Detect if background is dark (light logo on dark background)
    // This requires inverting the threshold logic to capture light content instead of dark
    const hasDarkBackground = !hasTransparentBackground && avgBgGray < 100;

    // For transparent backgrounds, detect if the content is light/white
    // by sampling the average grayscale of opaque pixels
    let hasLightContent = false;
    if (hasTransparentBackground) {
      let opaqueGraySum = 0;
      let opaqueCount = 0;
      for (let i = 0, p = 0; i < data.length; i += 4, p++) {
        if (data[i + 3] > 10) {
          opaqueGraySum += sharpened[p];
          opaqueCount++;
        }
      }
      const avgOpaqueGray = opaqueCount > 0 ? opaqueGraySum / opaqueCount : 128;
      hasLightContent = avgOpaqueGray > 180; // Light/white content
    }

    for (let i = 0, p = 0; i < data.length; i += 4, p++) {
      const a = data[i + 3];

      // Use preprocessed grayscale value (matches backend processing)
      const gray = sharpened[p];

      // Check if this pixel is background color (based on preprocessed grayscale)
      // IMPORTANT: For images with transparent backgrounds (SVGs, PNGs), skip color-based
      // background detection - transparent pixels ARE the background, all opaque pixels are content
      const isBackground = hasTransparentBackground
        ? false // Don't remove any opaque pixels based on color
        : a > 10 && Math.abs(gray - avgBgGray) < bgThreshold;

      // Check if this pixel has content based on threshold level
      // For transparent backgrounds with light content: LIGHT pixels are content (above inverted threshold)
      // For transparent backgrounds with dark content: DARK pixels are content (below threshold)
      // For dark backgrounds: LIGHT pixels are content (above inverted threshold)
      // For light backgrounds: DARK pixels are content (below threshold)
      let shouldEtch;
      if (hasTransparentBackground && hasLightContent) {
        // White/light logo on transparent background: capture light pixels
        const invertedThreshold = 255 - threshold;
        shouldEtch = a > 10 && gray > invertedThreshold;
      } else if (hasTransparentBackground) {
        // Dark logo on transparent background: capture dark pixels
        shouldEtch = a > 10 && gray < threshold;
      } else if (hasDarkBackground) {
        // Dark background with light logo: content is ABOVE inverted threshold
        const invertedThreshold = 255 - threshold;
        shouldEtch = a > 10 && gray > invertedThreshold && !isBackground;
      } else {
        // Normal: light background with dark logo
        shouldEtch = a > 10 && gray < threshold && !isBackground;
      }

      if (invert) {
        // Inverted logic: etch what was solid, make solid what was etched
        if (a > 10 && !isBackground) {
          if (shouldEtch) {
            // This was going to be etched, make it solid (transparent background)
            data[i + 3] = 0;
          } else {
            // This was going to be solid, make it etched (metallic)
            const x = (p % highResWidth) / highResWidth;
            const y = Math.floor(p / highResWidth) / highResHeight;
            
            // Create metallic silver with directional sheen
            const t = (Math.sin(x * Math.PI * 2) * 0.5 + 0.5) * 0.8 + 0.1;
            const metallicR = Math.round(130 * (1 - t) + 248 * t);
            const metallicG = Math.round(135 * (1 - t) + 249 * t);
            const metallicB = Math.round(140 * (1 - t) + 252 * t);
            
            data[i] = metallicR;
            data[i + 1] = metallicG;
            data[i + 2] = metallicB;
            data[i + 3] = 255; // Keep fully opaque
          }
        } else {
          // Background areas and transparent areas stay transparent
          data[i + 3] = 0;
        }
      } else {
        // Normal logic: etch dark areas, make light areas and background transparent
        if (shouldEtch) {
            // Calculate position for metallic effect
          const x = (p % highResWidth) / highResWidth;
          const y = Math.floor(p / highResWidth) / highResHeight;
            
            // Create metallic silver with directional sheen
            const t = (Math.sin(x * Math.PI * 2) * 0.5 + 0.5) * 0.8 + 0.1;
            const metallicR = Math.round(130 * (1 - t) + 248 * t);
            const metallicG = Math.round(135 * (1 - t) + 249 * t);
            const metallicB = Math.round(140 * (1 - t) + 252 * t);
            
            data[i] = metallicR;
            data[i + 1] = metallicG;
            data[i + 2] = metallicB;
            data[i + 3] = 255; // Keep fully opaque
          } else {
          // Make background and light areas transparent
            data[i + 3] = 0;
          }
        }
        
      // Additional background removal: remove extreme background colors not caught by threshold
      // SKIP for transparent backgrounds - all opaque content should be preserved
      if (!hasTransparentBackground && a > 10) {
        if (hasDarkBackground && gray < 15) {
          // Dark background: remove very dark areas (the background)
          data[i + 3] = 0;
        } else if (!hasDarkBackground && gray > 240) {
          // Light background: remove very light areas (the background)
          data[i + 3] = 0;
        }
      }
    }

    // Put the processed high-res data back
    highResCtx.putImageData(imageData, 0, 0);
    
    // Create final canvas at correct resolution (accounting for rotation)
    const finalCanvas = document.createElement('canvas');
    const finalCtx = finalCanvas.getContext('2d');
    finalCanvas.width = canvasWidth;
    finalCanvas.height = canvasHeight;
    
    // Enable high-quality downsampling
    finalCtx.imageSmoothingEnabled = true;
    finalCtx.imageSmoothingQuality = 'high';
    
    // Downsample the high-res image to final resolution
    finalCtx.drawImage(highResCanvas, 0, 0, canvasWidth, canvasHeight);
    
    return finalCanvas;
  };

  // Fast function to update metallic effect on stored base canvas
  const updateMetallicEffect = (baseCanvas, options = {}) => {
    if (!baseCanvas) return null;
    
    const { fastMode = false } = options;
    
    // For fast UI updates, downsample to 1x resolution first
    let canvas, ctx;
    if (fastMode && baseCanvas.width > 1000) {
      // Downsample to reasonable size for UI (max 1000px)
      const maxSize = 1000;
      const scale = Math.min(maxSize / baseCanvas.width, maxSize / baseCanvas.height);
      canvas = document.createElement('canvas');
      canvas.width = Math.floor(baseCanvas.width * scale);
      canvas.height = Math.floor(baseCanvas.height * scale);
      ctx = canvas.getContext('2d');
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';
      ctx.drawImage(baseCanvas, 0, 0, canvas.width, canvas.height);
    } else {
      // Use full resolution
      canvas = document.createElement('canvas');
      canvas.width = baseCanvas.width;
      canvas.height = baseCanvas.height;
      ctx = canvas.getContext('2d');
      ctx.drawImage(baseCanvas, 0, 0);
    }
    
    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const data = imageData.data;
    
    // Check if selected jug has bronze-etch tag
    const isBronzeEtchValue = isBronzeEtch();
    
    // Process all pixels
    for (let i = 0; i < data.length; i += 4) {
      const a = data[i + 3];
      if (a > 0) {
        // Calculate position for metallic effect
        const x = ((i / 4) % canvas.width) / canvas.width;
        
        // Create metallic effect - bronze or silver based on jug tag
        const distanceFromCenter = Math.abs(x - 0.5) * 2;
        const t = (1 - distanceFromCenter) * 0.8 + 0.1;
        
        let metallicR, metallicG, metallicB;
        if (isBronzeEtchValue) {
          const bronzeBase = { r: 155, g: 120, b: 85 };
          const bronzeHighlight = { r: 230, g: 190, b: 130 };
          metallicR = Math.round(bronzeBase.r * (1 - t) + bronzeHighlight.r * t);
          metallicG = Math.round(bronzeBase.g * (1 - t) + bronzeHighlight.g * t);
          metallicB = Math.round(bronzeBase.b * (1 - t) + bronzeHighlight.b * t);
        } else {
          metallicR = Math.round(130 * (1 - t) + 248 * t);
          metallicG = Math.round(135 * (1 - t) + 249 * t);
          metallicB = Math.round(140 * (1 - t) + 252 * t);
        }
        
        data[i] = metallicR;
        data[i + 1] = metallicG;
        data[i + 2] = metallicB;
      }
    }
    
    ctx.putImageData(imageData, 0, 0);
    return canvas;
  };

  // Function to convert processed canvas to SVG using a simpler approach
  const canvasToSVG = (canvas, width, height) => {
    // Convert the processed canvas to a data URL
    const dataURL = canvas.toDataURL('image/png');
    
    // Create SVG with the processed image embedded
    // This preserves the exact appearance without trying to trace paths
    const svg = `
      <svg width="${width}" height="${height}" viewBox="0 0 ${width} ${height}" xmlns="http://www.w3.org/2000/svg">
        <path d="M0,0 L0.001,0.001" />
        <path d="M${width},${height} L${width}.001,${height}.001" />
        <image href="${dataURL}" width="${width}" height="${height}" />
      </svg>
    `;
    
    return svg;
  };


  // Simple logo processing - just make it metallic
  const processImageLocally = async (imageUrl, options = {}) => {
    const {
      keepDrawerOpen = false,
      preserveTransform = false,
      preview = false,
      thresholdValue,
      forUpload = false,
      invert = false,
      skipModal = false,
      isSVG = false,
      skipBlobUpdate = false,
      displayCap = null,
    } = options;
    console.log("processImageLocally called with options:", options, "thresholdValue:", thresholdValue, "forUpload:", forUpload, "invert:", invert, "skipModal:", skipModal, "isSVG:", isSVG);
    const scale = preview ? 0.4 : 1; // Use 0.4 scale for preview (balanced size + quality)
    
    return new Promise((resolve, reject) => {
      try {
        setProcessingProgress(60);
        
        // Use cached Image element if available (for threshold/invert changes), otherwise load from URL
        let img = originalLogoImageRef.current;
        
        if (img && img.complete && img.naturalWidth > 0) {
          // Image is already loaded, process immediately
          processImageDirectly();
        } else {
          // Need to load the image
          img = new Image();
          img.crossOrigin = 'anonymous';
          
          img.onload = () => {
            // Cache the image for future threshold/invert changes
            if (!originalLogoImageRef.current) {
              originalLogoImageRef.current = img;
            }
            processImageDirectly();
          };
          
          img.onerror = (error) => {
            console.error("Error loading image:", error);
            setLogoProcessingState('error');
            setProcessingProgress(0);
            reject(error);
          };
          
          img.src = imageUrl;
        }
        
        function processImageDirectly() {
          // Use high-resolution rendering for much smoother edges
          const finalThreshold = thresholdValue || 128;
          console.log("Processing image with threshold:", finalThreshold, "Original threshold param:", thresholdValue, "Invert:", invert);

          // Get original image dimensions
          const originalWidth = img.naturalWidth || img.width;
          const originalHeight = img.naturalHeight || img.height;
          const originalArea = originalWidth * originalHeight;

          console.log(`📐 Processing image: ${originalWidth}x${originalHeight} (area: ${originalArea}px)`);

          // Safety check: if dimensions are 0, we have a problem
          if (originalWidth === 0 || originalHeight === 0) {
            console.error("❌ Image has 0 dimensions! Cannot process.");
            setLogoProcessingState("error");
            setProcessingProgress(0);
            reject(new Error("Image has 0 dimensions"));
            return;
          }

          // ============================================================
          // QUALITY DETECTION - Warn users about low-res, complex, or photo images
          // ============================================================
          // Only detect on first processing (not threshold/invert changes)
          // SVGs get special handling - they're infinitely scalable, so we skip
          // resolution warnings and edge density (anti-aliasing creates false positives)
          if (!originalLogoDimensionsRef.current) {
            let warning = null;

            // Check 1: Very small source images (likely to look pixelated)
            const isVerySmall = !isSVG && (originalWidth < 200 || originalHeight < 200);
            const isSmall = !isSVG && (originalWidth < 400 || originalHeight < 400);

            // For SVGs with tiny intrinsic dimensions, render at a larger size for analysis
            const svgRenderSize = isSVG
              ? Math.max(400, Math.max(originalWidth, originalHeight))
              : null;

            // Check 2: Image complexity analysis (detect photos/AI images)
            const analysisCanvas = document.createElement("canvas");
            const analysisCtx = analysisCanvas.getContext("2d");
            const sampleSize = 200; // Sample at 200x200 for fast analysis
            analysisCanvas.width = sampleSize;
            analysisCanvas.height = sampleSize;

            if (isSVG && svgRenderSize) {
              const aspectRatio = originalWidth / originalHeight;
              const renderWidthA = aspectRatio >= 1 ? svgRenderSize : svgRenderSize * aspectRatio;
              const renderHeightA = aspectRatio >= 1 ? svgRenderSize / aspectRatio : svgRenderSize;

              const svgCanvas = document.createElement("canvas");
              svgCanvas.width = renderWidthA;
              svgCanvas.height = renderHeightA;
              const svgCtx = svgCanvas.getContext("2d");
              svgCtx.drawImage(img, 0, 0, renderWidthA, renderHeightA);

              analysisCtx.drawImage(svgCanvas, 0, 0, sampleSize, sampleSize);
              console.log(`📐 SVG analysis: rendered at ${renderWidthA}x${renderHeightA} for analysis`);
            } else {
              analysisCtx.drawImage(img, 0, 0, sampleSize, sampleSize);
            }

            const analysisData = analysisCtx.getImageData(0, 0, sampleSize, sampleSize);
            const pixels = analysisData.data;

            const colorSet = new Set();
            let midToneCount = 0;
            let totalOpaquePixels = 0;
            let gradientTransitions = 0;
            let lastGray = -1;

            for (let i = 0; i < pixels.length; i += 4) {
              const r = pixels[i];
              const g = pixels[i + 1];
              const b = pixels[i + 2];
              const a = pixels[i + 3];

              if (a > 128) {
                totalOpaquePixels++;

                const qr = Math.floor(r / 8) * 8;
                const qg = Math.floor(g / 8) * 8;
                const qb = Math.floor(b / 8) * 8;
                colorSet.add(`${qr},${qg},${qb}`);

                const gray = Math.round(0.299 * r + 0.587 * g + 0.114 * b);

                if (gray > 50 && gray < 200) {
                  midToneCount++;
                }

                if (lastGray !== -1) {
                  const diff = Math.abs(gray - lastGray);
                  if (diff > 0 && diff < 20) {
                    gradientTransitions++;
                  }
                }
                lastGray = gray;
              }
            }

            const uniqueColors = colorSet.size;
            const midTonePercentage = totalOpaquePixels > 0 ? (midToneCount / totalOpaquePixels) * 100 : 0;
            const gradientPercentage = totalOpaquePixels > 0 ? (gradientTransitions / totalOpaquePixels) * 100 : 0;

            // Check 3: Edge density analysis for fine line work detection
            // SKIP for SVGs - anti-aliasing when rasterizing creates false edge pixels
            let edgePixelCount = 0;
            let edgeDensity = 0;
            const edgeThreshold = 30;

            if (!isSVG) {
              for (let y = 0; y < sampleSize; y++) {
                for (let x = 1; x < sampleSize - 1; x++) {
                  const idx = (y * sampleSize + x) * 4;
                  const idxLeft = (y * sampleSize + (x - 1)) * 4;
                  const idxRight = (y * sampleSize + (x + 1)) * 4;

                  const gray = Math.round(0.299 * pixels[idx] + 0.587 * pixels[idx + 1] + 0.114 * pixels[idx + 2]);
                  const grayLeft = Math.round(0.299 * pixels[idxLeft] + 0.587 * pixels[idxLeft + 1] + 0.114 * pixels[idxLeft + 2]);
                  const grayRight = Math.round(0.299 * pixels[idxRight] + 0.587 * pixels[idxRight + 1] + 0.114 * pixels[idxRight + 2]);

                  if (Math.abs(gray - grayLeft) > edgeThreshold || Math.abs(gray - grayRight) > edgeThreshold) {
                    edgePixelCount++;
                  }
                }
              }

              for (let y = 1; y < sampleSize - 1; y++) {
                for (let x = 0; x < sampleSize; x++) {
                  const idx = (y * sampleSize + x) * 4;
                  const idxUp = ((y - 1) * sampleSize + x) * 4;
                  const idxDown = ((y + 1) * sampleSize + x) * 4;

                  const gray = Math.round(0.299 * pixels[idx] + 0.587 * pixels[idx + 1] + 0.114 * pixels[idx + 2]);
                  const grayUp = Math.round(0.299 * pixels[idxUp] + 0.587 * pixels[idxUp + 1] + 0.114 * pixels[idxUp + 2]);
                  const grayDown = Math.round(0.299 * pixels[idxDown] + 0.587 * pixels[idxDown + 1] + 0.114 * pixels[idxDown + 2]);

                  if (Math.abs(gray - grayUp) > edgeThreshold || Math.abs(gray - grayDown) > edgeThreshold) {
                    edgePixelCount++;
                  }
                }
              }

              edgeDensity = totalOpaquePixels > 0 ? (edgePixelCount / (totalOpaquePixels * 2)) * 100 : 0;
            } else {
              console.log("⏭️ Skipping edge density analysis for SVG (anti-aliasing creates false positives)");
            }

            console.log("📊 Image analysis:", {
              isSVG,
              uniqueColors,
              midTonePercentage: midTonePercentage.toFixed(1) + "%",
              gradientPercentage: gradientPercentage.toFixed(1) + "%",
              edgeDensity: edgeDensity.toFixed(1) + "%",
              totalOpaquePixels,
            });

            const isLikelyPhoto = uniqueColors > 500 && midTonePercentage > 40;
            const hasComplexDetail = uniqueColors > 500 && gradientPercentage > 30;
            const hasTooManyColors = uniqueColors > 1500 && midTonePercentage > 20;
            const hasFineLineWork = !isSVG && edgeDensity > 32;
            const hasExtremeDetail = !isSVG && edgeDensity > 45;

            // Priority: Photo warning > Fine line work > Complex detail > Low resolution
            if (isLikelyPhoto || hasTooManyColors) {
              warning = {
                type: "photo_detected",
                message: "This image appears to be a photograph or AI-generated image with many colors and gradients. Photos typically do NOT engrave well because fine details and subtle shading will be lost. For best results, use a simple logo or graphic with solid colors and clear lines.",
                originalSize: `${originalWidth}×${originalHeight}`,
                details: `${uniqueColors} colors detected, ${midTonePercentage.toFixed(0)}% mid-tones`,
              };
            } else if (hasExtremeDetail) {
              warning = {
                type: "fine_detail",
                message: "This image contains extremely fine line work, hatching, or detailed textures. These fine details will likely bleed together during the laser engraving process because the laser beam has a fixed width. For best results, use images with thicker, bolder lines and less intricate detail.",
                originalSize: `${originalWidth}×${originalHeight}`,
                details: `${edgeDensity.toFixed(0)}% edge density (very high detail)`,
              };
            } else if (hasFineLineWork) {
              warning = {
                type: "fine_detail",
                message: "This image has fine line work or detailed textures that may not engrave clearly. Thin lines and small details can blend together during the engraving process. Please review the preview carefully and consider using a simpler version of this image.",
                originalSize: `${originalWidth}×${originalHeight}`,
                details: `${edgeDensity.toFixed(0)}% edge density (high detail)`,
              };
            } else if (hasComplexDetail) {
              warning = {
                type: "complex_detail",
                message: "This image has a lot of fine detail and color variation which may not engrave clearly. Small details and thin lines can blend together during the engraving process. Please review the preview carefully.",
                originalSize: `${originalWidth}×${originalHeight}`,
                details: `${uniqueColors} colors, ${gradientPercentage.toFixed(0)}% gradients`,
              };
            } else if (isVerySmall) {
              warning = {
                type: "very_low_res",
                message: "This image appears to be very low resolution which will likely cause quality issues when engraved. For best results, use a source image that is at least 400×400 pixels.",
                originalSize: `${originalWidth}×${originalHeight}`,
              };
            } else if (isSmall) {
              warning = {
                type: "low_res",
                message: "This image appears to be low resolution which may cause quality issues. Please verify that the rendering meets your standards before adding to cart.",
                originalSize: `${originalWidth}×${originalHeight}`,
              };
            }

            setQualityWarning(warning);
            setQualityAcknowledged(false);
          }

          // Store original dimensions on first processing for consistent quality
          // SVGs often have tiny intrinsic dimensions, so we need to scale them up
          if (!originalLogoDimensionsRef.current) {
            // For SVGs, always use a large base size since they're infinitely scalable
            // For raster images, use a minimum size of 1200px on the longest side
            const minSize = isSVG ? 1600 : 1200;
            let baseWidth = originalWidth;
            let baseHeight = originalHeight;

            if (isSVG || (baseWidth < minSize && baseHeight < minSize)) {
              const scaleFactor = minSize / Math.max(baseWidth, baseHeight);
              baseWidth = Math.round(baseWidth * scaleFactor);
              baseHeight = Math.round(baseHeight * scaleFactor);
            }

            originalLogoDimensionsRef.current = { width: baseWidth, height: baseHeight, isSVG };
            console.log(`📐 Stored original dimensions: ${baseWidth}x${baseHeight} (source was ${originalWidth}x${originalHeight}, isSVG: ${isSVG})`);
          }

          // Always use stored dimensions for consistent quality across threshold changes
          const storedDims = originalLogoDimensionsRef.current;
          const fullWidth = Math.max(1, Math.floor(storedDims.width * scale));
          const fullHeight = Math.max(1, Math.floor(storedDims.height * scale));

          // For slider display renders, cap the dimensions for speed.
          let renderWidth = fullWidth;
          let renderHeight = fullHeight;
          if (displayCap && (renderWidth > displayCap || renderHeight > displayCap)) {
            const ratio = displayCap / Math.max(renderWidth, renderHeight);
            renderWidth = Math.round(renderWidth * ratio);
            renderHeight = Math.round(renderHeight * ratio);
          }

          // Precompute grayscale once at the full render output resolution with 4×
          // supersampling via GPU, so the fast threshold slider can re-threshold without
          // reprocessing (see renderThresholdFast).
          if (!precomputedGrayscaleRef.current && !skipBlobUpdate) {
            const gW = renderWidth;
            const gH = renderHeight;
            const gW4 = gW * 4;
            const gH4 = gH * 4;

            // --- Pass 1: alpha at output size (downsampled from source) ---
            const alphaCanvas = document.createElement("canvas");
            alphaCanvas.width = gW;
            alphaCanvas.height = gH;
            alphaCanvas.getContext("2d").drawImage(img, 0, 0, gW, gH);
            const alphaRaw = alphaCanvas.getContext("2d").getImageData(0, 0, gW, gH).data;
            const gAlpha = new Uint8ClampedArray(gW * gH);
            for (let i = 0, p = 0; i < alphaRaw.length; i += 4, p++) gAlpha[p] = alphaRaw[i + 3];

            // --- Pass 2: 4× supersampled grayscale via GPU ---
            const hiCanvas = document.createElement("canvas");
            hiCanvas.width = gW4;
            hiCanvas.height = gH4;
            const hiCtx = hiCanvas.getContext("2d");
            hiCtx.fillStyle = "#ffffff";
            hiCtx.fillRect(0, 0, gW4, gH4);
            hiCtx.drawImage(img, 0, 0, gW4, gH4);

            const dsCanvas = document.createElement("canvas");
            dsCanvas.width = gW;
            dsCanvas.height = gH;
            const dsCtx = dsCanvas.getContext("2d");
            dsCtx.filter = "grayscale(100%)";
            dsCtx.imageSmoothingEnabled = true;
            dsCtx.imageSmoothingQuality = "high";
            dsCtx.drawImage(hiCanvas, 0, 0, gW, gH);
            dsCtx.filter = "none";

            const dsRaw = dsCtx.getImageData(0, 0, gW, gH).data;
            const gGray = new Uint8ClampedArray(gW * gH);
            for (let i = 0, p = 0; i < dsRaw.length; i += 4, p++) gGray[p] = dsRaw[i];

            // Background detection (same logic as renderHighRes)
            const sampleEdge = Math.min(gW, gH) * 0.1;
            let bgSum = 0, bgCount = 0, transpCount = 0;
            for (let p = 0; p < gW * gH; p++) {
              const px = p % gW, py = Math.floor(p / gW);
              if (px < sampleEdge || px >= gW - sampleEdge || py < sampleEdge || py >= gH - sampleEdge) {
                if (gAlpha[p] > 0) {
                  bgSum += gGray[p];
                } else {
                  bgSum += 255;
                  transpCount++;
                }
                bgCount++;
              }
            }
            const avgBgGray = bgCount > 0 ? bgSum / bgCount : 255;
            const transpRatio = bgCount > 0 ? transpCount / bgCount : 0;
            const hasTransparentBg = transpRatio > 0.5;
            const hasDarkBg = !hasTransparentBg && avgBgGray < 100;
            let hasLightContent = false;
            if (hasTransparentBg) {
              let opSum = 0, opCount = 0;
              for (let p = 0; p < gW * gH; p++) {
                if (gAlpha[p] > 10) {
                  opSum += gGray[p];
                  opCount++;
                }
              }
              hasLightContent = opCount > 0 && opSum / opCount > 180;
            }

            precomputedGrayscaleRef.current = {
              gray: gGray,
              alpha: gAlpha,
              width: gW,
              height: gH,
              avgBgGray,
              hasTransparentBg,
              hasDarkBg,
              hasLightContent,
            };
          }

          // For slider preview renders (displayCap set), use scale=1 so the pixel loop
          // runs on a small canvas — 16x fewer pixels, near-instant.
          const highResScale = displayCap ? 1 : 4;
          let baseCanvas = renderHighRes(img, renderWidth, renderHeight, finalThreshold, invert, 0, highResScale);

          // Store the base canvas (without metallic effect) for fast color updates
          // MUST be done before applying metallic effect
          if (!forUpload) {
            const storedCanvas = document.createElement('canvas');
            storedCanvas.width = baseCanvas.width;
            storedCanvas.height = baseCanvas.height;
            const storedCtx = storedCanvas.getContext('2d');
            storedCtx.drawImage(baseCanvas, 0, 0);

            if (!preserveTransform && !skipBlobUpdate) {
              processedLogoCanvasRef.current = storedCanvas;
              console.log('💾 Stored base canvas for fast updates:', storedCanvas.width, 'x', storedCanvas.height);
            }
          }

          // Store the Image element for future threshold/invert changes (skip reloading)
          if (!originalLogoImageRef.current && img && img.complete) {
            originalLogoImageRef.current = img;
            console.log('💾 Cached original Image element for fast reprocessing');
          }

          // Apply metallic effect (bronze or silver) for UI preview, not for upload
          const canvas = document.createElement('canvas');
          canvas.width = baseCanvas.width;
          canvas.height = baseCanvas.height;
          const canvasCtx = canvas.getContext('2d');
          canvasCtx.drawImage(baseCanvas, 0, 0);

          // For slider preview renders, skip the metallic recolor pass — the threshold
          // preview only needs to show which pixels survive vs. go transparent.
          if (!forUpload && !skipBlobUpdate) {
            const ctx = canvas.getContext('2d');
            const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
            const data = imageData.data;

            // Check if selected jug has bronze-etch tag
            const isBronzeEtchValue = isBronzeEtch();

            for (let i = 0; i < data.length; i += 4) {
              const a = data[i + 3];
              if (a > 0) {
                const x = (i / 4) % canvas.width / canvas.width;
                const y = Math.floor((i / 4) / canvas.width) / canvas.height;

                const distanceFromCenter = Math.abs(x - 0.5) * 2;
                const t = (1 - distanceFromCenter) * 0.8 + 0.1;

                let metallicR, metallicG, metallicB;
                if (isBronzeEtchValue) {
                  const bronzeBase = { r: 155, g: 120, b: 85 };
                  const bronzeHighlight = { r: 230, g: 190, b: 130 };
                  metallicR = Math.round(bronzeBase.r * (1 - t) + bronzeHighlight.r * t);
                  metallicG = Math.round(bronzeBase.g * (1 - t) + bronzeHighlight.g * t);
                  metallicB = Math.round(bronzeBase.b * (1 - t) + bronzeHighlight.b * t);
                } else {
                  metallicR = Math.round(130 * (1 - t) + 248 * t);
                  metallicG = Math.round(135 * (1 - t) + 249 * t);
                  metallicB = Math.round(140 * (1 - t) + 252 * t);
                }

                data[i] = metallicR;
                data[i + 1] = metallicG;
                data[i + 2] = metallicB;
                data[i + 3] = a; // Keep original alpha
              }
            }

            ctx.putImageData(imageData, 0, 0);
          }

          // Create high-quality PNG for Cloudinary upload
          canvas.toBlob((pngBlob) => {
            if (!pngBlob) return;

            if (!skipBlobUpdate) {
              setProcessedBlob(pngBlob);
              setProcessedSize({ width: canvas.width, height: canvas.height });
            }

            // Store display dimensions on first full-quality processing
            if (!displayDimensionsRef.current && !skipBlobUpdate) {
              displayDimensionsRef.current = { width: canvas.width, height: canvas.height };
              console.log(`📐 Stored display dimensions: ${canvas.width}x${canvas.height}`);
            }

            // Always use the stored display dimensions for the SVG viewport so the image
            // stays the same intrinsic size regardless of whether this is a capped
            // (slider preview) or full-quality render.
            const displayWidth = displayDimensionsRef.current?.width ?? canvas.width;
            const displayHeight = displayDimensionsRef.current?.height ?? canvas.height;

            let displayUrl;
            try {
              const svgString = canvasToSVG(canvas, displayWidth, displayHeight);
              const svgBlob = new Blob([svgString], { type: 'image/svg+xml' });
              displayUrl = URL.createObjectURL(svgBlob);

              // Use SVG for UI display (crisp)
              setProcessedLogoUrl(displayUrl);
            } catch (svgError) {
              console.error("SVG creation for UI failed, using PNG:", svgError);
              displayUrl = URL.createObjectURL(pngBlob);
              setProcessedLogoUrl(displayUrl);
            }

            setLogoProcessingState('processed');
            setProcessingProgress(100);

            // Show the review modal for user to approve or adjust
            if (!skipModal) {
              setShowReviewModal(true);
            }

            resolve({ displayUrl });
          }, 'image/png');
        }
      
      } catch (error) {
        console.error("Local processing error:", error);
        setLogoProcessingState('error');
        setProcessingProgress(0);
        reject(error);
      }
    });
  };

  // Handle file upload
  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

      // Convert to base64 immediately (this is the TRUE original client file)
      const reader = new FileReader();
      reader.onloadend = () => {
        const dataUrl = reader.result; 
        const base64 = dataUrl.split(',')[1];
        setOriginalLogoBase64(base64);
      };
      reader.readAsDataURL(file);

    // Validate image dimensions (must be at least 300px in width or height)
    // SVG files are exempt from dimension requirements
    const validateImageDimensions = (file) => {
      return new Promise((resolve, reject) => {
        // Check if file is SVG - skip dimension validation for SVG files
        const isSVG = file.type === 'image/svg+xml' || file.name.toLowerCase().endsWith('.svg');
        
        if (isSVG) {
          console.log('SVG file detected - skipping dimension validation');
          resolve({ width: 'auto', height: 'auto', isSVG: true });
          return;
        }
        
        const img = new Image();
        const url = URL.createObjectURL(file);
        
        img.onload = () => {
          URL.revokeObjectURL(url);
          const width = img.naturalWidth;
          const height = img.naturalHeight;
          
          // Check if image is at least 300px in width OR height
          if (width >= 300 || height >= 300) {
            resolve({ width, height, isSVG: false });
          } else {
            reject(new Error(`Image must be at least 300px in width or height. Current dimensions: ${width}x${height}px`));
          }
        };
        
        img.onerror = () => {
          URL.revokeObjectURL(url);
          reject(new Error('Failed to load image for validation'));
        };
        
        img.src = url;
      });
    };

    try {
      // Validate dimensions before processing
      await validateImageDimensions(file);
      
      // Clear any existing designs when uploading new logo
      setCustomElements([]);
      setSelectedPresetDesign(null);
      setCustomText(''); // Clear custom text

      console.log("Processing logo for etching:", file.name, file.type);
      await processLogoForEtching(file);
    } catch (error) {
      console.error("Image validation error:", error);
      setLogoProcessingState('error');
      setProcessingProgress(0);
      
      // Show error message to user
      alert(error.message || 'Image validation failed. Please try again with a larger image.');
      
      // Reset file input
      e.target.value = '';
    }
  };

  // Handle threshold value change with debouncing
  const handleThresholdChange = async (value) => {
    console.log('Applying threshold change:', value);
    
    if (originalLogoUrl) {
      // Set processing state to show progress
      setLogoProcessingState('processing');
      setProcessingProgress(50);
      
      try {
        // Use preview mode (0.5 scale) for faster threshold/invert changes
        // This is just for UI preview, full quality will be used when adding to cart
        await processImageLocally(originalLogoUrl, {
          thresholdValue: value,
          keepDrawerOpen: true, // Keep drawer open during processing
          invert: invertEtch,
          preview: true, // Use lower resolution for faster processing
          isSVG: isSourceSVG, // Pass SVG flag for appropriate handling
        });

        // Update processing state to show completion
        setLogoProcessingState('processed');
        setProcessingProgress(100);
        
        console.log('Threshold applied successfully with value:', value);
      } catch (error) {
        console.error('Error applying threshold:', error);
        setLogoProcessingState('error');
        setProcessingProgress(0);
      }
    }
  };

  // Debounced threshold change - waits 500ms after user stops moving slider
  const handleThresholdChangeDebounced = (value) => {
    // Clear any existing timeout
    if (thresholdDebounceRef.current) {
      clearTimeout(thresholdDebounceRef.current);
    }
    
    // Set new timeout
    thresholdDebounceRef.current = setTimeout(() => {
      handleThresholdChange(value);
    }, 500); // Wait 500ms after user stops moving slider
  };
  
  // Instant threshold preview using precomputed grayscale.
  // Writes directly to previewCanvasRef — no toDataURL, no PNG encoding, no React
  // state update, no SVG wrapper. The loop only outputs opaque vs transparent
  // because the review image has filter:brightness(0) which turns every opaque
  // pixel solid black in the browser anyway. Kept in sync with renderHighRes.
  const renderThresholdFast = (value, invertOverride) => {
    const stored = precomputedGrayscaleRef.current;
    const previewCanvas = previewCanvasRef.current;
    if (!stored || !previewCanvas) return false;

    const invert = invertOverride !== undefined ? invertOverride : invertEtch;
    const { gray, alpha, width, height, avgBgGray, hasTransparentBg, hasDarkBg, hasLightContent } = stored;
    const bgThreshold = 20;

    if (previewCanvas.width !== width || previewCanvas.height !== height) {
      previewCanvas.width = width;
      previewCanvas.height = height;
    }

    const imageData = new ImageData(width, height);
    const data = imageData.data;

    for (let p = 0; p < width * height; p++) {
      const i = p * 4;
      const a = alpha[p];
      const g = gray[p];

      const isBackground = hasTransparentBg
        ? false
        : a > 10 && Math.abs(g - avgBgGray) < bgThreshold;

      let shouldEtch;
      if (hasTransparentBg && hasLightContent) {
        shouldEtch = a > 10 && g > 255 - value;
      } else if (hasTransparentBg) {
        shouldEtch = a > 10 && g < value;
      } else if (hasDarkBg) {
        shouldEtch = a > 10 && g > 255 - value && !isBackground;
      } else {
        shouldEtch = a > 10 && g < value && !isBackground;
      }

      if (invert) {
        if (a > 10 && !isBackground) shouldEtch = !shouldEtch;
        else shouldEtch = false;
      } else if (a <= 10 || isBackground) {
        shouldEtch = false;
      }

      // Additional bg removal (mirrors renderHighRes)
      if (!hasTransparentBg && a > 10) {
        if (hasDarkBg && g < 15) shouldEtch = false;
        else if (!hasDarkBg && g > 240) shouldEtch = false;
      }

      if (shouldEtch) {
        data[i] = data[i + 1] = data[i + 2] = 0;
        data[i + 3] = 255;
      }
      // else: all bytes default to 0 (transparent) from new ImageData
    }

    previewCanvas.getContext("2d").putImageData(imageData, 0, 0);
    return true;
  };

  // Threshold change for review modal.
  // Stores the latest value and schedules ONE renderThresholdFast per animation
  // frame via RAF. The full-quality render only runs once when the user clicks
  // ACCEPT, so the slider never blocks the main thread.
  const handleThresholdChangeForModal = (value) => {
    if (!originalLogoUrl) return;

    // Always track the latest requested value
    pendingThresholdValueRef.current = value;

    // RAF throttle: if a frame is already queued, skip — it will use the latest value
    if (rafThresholdRef.current) return;
    rafThresholdRef.current = requestAnimationFrame(() => {
      rafThresholdRef.current = null;
      renderThresholdFast(pendingThresholdValueRef.current);
    });
  };

  const handleThresholdChangeFullResDebounced = (value) => {
    handleThresholdChangeForModal(value);
  };

  // Function to apply rotation to any image blob
  const applyRotationToImage = async (imageUrl, rotation) => {
    // Normalize the rotation input. Anything falsy or a multiple of 360 is a
    // no-op — copy the source blob through so callers still get a fresh Blob.
    const rotationNum = Number(rotation) || 0;
    const normalized = ((rotationNum % 360) + 360) % 360;
    if (normalized === 0) {
      const response = await fetch(imageUrl);
      return await response.blob();
    }

    return new Promise((resolve, reject) => {
      const img = new Image();
      // Only request CORS for HTTP(S) sources. Setting crossOrigin on a
      // same-origin blob:/data: URL can cause silent load failures in some
      // Safari builds — we don't need CORS for those since they're same-origin.
      if (/^https?:/i.test(imageUrl)) {
        img.crossOrigin = "anonymous";
      }

      img.onload = () => {
        // Use naturalWidth/Height — img.width/height can return 0 for detached
        // images without a width attr. Fall back only if natural dims missing.
        const w = img.naturalWidth || img.width;
        const h = img.naturalHeight || img.height;
        if (!w || !h) {
          reject(new Error(
            `applyRotationToImage: source image has no intrinsic size (got ${w}x${h}). URL: ${imageUrl.slice(0, 60)}`,
          ));
          return;
        }

        const radians = (normalized * Math.PI) / 180;
        const cos = Math.abs(Math.cos(radians));
        const sin = Math.abs(Math.sin(radians));
        const canvasWidth = Math.ceil(w * cos + h * sin);
        const canvasHeight = Math.ceil(w * sin + h * cos);

        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        canvas.width = canvasWidth;
        canvas.height = canvasHeight;

        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = "high";
        ctx.translate(canvasWidth / 2, canvasHeight / 2);
        ctx.rotate(radians);
        ctx.drawImage(img, -w / 2, -h / 2, w, h);

        canvas.toBlob((blob) => {
          if (blob) {
            console.log(`Applied rotation to image: ${normalized}° (src ${w}x${h} → ${canvasWidth}x${canvasHeight})`);
            resolve(blob);
          } else {
            reject(new Error('Failed to create rotated image blob'));
          }
        }, 'image/png');
      };

      img.onerror = () =>
        reject(new Error(`applyRotationToImage: image failed to load (${imageUrl.slice(0, 60)})`));
      img.src = imageUrl;
    });
  };

  // Generate clean version for upload (no noise, crisp edges)
  const generateCleanUploadVersion = async () => {
    if (!originalLogoUrl) return null;
    
    try {
      console.log('Generating clean upload version (no noise)...');
      
      // Get rotation from customElements if available
      const rotation = customElements.length > 0 ? (customElements[0].rotation || 0) : 0;
      console.log('Applying rotation to upload version:', rotation);
      console.log('CustomElements:', customElements);
      console.log('First element rotation:', customElements[0]?.rotation);
      
      // Process image without noise for clean upload
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      
      const img = new Image();
      img.crossOrigin = 'anonymous';
      
      return new Promise((resolve, reject) => {
        img.onload = () => {
          const finalThreshold = thresholdValue || 128;

          // Use the same stored dimensions as processImageLocally so the upload
          // canvas matches the preview resolution (min 1200px raster / 1600px SVG).
          // Without this, a 400px source would produce a smaller upload canvas
          // than the preview shows.
          const storedDims = originalLogoDimensionsRef.current;
          const targetWidth = storedDims ? storedDims.width : img.naturalWidth;
          const targetHeight = storedDims ? storedDims.height : img.naturalHeight;
          console.log(`Processing clean upload version at ${targetWidth}x${targetHeight} (source: ${img.naturalWidth}x${img.naturalHeight}), threshold: ${finalThreshold}`);

          // Render at 4× the stored dimensions for maximum vectorizer input quality
          const canvas = renderHighRes(img, targetWidth, targetHeight, finalThreshold, invertEtch, rotation);

          // Convert to PNG for Cloudinary processing (let Cloudinary handle SVG conversion)
          canvas.toBlob((blob) => {
            if (blob) {
              console.log(`Clean PNG upload version: ${canvas.width}x${canvas.height} (${(blob.size / 1024).toFixed(0)} KB)`);
              resolve(blob);
            } else {
              reject(new Error('Failed to generate clean upload version'));
            }
          }, 'image/png');
        };
        
        img.onerror = reject;
        img.src = originalLogoUrl;
      });
      
    } catch (error) {
      console.error('Error generating clean upload version:', error);
      throw error;
    }
  };

  // Add to cart with previewUrl property

  
  async function moderateImage(imageUrl) {
    try {
      console.log('🔄 Starting moderation request for:', imageUrl);
      const startTime = Date.now();
      
      // Add timeout to handle cold starts (60 seconds)
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 60000);
      
      // Use Firebase Functions endpoint
      const res = await fetch('https://moderate-2yordtblma-uc.a.run.app', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ imageUrl }),
        signal: controller.signal,
      });
      
      clearTimeout(timeoutId);
      const duration = Date.now() - startTime;
      console.log(`⏱️ Moderation request took ${duration}ms`);
      
      if (!res.ok) {
        const errorText = await res.text();
        console.error('❌ Moderation API error:', res.status, errorText);
        throw new Error(`Moderation failed: ${res.status} ${errorText}`);
      }
      
      const data = await res.json();
      console.log('✅ Moderation result:', data);
      return data;
    } catch (error) {
      if (error.name === 'AbortError') {
        console.error('⏱️ Moderation request timed out after 60 seconds');
        throw new Error('Moderation request timed out. The server may be starting up (cold start). Please try again.');
      }
      console.error('❌ Error calling moderation API:', error);
      throw error;
    }
  }

  // Google Drive folder ID - get this from the folder URL in Google Drive
  // The folder ID is the long string in the URL: https://drive.google.com/drive/folders/FOLDER_ID_HERE
  const GOOGLE_DRIVE_FOLDER_ID = '0AFXOTJyY31GRUk9PVA';

  // Upload image to Google Drive after Cloudinary upload and moderation
  // Uses backend service account for zero popups - completely automatic
  async function uploadToGoogleDrive(cloudinaryUrl, metadata = {}) {
    // Extract filename from Cloudinary URL or use provided filename
    let fileName = metadata.fileName;
    
    console.log('📤 uploadToGoogleDrive called with:', {
      cloudinaryUrl,
      metadataFileName: metadata.fileName,
      metadataType: metadata.type
    });
    
    // If no filename provided, try to extract from Cloudinary URL
    if (!fileName && cloudinaryUrl) {
      try {
        // Cloudinary URLs typically have format: https://res.cloudinary.com/cloud_name/image/upload/v1234567890/public_id.format
        const urlParts = cloudinaryUrl.split('/');
        const lastPart = urlParts[urlParts.length - 1];
        // Extract public_id and format
        const match = lastPart.match(/^(.+?)\.([^.]+)$/);
        if (match) {
          const publicId = match[1];
          const format = match[2];
          fileName = `${publicId}.${format}`;
          console.log('📁 Extracted filename from URL:', fileName);
        }
      } catch (e) {
        console.warn('Could not extract filename from Cloudinary URL:', e);
      }
    }
    
    // Fallback to generated filename if extraction failed
    if (!fileName) {
      const extension = metadata.type === 'jersey' ? 'svg' : 'png';
      fileName = `${metadata?.type || 'design'}_${Date.now()}.${extension}`;
      console.warn('⚠️ Using fallback filename:', fileName);
    } else {
      console.log('✅ Using filename:', fileName);
    }
    try {
      console.log('📤 Uploading to Google Drive:', cloudinaryUrl);
      const startTime = Date.now();
      
      // Add timeout to handle Render cold starts (60 seconds for free tier)
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 60000);
      
      // For local testing, use: 'http://localhost:10000/apps/customizer/upload-to-drive'
      // For production, use: 'https://customizer-api-lgj8.onrender.com/apps/customizer/upload-to-drive'
      const res = await fetch('https://uploadtodrive-2yordtblma-uc.a.run.app', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          imageUrl: cloudinaryUrl,
          folderId: GOOGLE_DRIVE_FOLDER_ID,
          fileName: fileName, // Pass the extracted/generated filename
          metadata: metadata // Can include product info, design type, etc.
        }),
        signal: controller.signal,
      });
      
      clearTimeout(timeoutId);
      const duration = Date.now() - startTime;
      console.log(`⏱️ Google Drive upload request took ${duration}ms`);
      
      if (!res.ok) {
        const errorText = await res.text();
        console.error('❌ Google Drive upload failed:', res.status, errorText);
        // Don't throw - this is a non-critical operation
        return { success: false, error: errorText };
      }
      
      const data = await res.json();
      console.log('✅ Google Drive upload successful:', data);
      return { success: true, data };
    } catch (error) {
      if (error.name === 'AbortError') {
        console.error('⏱️ Google Drive upload timed out after 60 seconds');
        return { success: false, error: 'Upload timed out. The server may be starting up (cold start).' };
      }
      console.error('❌ Error uploading to Google Drive:', error);
      // Don't throw - this is a non-critical operation that shouldn't block the flow
      return { success: false, error: error.message };
    }
  }

  // 🔗 Firebase HTTPS function URL — prepSvgForLaser returns BOTH a display SVG
  // (renders correctly in the cart Etch Preview, i.e. NOT filled-in) and a
  // laser SVG (radian/laser variants for the laser software). Replaces the old
  // single `vectorize` endpoint whose one SVG rendered filled-in in the cart.
  const FIREBASE_VECTORIZE_PREPROCESSED_URL = 'https://prepsvgforlaser-2yordtblma-uc.a.run.app';
  

  // Helper: Blob → base64 string (no data: prefix)
  const blobToBase64 = (blob) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        const dataUrl = reader.result; // "data:image/png;base64,AAAA..."
        const base64 = dataUrl.split(',')[1];
        resolve(base64);
      };
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });


  const handleAddToCart = async () => {
    if (!selectedColor || (!processedBlob && customElements.length === 0)) {
      alert('Select a product/color and add a custom design first.');
      return;
    }

    try {
      setAddingToCart(true);
      setCartError(null); // Clear any previous error
      
      // Show loading modal and reset progress
      setShowLoadingModal(true);
      setUploadProgress(0);
      
      let securePreviewUrl;
      let originalUploadedUrl;
      let laserSvgUrl;
      if (processedBlob) {
        console.log("Processing SVG vectorization for cart...");

        setUploadProgress(10);
        // Use generateCleanUploadVersion so rotation (from customElements[0].rotation)
        // is baked into the blob before it's sent to the backend.
        const uploadBlob = await generateCleanUploadVersion() || processedBlob;
        const imageBase64 = await blobToBase64(uploadBlob);
        console.log('Sending processed blob to prepSvgForLaser, size:', imageBase64.length);

        // 1) Vectorize using prepSvgForLaser. The blob is already binarized +
        // rotated, so we DON'T send invert/threshold. Returns both a display SVG
        // (correct appearance for the cart preview) and laser SVG variants.
        const vectorizeRes = await fetch(FIREBASE_VECTORIZE_PREPROCESSED_URL, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ imageBase64: imageBase64 }),
        });

        const vectorizeData = await vectorizeRes.json();
        const { displaySvg, laserSvg, radianSvg, tykmaSvg, subpathCount, complexityWarning } = vectorizeData;
        if (!displaySvg || !laserSvg) throw new Error("Vectorization API returned incomplete SVG data");

        console.log(`📊 Vectorization complete: ${subpathCount || 'unknown'} subpaths`);
        if (complexityWarning) {
          console.warn('⚠️ Backend complexity warning:', complexityWarning);
        }

        setUploadProgress(20);

        // 2) Convert SVG strings → Blobs (with proper charset)
        const displaySvgBlob = new Blob([displaySvg], { type: "image/svg+xml;charset=utf-8" });
        const laserSvgBlob = new Blob([laserSvg], { type: "image/svg+xml;charset=utf-8" });
        const radianSvgBlob = radianSvg ? new Blob([radianSvg], { type: "image/svg+xml;charset=utf-8" }) : null;

        // 3a) Upload DISPLAY SVG to Cloudinary (for Etch Preview — renders correctly, not filled-in)
        console.log("Uploading display SVG to Cloudinary...");
        const displayFormData = new FormData();
        displayFormData.append("file", displaySvgBlob, "vectorized-logo-display.svg");
        displayFormData.append("upload_preset", "upload_test");

        setUploadProgress(35);

        const displayUploadRes = await fetch(
          "https://api.cloudinary.com/v1_1/dgepygozk/image/upload",
          { method: "POST", body: displayFormData }
        );
        if (!displayUploadRes.ok) {
          const txt = await displayUploadRes.text();
          throw new Error(`Cloudinary display SVG upload failed: ${txt}`);
        }
        const displayUploaded = await displayUploadRes.json();
        console.log("Display SVG upload successful:", displayUploaded);
        securePreviewUrl = displayUploaded.secure_url; // For "Etch Preview" shown to customer

        setUploadProgress(50);

        // 3b) Upload LASER SVG to Cloudinary (for vectorizedUrl / laser software).
        // Prefer radianSvg (SVG 1.0, presentation attributes) when available.
        console.log("Uploading laser SVG to Cloudinary...");
        const laserFormData = new FormData();
        laserFormData.append("file", radianSvgBlob || laserSvgBlob, "vectorized-logo.svg");
        laserFormData.append("upload_preset", "upload_test");

        const laserUploadRes = await fetch(
          "https://api.cloudinary.com/v1_1/dgepygozk/image/upload",
          { method: "POST", body: laserFormData }
        );

        setUploadProgress(60);

        if (!laserUploadRes.ok) {
          const txt = await laserUploadRes.text();
          throw new Error(`Cloudinary laser SVG upload failed: ${txt}`);
        }
        const laserUploaded = await laserUploadRes.json();
        console.log("Laser SVG upload successful:", laserUploaded);
        laserSvgUrl = laserUploaded.secure_url; // For vectorizedUrl and Google Drive

        // 3c) Upload the original (unprocessed) logo to Cloudinary for comparison
        console.log("Uploading original logo to Cloudinary...");
        const originalBlob = await fetch(`data:image/png;base64,${originalLogoBase64}`).then(r => r.blob());
        const originalFormData = new FormData();
        originalFormData.append("file", originalBlob, "original-logo.png");
        originalFormData.append("upload_preset", "originals");
        originalFormData.append("folder", "customizer-originals");

        const originalUploadRes = await fetch(
          "https://api.cloudinary.com/v1_1/dgepygozk/image/upload",
          { method: "POST", body: originalFormData }
        );
        if (originalUploadRes.ok) {
          const originalUploaded = await originalUploadRes.json();
          console.log("Original logo upload successful:", originalUploaded);
          originalUploadedUrl = originalUploaded.secure_url;
        } else {
          console.warn("Original logo upload failed (non-critical):", await originalUploadRes.text());
        }

        // 4) Save backup to Drive (optional) — use laser SVG for laser software compatibility
        const cloudinaryFileName = laserUploaded.public_id + ".svg";

        setUploadProgress(75);

        await uploadToGoogleDrive(laserSvgUrl, {
          type: "logo",
          fileName: cloudinaryFileName,
          productId: selectedProduct?.id,
          colorId: selectedColor?.id,
          timestamp: new Date().toISOString(),
        });

        setUploadProgress(85);
      }
      
       else if (customElements.length > 0) {
        // Handle custom elements (metallic text, jersey, or preset designs)
        console.log('Processing custom element for cart...');
        
        setUploadProgress(10);
        
        const customElement = customElements[0];
        
        if (customElement.type === 'image' && customElement.imageUrl) {
          // Check if this is a preset design (no Cloudinary upload needed)
          if (selectedPresetDesign) {
            console.log('Using preset design, no Cloudinary upload needed');
            setUploadProgress(85);
            securePreviewUrl = selectedPresetDesign.featured_image?.src || selectedPresetDesign.image?.src || selectedPresetDesign.image?.url;
          } else {
            // This is a custom metallic element (text or jersey) - upload to Cloudinary
            console.log('Processing custom metallic element for cart...');
            
            let elementBlob;
            let fileName;

            // Jersey branch
            console.log('customElement', customElement);
            console.log('jerseyNumber', jerseyNumber);
            console.log('jerseyText', jerseyText);

            if (customElement.imageUrl.includes('blob:') && (jerseyNumber || jerseyText)) {
              console.log('Creating black version of jersey design for Cloudinary...');

              const svgString = await createJerseyDesignForUpload(
                jerseyNumber,
                jerseyText,
                jerseyTextPosition,
                jerseyCurveText
              );
              console.log('svgString', svgString);
              
              const svgBlob = new Blob([svgString], { type: 'image/svg+xml' });

              const elementRotation = customElement.rotation || 0;
              if (elementRotation !== 0) {
                console.log('Applying rotation to jersey design:', elementRotation);
                const svgUrl = URL.createObjectURL(svgBlob);
                elementBlob = await applyRotationToImage(svgUrl, elementRotation);
                URL.revokeObjectURL(svgUrl);
                fileName = 'jersey-design.png'; // rotation turns it into PNG
              } else {
                elementBlob = svgBlob;
                fileName = 'jersey-design.svg'; // stays SVG
              }
            } else {
              // Metallic element branch
              const elementRotation = customElement.rotation || 0;
              const sourceUrl = customElement.highResUrl || customElement.imageUrl;
              elementBlob = await applyRotationToImage(sourceUrl, elementRotation);
              fileName = 'metallic-element.png';
            }

            setUploadProgress(25);
            
            // ✅ Only one FormData build
            const formData = new FormData();
            formData.append('file', elementBlob, fileName);
            
            // Use different presets: 'upload_test' for SVGs (has vectorize), 'originals' for PNGs (no vectorize)
            formData.append('upload_preset', 'hydrojug_customizer');


            console.log('Uploading metallic element to Cloudinary...');
            
            setUploadProgress(35);
            
            // Use standard upload URL
            let uploadUrl = 'https://api.cloudinary.com/v1_1/dgepygozk/image/upload';
            
            const uploadRes = await fetch(uploadUrl, {
              method: 'POST',
              body: formData,
            });
            
            setUploadProgress(60);
            
            if (!uploadRes.ok) {
              const txt = await uploadRes.text();
              throw new Error(`Cloudinary upload failed: ${txt}`);
            }
            
            const uploaded = await uploadRes.json();
            console.log('Element upload successful:', uploaded);
            
            setUploadProgress(70);
            
            // Upload to Google Drive (custom elements don't go through moderation, so upload directly)
            // Zero popups - uses backend service account
            // Extract filename from Cloudinary response (public_id + format)
            const cloudinaryFileName = uploaded.public_id 
              ? `${uploaded.public_id}.${uploaded.format || (fileName.includes('.svg') ? 'svg' : 'png')}` 
              : fileName; // Fallback to the fileName we set earlier
            
            setUploadProgress(75);
            
            const driveUploadResult = await uploadToGoogleDrive(uploaded.secure_url, {
              type: customElement.type === 'image' && (jerseyNumber || jerseyText) ? 'jersey' : 'custom-text',
              fileName: cloudinaryFileName, // Use Cloudinary filename
              productId: selectedProduct?.id,
              colorId: selectedColor?.id,
              designId: customElement.isPresetDesign ? selectedPresetDesign?.sku || selectedPresetDesign?.title : 'custom',
              timestamp: new Date().toISOString()
            });
            
            // Check if Google Drive upload failed
            if (!driveUploadResult.success) {
              console.error('Google Drive backup failed:', driveUploadResult.error);
              setAddingToCart(false);
              setShowLoadingModal(false);
              alert('There was an error uploading your image. Please refresh the page and try again.\n\nIf the problem persists, please contact us at service@thehydrojug.com');
              return;
            }
            
            setUploadProgress(85);
            
            // Use the secure_url directly - rotation should be baked into the image if needed
            securePreviewUrl = uploaded.secure_url;
          }
        }
      }
      
      if (!securePreviewUrl) {
        throw new Error('Failed to generate preview URL');
      }
      
      console.log('Secure Preview URL:', securePreviewUrl);

      // Determine etch type and design info
      const isPresetDesign = !!selectedPresetDesign;
      const isJerseyDesign = !!(jerseyNumber || jerseyText);
      const isCustomUpload = !!processedBlob;
      const isCustomText = !!customText;
      
      // Determine etchType dynamically
      let etchType;
      let designType;
      if (isPresetDesign) {
        etchType = 'design';
      } else {
        etchType = 'custom-upload';
      }
      if (isCustomUpload) {
        designType = 'custom-upload';
      } else if (isJerseyDesign) {
        designType = 'jersey';
      } else if (isCustomText) {
        designType = 'custom-text';
      } else {
        designType = 'design';
      }
      
      const designId = isPresetDesign ? (selectedPresetDesign.sku || selectedPresetDesign.title || selectedPresetDesign.name || 'unknown') : 'custom';
      // Dual-SVG: securePreviewUrl = display SVG (correct appearance for the cart
      // Etch Preview), laserSvgUrl = laser-compatible split-path SVG for the
      // laser software. Falls back to the preview URL when no laser SVG exists
      // (e.g. preset designs).
      const vectorizedUrl = laserSvgUrl || securePreviewUrl;

      // Get rotation if preset design
      const rotation = isPresetDesign && customElements.length > 0 ? (customElements[0].rotation || 0) : undefined;

      // Build etch info object
      const etchInfo = {
        etchType: etchType,
        designId: designId,
        previewUrl: securePreviewUrl,
        vectorizedUrl: vectorizedUrl, // Laser-compatible SVG (split paths)
        designType: designType
      };

      // Add rotation only if it exists and it's a preset design
      if (rotation !== undefined && isPresetDesign && rotation !== 0) {
        etchInfo.rotation = rotation;
      }
      if (originalUploadedUrl) {
        etchInfo.originalUrl = originalUploadedUrl;
      }

      console.log('Etch Info:', etchInfo);
      if (laserSvgUrl) {
        console.log('📋 Using dual SVG approach:');
        console.log('   - Etch Preview (display):', securePreviewUrl);
        console.log('   - vectorizedUrl (laser):', laserSvgUrl);
      }

      setUploadProgress(90);

      // Add to Shopify cart with SVG previewUrl
      const variantId = selectedColor.variants?.[0]?.id;
      if (!variantId) throw new Error('No variant available to add to cart');

      // Fetch current cart state for debugging B2B minimum issues
      try {
        const currentCartRes = await fetch('/cart.js');
        const currentCart = await currentCartRes.json();
        console.log('🛒 Current cart state BEFORE add:', {
          itemCount: currentCart.item_count,
          totalPrice: currentCart.total_price / 100, // Convert cents to dollars
          currency: currentCart.currency,
          items: currentCart.items?.map(item => ({
            title: item.title,
            quantity: item.quantity,
            price: item.price / 100,
            linePrice: item.line_price / 100
          })),
          // B2B specific fields if present
          buyerIdentity: currentCart.buyer_identity,
          attributes: currentCart.attributes
        });
      } catch (cartFetchErr) {
        console.warn('Could not fetch current cart state:', cartFetchErr);
      }

      // Log cart add request details for debugging
      const unitPrice = selectedColor.variants?.[0]?.price;
      const lineTotal = unitPrice ? (parseFloat(unitPrice) * quantity).toFixed(2) : 'N/A';
      console.log('🛒 Attempting to add to cart:', {
        variantId,
        quantity,
        unitPrice,
        lineTotal,
        productTitle: selectedColor?.title,
        previewUrl: securePreviewUrl?.substring(0, 100) + '...',
        note: `This should add $${lineTotal} to cart total`
      });

      // Get the cart drawer element to determine which sections to request
      const cartDrawer = document.querySelector('cart-drawer');
      const cartNotification = document.querySelector('cart-notification');
      const cart = cartNotification || cartDrawer;
      
      // Build the sections to request (matches theme's product-form.js pattern)
      const sectionsToRender = cart ? cart.getSectionsToRender().map((section) => section.id) : [];
      
      const cartRes = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          items: [
            {
              id: variantId,
              quantity: quantity,
              properties: {
                "Etch Preview": securePreviewUrl,
                'etchInfo': etchInfo,
                'Notice': 'Final Sale'
              }
            }
          ],
          // Include sections to get updated HTML in response (like theme's product-form.js)
          sections: sectionsToRender,
          sections_url: window.location.pathname
        })
      });

      if (!cartRes.ok) {
        const errorData = await cartRes.json().catch(() => null);
        let errorMessage = 'There was an issue adding to cart.';
        
        // Log full error details for debugging
        console.error('🛒 Cart Add Error:', {
          status: cartRes.status,
          statusText: cartRes.statusText,
          errorData: errorData,
          variantId: variantId,
          quantity: quantity,
          selectedColor: selectedColor?.title,
          selectedProduct: selectedProduct?.title,
          price: selectedColor?.variants?.[0]?.price,
          calculatedTotal: selectedColor?.variants?.[0]?.price ? (parseFloat(selectedColor.variants[0].price) * quantity).toFixed(2) : 'N/A'
        });
        
        // Extract error message from Shopify response
        if (errorData) {
          console.error('🛒 Shopify Error Response (full object):', errorData);
          console.error('🛒 Shopify Error Response (stringified):', JSON.stringify(errorData, null, 2));
          
          // Check various error formats Shopify might return
          if (errorData.description) {
            errorMessage = errorData.description;
          } else if (errorData.message) {
            errorMessage = errorData.message;
          } else if (errorData.errors) {
            // Handle array of errors
            if (Array.isArray(errorData.errors)) {
              errorMessage = errorData.errors.join('. ');
            } else if (typeof errorData.errors === 'object') {
              errorMessage = Object.values(errorData.errors).flat().join('. ');
            } else {
              errorMessage = errorData.errors;
            }
          } else if (typeof errorData === 'string') {
            errorMessage = errorData;
          }
          
          // Log any B2B specific fields
          if (errorData.buyer_identity || errorData.company || errorData.minimum) {
            console.error('🛒 B2B Related Fields:', {
              buyer_identity: errorData.buyer_identity,
              company: errorData.company,
              minimum: errorData.minimum
            });
          }
        }
        
        console.error('🛒 Final error message shown to user:', errorMessage);
        
        // Fetch cart state after failed add to see current state
        try {
          const postErrorCartRes = await fetch('/cart.js');
          const postErrorCart = await postErrorCartRes.json();
          console.error('🛒 Cart state AFTER failed add:', {
            itemCount: postErrorCart.item_count,
            totalPrice: postErrorCart.total_price / 100,
            buyerIdentity: postErrorCart.buyer_identity
          });
        } catch (e) {
          console.warn('Could not fetch cart after error:', e);
        }
        
        setCartError(errorMessage);
        setAddingToCart(false);
        setShowLoadingModal(false);
        return;
      }

      // Parse the response (it includes sections HTML when we requested them)
      const response = await cartRes.json();
      
      // Remove is-empty class from cart if it exists (like theme's product-form.js line 97)
      if (cart && cart.classList.contains('is-empty')) {
        cart.classList.remove('is-empty');
      }
      
      // Use the theme's native renderContents method with the response
      // This handles updating the drawer HTML and opening it properly
      if (cart && typeof cart.renderContents === 'function') {
        cart.renderContents(response);
      } else {
        // Fallback: redirect to cart page if no cart drawer
        window.location = window.routes?.cart_url || '/cart';
      }
      
      console.log('✅ Item added to cart successfully');

      setAddingToCart(false);
      setShowLoadingModal(false);
    } catch (err) {
      console.error('Add to cart error:', err);
      setAddingToCart(false);
      setShowLoadingModal(false);
      setCartError(err.message || 'There was an issue adding to cart. Please try again.');
    }
  };

  const addTextElement = () => {
    if (!customText.trim()) return;
    
    // Clear any existing designs when adding new text
    setCustomElements([]);
    setSelectedPresetDesign(null);
    // Clear logo states
    setLogoProcessingState('idle');
    setProcessedLogoUrl(null);
    setOriginalLogoUrl(null);
    setProcessedBlob(null);
    processedLogoCanvasRef.current = null;
    originalLogoImageRef.current = null;
    originalLogoDimensionsRef.current = null;
    presetDesignCanvasRef.current = null;
    // Clear jersey states
    setJerseyNumber('');
    setJerseyText('');
    setJerseyTextPosition('below');
    
    // Center the text on the canvas (dynamic size)
    const canvasWidth = window.innerWidth <= 768 ? 400 : 600; // Use mobile size if on mobile
    const canvasHeight = window.innerWidth <= 768 ? 400 : 600; // Use mobile size if on mobile
    
    // Create metallic text effect
    createMetallicText(customText, selectedFont, canvasWidth, canvasHeight);
    
    // Close drawer but keep text for easy editing
    setIsDrawerOpen(false);
  };


  // Function to create metallic text effect
  const createMetallicText = (text, fontFamily, canvasWidth, canvasHeight) => {
    try {
      console.log('Creating metallic text:', { text, fontFamily, canvasWidth, canvasHeight });
      
      // Clear all other style states when creating custom text
      setSelectedPresetDesign(null);
      // Clear logo states
      setLogoProcessingState('idle');
      setProcessedLogoUrl(null);
      setOriginalLogoUrl(null);
      setProcessedBlob(null);
      setOriginalLogoBase64(null);
      processedLogoCanvasRef.current = null;
      // Clear jersey states
      setJerseyNumber('');
      setJerseyText('');
      setJerseyTextPosition('below');
      
      // Create a temporary canvas to measure text properly
      const measureCanvas = document.createElement('canvas');
      const measureCtx = measureCanvas.getContext('2d');
      
      // Use a larger base font size for better quality
      const baseFontSize = 48; // Increased from 24px
      const fontWeight = fontFamily === 'Montserrat' ? '600' : '400';
      measureCtx.font = `${fontWeight} ${baseFontSize}px ${fontFamily}`;
      measureCtx.textAlign = 'center';
      measureCtx.textBaseline = 'middle';
      
      // Measure text to get its actual bounds
      const textMetrics = measureCtx.measureText(text);
      const textWidth = textMetrics.width;
      const textHeight = textMetrics.actualBoundingBoxAscent + textMetrics.actualBoundingBoxDescent;
      
      console.log('Text metrics:', { textWidth, textHeight, fontFamily });
      
      // Create a new canvas just for the text
      const textCanvas = document.createElement('canvas');
      const textCtx = textCanvas.getContext('2d');
      
      // Use ultra-high resolution for crisp text (16x scale for ultra-smooth curves)
      const scale = 16;
      const padding = 60; // Increased padding for better spacing
      const baseWidth = textWidth + padding;
      const baseHeight = textHeight + padding;
      
      // Ensure minimum size for very short text
      const finalWidth = Math.max(baseWidth, 120);
      const finalHeight = Math.max(baseHeight, 80);
      
      // Set canvas size with ultra-high resolution
      textCanvas.width = finalWidth * scale;
      textCanvas.height = finalHeight * scale;
      
      // Scale the context for ultra-high resolution
      textCtx.scale(scale, scale);
      
      // Advanced quality settings for ultra-smooth rendering
      textCtx.imageSmoothingEnabled = true;
      textCtx.imageSmoothingQuality = 'high';
      
      // Additional canvas optimizations for smoothest possible curves
      textCtx.textRenderingOptimization = 'optimizeQuality';
      textCtx.antialias = true;
      
      // Set font rendering hints for maximum quality
      if (textCtx.fontKerning) textCtx.fontKerning = 'normal';
      if (textCtx.fontStretch) textCtx.fontStretch = 'normal';
      if (textCtx.fontVariantCaps) textCtx.fontVariantCaps = 'normal';
      if (textCtx.textRendering) textCtx.textRendering = 'optimizeQuality';
      
      console.log('Text canvas size:', { 
        baseWidth: finalWidth, 
        baseHeight: finalHeight, 
        scaledWidth: textCanvas.width, 
        scaledHeight: textCanvas.height 
      });
      
      // Enable high-quality text rendering
      textCtx.imageSmoothingEnabled = false;
      textCtx.textRenderingOptimization = 'optimizeQuality';
      
      // Draw text in black for processing with larger font size
      textCtx.font = `${fontWeight} ${baseFontSize}px ${fontFamily}`;
      textCtx.textAlign = 'center';
      textCtx.textBaseline = 'middle';
      textCtx.fillStyle = 'black';
      textCtx.fillText(text, finalWidth / 2, finalHeight / 2);
      
      // Get image data for processing
      const imageData = textCtx.getImageData(0, 0, textCanvas.width, textCanvas.height);
      const data = imageData.data;
      
      console.log('Processing image data, pixels:', data.length / 4);
      
      // Apply metallic effect to text pixels with better detection
      for (let i = 0; i < data.length; i += 4) {
        const r = data[i];
        const g = data[i + 1];
        const b = data[i + 2];
        const a = data[i + 3];
        
        // Ultra-inclusive text detection for maximum smoothness - captures all anti-aliased pixels
        // This preserves even the lightest anti-aliased edges for ultra-smooth curves
        const isTextPixel = a > 20 && (r + g + b) < 250; // Very inclusive for ultra-smooth edges
        
        if (isTextPixel) {
          // Calculate position for metallic effect
          const x = (i / 4) % textCanvas.width / textCanvas.width;
          const y = Math.floor((i / 4) / textCanvas.width) / textCanvas.height;
          
          // Check if selected jug has bronze-etch tag
          const isBronzeEtchValue = isBronzeEtch();
          
          // Create metallic effect - bronze or silver based on jug tag (centered gradient like Jersey)
          const distanceFromCenter = Math.abs(x - 0.5) * 2; // 0 at center, 1 at edges
          const t = (1 - distanceFromCenter) * 0.8 + 0.1; // Light in center, darker at edges
          
          let metallicR, metallicG, metallicB;
          if (isBronzeEtchValue) {
            // Bronze metallic effect - balanced bronze-gold with even lighter base
            const bronzeBase = { r: 155, g: 120, b: 85 }; // Even lighter bronze-gold base
            const bronzeHighlight = { r: 230, g: 190, b: 130 }; // Balanced bronze-gold highlight
            metallicR = Math.round(bronzeBase.r * (1 - t) + bronzeHighlight.r * t);
            metallicG = Math.round(bronzeBase.g * (1 - t) + bronzeHighlight.g * t);
            metallicB = Math.round(bronzeBase.b * (1 - t) + bronzeHighlight.b * t);
          } else {
            // Silver metallic effect (original)
            metallicR = Math.round(130 * (1 - t) + 248 * t);
            metallicG = Math.round(135 * (1 - t) + 249 * t);
            metallicB = Math.round(140 * (1 - t) + 252 * t);
          }
          
          data[i] = metallicR;
          data[i + 1] = metallicG;
          data[i + 2] = metallicB;
          data[i + 3] = 255; // Keep fully opaque
        } else {
          // Make background transparent
          data[i + 3] = 0;
        }
      }
      
      // Add subtle noise for brushed metal effect
      const noiseAmount = 1; // Reduced noise for text
      for (let i = 0; i < data.length; i += 4) {
        if (data[i + 3] === 255) {
          const n = (Math.random() - 0.5) * noiseAmount;
          data[i] = Math.max(0, Math.min(255, data[i] + n));
          data[i + 1] = Math.max(0, Math.min(255, data[i + 1] + n));
          data[i + 2] = Math.max(0, Math.min(255, data[i + 2] + n));
        }
      }
      
      // Put the processed image data back
      textCtx.putImageData(imageData, 0, 0);
      
      // Apply subtle smoothing filter for ultra-smooth curves
      textCtx.filter = 'blur(0.5px)';
      textCtx.globalCompositeOperation = 'source-over';
      textCtx.drawImage(textCanvas, 0, 0);
      textCtx.filter = 'none'; // Reset filter
      
      console.log('Metallic effect applied with smoothing, converting to blob...');
      
      // Create a display-optimized version for UI (4x scale for perfect balance)
      const displayCanvas = document.createElement('canvas');
      const displayCtx = displayCanvas.getContext('2d');
      const displayScale = 4; // Optimal for UI display
      
      displayCanvas.width = finalWidth * displayScale;
      displayCanvas.height = finalHeight * displayScale;
      
      // Enable high-quality scaling for display version
      displayCtx.imageSmoothingEnabled = true;
      displayCtx.imageSmoothingQuality = 'high';
      
      // Draw the high-res version scaled down with high quality
      displayCtx.drawImage(textCanvas, 0, 0, displayCanvas.width, displayCanvas.height);
      
      // Convert display version to blob
      displayCanvas.toBlob((displayBlob) => {
        if (!displayBlob) {
          console.error('Failed to create display blob from canvas');
          return;
        }
        
        const displayUrl = URL.createObjectURL(displayBlob);
        
        // Convert high-res version to blob for Cloudinary
      textCanvas.toBlob((blob) => {
        if (!blob) {
          console.error('Failed to create blob from canvas');
          return;
        }
        
        console.log('High-res blob created:', blob.size, 'bytes');
        const highResUrl = URL.createObjectURL(blob);
        console.log('Display URL created:', displayUrl);
        console.log('High-res URL created for Cloudinary:', highResUrl);
        
        // Get dynamic styles based on selected product and text length
        const productType = selectedProduct?.collectionHandle || 'traveler-40oz';
        const styles = getCustomElementStyles(productType, 'text', text.length);

        // Preserve the user's current rotation across color-change regenerations
        // (read the live value via the ref, not the possibly-stale closure).
        const priorRotation = customElementsRef.current[0]?.rotation || 0;

        // Create the text element with the display-optimized image for UI
        const newElement = {
          type: 'image', // Treat as image since it's now a processed image
          designType: 'custom-text',
          imageUrl: displayUrl, // Use display-optimized version for UI
          highResUrl: highResUrl, // Store high-res version for Cloudinary
          width: styles.width,
          height: styles.height,
          top: styles.top,
          left: styles.left,
          right: styles.right,
          margin: styles.margin,
          rotation: priorRotation,
          isPresetDesign: false,
          textLength: text.length
        };
        
        console.log('New metallic text element created:', newElement);
        
        // Replace all existing elements with the new metallic text element
        setCustomElements([newElement]);
        console.log("Added metallic text element:", newElement);
        }, 'image/png', 1.0); // Maximum quality for high-res version
      }, 'image/png', 1.0); // Maximum quality for display version
      
    } catch (error) {
      console.error("Error creating metallic text:", error);
      
      // Fallback to regular text if metallic processing fails
      const newElement = {
        type: 'text',
        text: text,
        x: canvasWidth / 2,
        y: canvasHeight / 2,
        fontSize: 24,
        fontFamily: fontFamily,
        fill: '#808080', // Fallback to gray color
        align: 'center',
        verticalAlign: 'middle',
        rotation: 0
      };
      
      setCustomElements([newElement]);
      console.log("Added fallback text element:", newElement);
    }
  };

  // Helper: read an image's intrinsic aspect ratio (naturalW/naturalH),
  // defaulting to 1 (square) if the image can't be loaded.
  const getImageAspectRatio = (imageUrl) => {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => {
        const ar = img.naturalWidth / img.naturalHeight;
        resolve(ar || 1);
      };
      img.onerror = () => {
        console.warn("⚠️ Could not load image for aspect ratio, defaulting to 1");
        resolve(1);
      };
      img.src = imageUrl;
    });
  };

  // Helper: build an image custom-element carrying aspect-ratio + base-style
  // metadata so rotation (90°/270°) can swap display dimensions correctly.
  const createImageElementWithAspectRatio = async (imageUrl, options = {}) => {
    const { isPresetDesign = false, rotation = 0, textLength = 0 } = options;

    const productType = selectedProduct?.collectionHandle || "traveler-40oz";
    const baseStyles = getCustomElementStyles(productType, "design");
    const imageAspectRatio = await getImageAspectRatio(imageUrl);

    const originalWidth = baseStyles.width;
    const originalHeight = baseStyles.height;

    const isRotated90or270 = rotation === 90 || rotation === 270;
    const displayWidth = isRotated90or270 ? originalHeight : originalWidth;
    const displayHeight = isRotated90or270 ? originalWidth : originalHeight;

    return {
      type: "image",
      imageUrl: imageUrl,
      width: displayWidth,
      height: displayHeight,
      top: baseStyles.top,
      left: baseStyles.left,
      right: baseStyles.right,
      margin: baseStyles.margin,
      rotation: rotation,
      isPresetDesign: isPresetDesign,
      textLength: textLength,
      imageAspectRatio: imageAspectRatio,
      baseStyles: baseStyles,
      originalWidth: originalWidth,
      originalHeight: originalHeight,
      productType: productType,
    };
  };

  // Helper: for 90°/270° rotations, swap width/height so the image fills the
  // etch area correctly; 0°/180° keep original dimensions.
  const calculateRotatedDisplaySize = (
    baseStyles,
    imageAspectRatio,
    rotation,
    currentWidth,
    currentHeight,
  ) => {
    const originalWidth = currentWidth || baseStyles.width;
    const originalHeight = currentHeight || baseStyles.height;
    const isRotated90or270 = rotation === 90 || rotation === 270;
    if (isRotated90or270) {
      return { width: originalHeight, height: originalWidth };
    }
    return { width: originalWidth, height: originalHeight };
  };

  // True when the currently-selected color (or its parent product) carries the
  // "new-photos" tag — those SKUs use an updated bottle photo set whose
  // etch-area framing differs from the legacy photos.
  const hasNewPhotosTag = () => {
    const tags = selectedColor?.tags || selectedProduct?.tags || [];
    return Array.isArray(tags) && tags.includes("new-photos");
  };

  // Function to get dynamic styles based on product type
  const getCustomElementStyles = (productType, elementType, textLength = 0) => {
    console.log("type", productType);
    console.log("elementType", elementType);
    const styles = {
      width: '150px',
      height: 'auto',
      top: '15%',
      margin: '0 auto',
      left: 0,
      right: 0,
    };

    // Products tagged "new-photos" use an updated bottle photo set whose
    // etch-area framing differs. Legacy (else) values are kept exactly as the
    // wholesale theme had them to avoid regressing current product photos;
    // newPhotos + ease2o values are ported from the retail customizer.
    const newPhotos = hasNewPhotosTag();

    switch (productType) {
      case 'traveler-40oz':
        if (newPhotos) {
          styles.width = "23.3%";
          styles.height = "34.1%";
          styles.top = "20%";
        } else {
          styles.width = "33%";
          styles.height = "35%";
          styles.top = '15%';
        }
        break;
      case 'traveler-32oz':
        if (newPhotos) {
          styles.width = "19.3%";
          styles.height = "29.5%";
          styles.top = "27%";
        } else {
          styles.width = "27%";
          styles.height = "39.5%";
          styles.top = '20%';
        }
        break;
      case 'traveler-20oz':
        if (newPhotos) {
          styles.width = "17.9%";
          styles.height = "23.7%";
          styles.top = "36%";
        } else {
          styles.width = "22%";
          styles.height = "27%";
          styles.top = '35%';
        }
        break;
      case 'coffee-travelers':
        if (newPhotos) {
          styles.width = "17.9%";
          styles.height = "23.7%";
          styles.top = "36%";
        } else {
          styles.width = "22%";
          styles.height = "27%";
          styles.top = '35%';
        }
        break;
      case 'sport-40oz':
        if (newPhotos) {
          styles.width = "20.4%";
          styles.height = "29.9%";
          styles.top = "25%";
        } else {
          styles.width = '27%';
          styles.height = '38.5%';
          styles.top = '17%';
          styles.left = '-11px';
        }
        break;
      case 'sport-32oz':
        if (newPhotos) {
          styles.width = "18.8%";
          styles.height = "28.2%";
          styles.top = "26%";
        } else {
          styles.width = '21%';
          styles.height = '35.2%';
          styles.top = '23%';
          styles.left = '-11px';
        }
        break;
      case 'sport-20oz':
        if (newPhotos) {
          styles.width = "14.9%";
          styles.height = "22.9%";
          styles.top = "31%";
        } else {
          styles.width = '19%';
          styles.height = '29.3%';
          styles.top = '34%';
          styles.left = '-11px';
        }
        break;
      case 'sport-14oz':
        if (newPhotos) {
          styles.width = "17.8%";
          styles.height = "26.2%";
          styles.top = "31%";
        } else {
          styles.width = '24%';
          styles.height = '28%';
          styles.top = '41%';
          styles.left = '-4px';
        }
        break;
      case 'stainless-64oz':
        styles.width = '33.5%';
        styles.height = '47%';
        styles.top = '42.5%';
        break;
      case 'can-coolers-slim':
        if (newPhotos) {
          styles.width = "17.8%";
          styles.height = "25.7%";
          styles.top = "41%";
        } else {
          styles.width = '28%';
          styles.height = '33%';
          styles.top = '44%';
        }
        break;
      case 'can-coolers-standard':
        if (newPhotos) {
          styles.width = "21%";
          styles.height = "31%";
          styles.top = "43%";
        } else {
          styles.width = '28%';
          // styles.height = '36%';
          styles.top = '51%';
        }
        break;
      case 'can-cooler-16oz':
        styles.width = '29%';
        styles.height = '34%';
        styles.top = '41%';
        break;
      case 'everyday-tumbler-14oz':
        styles.width = '25%';
        styles.height = '24%';
        styles.top = '50%';
        break;
      case 'everyday-tumbler-20oz':
        if (newPhotos) {
          styles.width = "16.3%";
          styles.height = "24.5%";
          styles.top = "40%";
        } else {
          styles.width = '27%';
          styles.height = '26.5%';
          styles.top = '40%';
        }
        break;
      case 'insulated-shaker':
        if (newPhotos) {
          styles.width = "16.6%";
          styles.height = "24.2%";
          styles.top = "39%";
        } else {
          styles.width = '19%';
          styles.height = '29%';
          styles.top = '42%';
        }
        break;
      case 'ease2o-40oz':
        styles.width = "23%";
        styles.height = "36%";
        styles.top = "25%";
        break;
      case 'ease2o-32oz':
        if (newPhotos) {
          styles.width = "27%";
          styles.height = "25%";
          styles.top = "31%";
        } else {
          styles.width = "21%";
          styles.height = "34%";
          styles.top = "28%";
        }
        break;
      case 'ease2o-24oz':
        if (newPhotos) {
          styles.width = "15%";
          styles.height = "22%";
          styles.top = "38%";
        } else {
          styles.width = "19%";
          styles.height = "30%";
          styles.top = "36%";
        }
        break;
      case 'ease2o-14oz':
        if (newPhotos) {
          styles.width = "14%";
          styles.height = "20%";
          styles.top = "48%";
        } else {
          styles.width = "21%";
          styles.height = "42%";
          styles.top = "46%";
        }
        break;
      default:
        break;
    }

    return styles;
  };

  // Create SVG version of jersey design with curved text support
  const createSVGJersey = async (number, text, textPosition, width, height, isCurved = false, forCloudinary = false) => {
    // Create temporary DOM element to measure actual text dimensions
    const tempDiv = document.createElement('div');
    tempDiv.style.position = 'absolute';
    tempDiv.style.visibility = 'hidden';
    tempDiv.style.fontFamily = 'Jersey M54, Arial, sans-serif';
    tempDiv.style.fontSize = '220px'; // Increased from 180px for larger display
    tempDiv.style.fontWeight = 'bold';
    tempDiv.textContent = number;
    document.body.appendChild(tempDiv);
    
    const actualNumberHeight = tempDiv.offsetHeight;
    document.body.removeChild(tempDiv);
    
    // Use actual measured height for centering
    // Need larger offset to align the visual center of the number with element center
    const numberY = (height / 2) + (actualNumberHeight * 0.225); // Larger offset for proper visual centering
    
    // Calculate dynamic text font size based on text length
    const textLength = text.length;
    let textFontSize;
    if (textLength <= 4) {
      textFontSize = 78;
    } else if (textLength <= 8) {
      textFontSize = 70;
    } else if (textLength <= 12) {
      textFontSize = 50;
    } else {
      textFontSize = 40;
    }

    // Calculate relative text height based on font size as percentage of canvas
    let textHeightPercent;
    if (textLength <= 4) {
      textHeightPercent = 0.25; // 90px text = ~25% of canvas height
    } else if (textLength <= 8) {
      textHeightPercent = 0.20; // 70px text = ~20% of canvas height
    } else if (textLength <= 12) {
      textHeightPercent = 0.14; // 50px text = ~14% of canvas height
    } else {
      textHeightPercent = 0.11; // 40px text = ~11% of canvas height
    }
    const textRelativeHeight = height * textHeightPercent;
    
    // Calculate positioning using relative measurements and percentage-based gap
    const gapPercent = 0.06; // Reduced gap to prevent text cutoff
    const gap = height * gapPercent;
    
    // Measure actual text height for positioning
    const tempTextDiv = document.createElement('div');
    tempTextDiv.style.position = 'absolute';
    tempTextDiv.style.visibility = 'hidden';
    tempTextDiv.style.fontFamily = 'Jersey M54, Arial, sans-serif';
    tempTextDiv.style.fontSize = `${textFontSize}px`;
    tempTextDiv.style.fontWeight = 'bold';
    tempTextDiv.textContent = text;
    document.body.appendChild(tempTextDiv);
    
    const actualTextHeight = tempTextDiv.offsetHeight;
    document.body.removeChild(tempTextDiv);
    
    // Scale down the measured heights to match SVG rendering
    // DOM measurements are larger than SVG text, so we need to scale them down
    const svgScaleFactor = 0.5; // Increased scale factor for better spacing calculations
    const svgNumberHeight = actualNumberHeight * svgScaleFactor;
    const svgTextHeight = actualTextHeight * svgScaleFactor;

    // Use the actual measured text height from our DOM measurement
    const actualTextHeightScaled = actualTextHeight * svgScaleFactor;
    
    // Define number dimensions using scaled height
    const numberTop = numberY - (svgNumberHeight / 2);
    const numberBottom = numberY + (svgNumberHeight / 2);
    
    
    // Use font-size compensated positioning to maintain consistent visual spacing
    // Compensate for the fact that smaller fonts need different positioning to appear consistent
    const baseFontSize = 90; // Reference font size
    const fontSizeRatio = textFontSize / baseFontSize; // How much smaller/larger than base
    
    // Reserve a top padding equal to the text font size's ascender so short
    // text (≤4 chars, larger font) never gets clipped by the SVG bbox.
    const minTopPadding = textFontSize * 0.6;
    const rawTextY = textPosition === 'above' ?
      numberY - (height * 0.525) : // Above: fixed position
      numberY + (height * 0.125); // Below: compensate for font size
    const textY = textPosition === 'above' ? Math.max(rawTextY, minTopPadding) : rawTextY;
    const curveAmount = width * 0.10; // 10% curve
    
    // Calculate control point based on the actual curved line position
    const curvedLineY = textPosition === 'above' ? 
      textY + (actualTextHeightScaled * 0.2): // Above: use textY as is
      textY + (actualTextHeightScaled * 0.55); // Below: same compensation as straight text
    
    const controlY = textPosition === 'above' ? curvedLineY - curveAmount : curvedLineY + curveAmount;
    
    // Create curved path for text
    const startX = width * 0.05;
    const endX = width * 0.95;
    const pathId = 'jerseyCurve';
    
    // Generate curved path using quadratic Bézier curve
    // For straight lines, use the actual measured font metrics to position text consistently
    // The key insight: SVG textPath ignores baseline attributes, so we must position the path precisely
    
 
    
    // For "below", position the path so the text top is always at the same visual position
    // We need to account for how SVG positions text on paths (typically centers on the path)
    const straightLineY = textPosition === 'above' ? 
      textY : // Above: use textY as is
      textY + (actualTextHeightScaled * 0.85); // Below: offset by 70% of the actual text height
    
    // Use the same curvedLineY calculated above for the path
    
    const curvePath = isCurved ? 
      `M ${startX},${curvedLineY} Q ${width/2},${controlY} ${endX},${curvedLineY}` : 
      `M ${startX},${straightLineY} L ${endX},${straightLineY}`;
    
    // Try to load the font as a data URI. Build base64 with a chunked loop
    // instead of String.fromCharCode(...bigArray) — the spread form can blow
    // the call stack on large font buffers.
    let fontDataUri = '';
    try {
      const fontResponse = await fetch('https://cdn.shopify.com/s/files/1/0110/7827/1033/files/Jersey_M54.ttf?v=1758053820');
      const fontBuffer = await fontResponse.arrayBuffer();
      const u8 = new Uint8Array(fontBuffer);
      let binary = '';
      for (let i = 0; i < u8.length; i++) binary += String.fromCharCode(u8[i]);
      const fontBase64 = btoa(binary);
      fontDataUri = `data:font/truetype;base64,${fontBase64}`;
    } catch (error) {
      console.warn('Could not load font as data URI, using fallback:', error);
    }
    
    // Create different SVG versions for UI display vs Cloudinary upload
    if (forCloudinary) {
      // Black version with font for Cloudinary processing
      return `
        <svg width="${width}" height="${height}" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink">
          <defs>
            ${fontDataUri ? `
            <style>
              @font-face {
                font-family: 'Jersey M54';
                src: url('${fontDataUri}') format('truetype');
              }
            </style>
            ` : ''}
            <path id="${pathId}" d="${curvePath}" fill="transparent" />
          </defs>
          <text x="${width / 2}" y="${numberY}" font-family="Jersey M54, Arial, sans-serif" font-size="220" font-weight="bold" text-anchor="middle" text-baseline="middle" fill="black">${number}</text>
          <text font-family="Jersey M54, Arial, sans-serif" font-size="${textFontSize}" font-weight="bold" alignment-baseline="top" text-baseline="top" fill="black">
            <textPath xlink:href="#${pathId}" startOffset="50%" text-anchor="middle" text-baseline="top" alignment-baseline="top">${text}</textPath>
          </text>
        </svg>
      `;
    } else {
      // Full metallic version for UI display
      return `
        <svg width="${width}" height="${height}" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink">
          <defs>
            ${fontDataUri ? `
            <style>
              @font-face {
                font-family: 'Jersey M54';
                src: url('${fontDataUri}') format('truetype');
              }
            </style>
            ` : ''}
            <linearGradient id="jerseyGradient" x1="0%" y1="0%" x2="100%" y2="0%">
              ${(() => {
                // Check if selected jug has bronze-etch tag
                const isBronzeEtchValue = isBronzeEtch();
                
                if (isBronzeEtchValue) {
                  // Bronze gradient - balanced bronze-gold with even lighter ends
                  return `
                    <stop offset="0%" stop-color="#9B7855" />
                    <stop offset="25%" stop-color="#A6824E" />
                    <stop offset="50%" stop-color="#E6BE82" />
                    <stop offset="75%" stop-color="#A6824E" />
                    <stop offset="100%" stop-color="#9B7855" />
                  `;
                } else {
                  // Silver gradient (original)
                  return `
                    <stop offset="0%" stop-color="#999999" />
                    <stop offset="25%" stop-color="#cccccc" />
                    <stop offset="50%" stop-color="#ffffff" />
                    <stop offset="75%" stop-color="#cccccc" />
                    <stop offset="100%" stop-color="#999999" />
                  `;
                }
              })()}
            </linearGradient>
            <path id="${pathId}" d="${curvePath}" fill="transparent" />
          </defs>
          <text x="${width / 2}" y="${numberY}" font-family="Jersey M54, Arial, sans-serif" font-size="220" font-weight="bold" text-anchor="middle" text-baseline="middle" fill="url(#jerseyGradient)">${number}</text>
          <text class="jersey-text" font-family="Jersey M54, Arial, sans-serif" font-size="${textFontSize}" font-weight="bold" alignment-baseline="top" text-baseline="top" fill="url(#jerseyGradient)" style="line-height: 0;">
            <textPath xlink:href="#${pathId}" startOffset="50%" text-anchor="middle" text-baseline="top" alignment-baseline="top" style="line-height: 0;">${text}</textPath>
          </text>
        </svg>
      `;
    }
  };

  // Function to convert textPath to actual SVG path outlines for laser compatibility
  const convertTextPathToSVGPaths = async (text, curvePath, fontSize, fontFamily) => {
    // Create a temporary SVG element to measure text and create paths
    const tempSvg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    tempSvg.style.position = 'absolute';
    tempSvg.style.left = '-9999px';
    tempSvg.style.top = '-9999px';
    document.body.appendChild(tempSvg);
    
    try {
      // Parse the curve path to get control points
      const pathMatch = curvePath.match(/M\s*([\d.]+),([\d.]+)\s*Q\s*([\d.]+),([\d.]+)\s*([\d.]+),([\d.]+)/);
      if (!pathMatch) {
        // Fallback for straight line
        const lineMatch = curvePath.match(/M\s*([\d.]+),([\d.]+)\s*L\s*([\d.]+),([\d.]+)/);
        if (lineMatch) {
          const [, startX, startY, endX, endY] = lineMatch.map(Number);
          const centerX = (startX + endX) / 2;
          return `<text x="${centerX}" y="${startY}" font-family="${fontFamily}, Arial, sans-serif" font-size="${fontSize}" font-weight="bold" text-anchor="middle" fill="black">${text}</text>`;
        }
        return '';
      }
      
      const [, startX, startY, controlX, controlY, endX, endY] = pathMatch.map(Number);
      
      // Create individual text elements for each character positioned along the curve
      const characters = text.split('');
      let svgElements = '';
      
      // Calculate character positions along the curve
      const totalLength = Math.sqrt(Math.pow(endX - startX, 2) + Math.pow(endY - startY, 2));
      const charSpacing = totalLength / Math.max(characters.length - 1, 1);
      
      characters.forEach((char, index) => {
        if (char.trim() === '') return; // Skip spaces for now
        
        // Calculate position along curve (0 to 1)
        const t = characters.length === 1 ? 0.5 : index / (characters.length - 1);
        
        // Quadratic Bézier curve formula: B(t) = (1-t)²P₀ + 2(1-t)tP₁ + t²P₂
        // X coordinates are linear (straight line horizontally)
        const x = startX + t * (endX - startX);
        // Y coordinates follow the curve
        const y = Math.pow(1 - t, 2) * startY + 2 * (1 - t) * t * controlY + Math.pow(t, 2) * endY;
        
        // Calculate rotation angle for the character
        const dy = 2 * (1 - t) * (controlY - startY) + 2 * t * (endY - controlY);
        const dx = endX - startX;
        const angle = Math.atan2(dy, dx) * (180 / Math.PI);
        
        // Create individual text element for this character
        svgElements += `<text x="${x}" y="${y}" font-family="${fontFamily}, Arial, sans-serif" font-size="${fontSize}" font-weight="bold" text-anchor="middle" fill="black" transform="rotate(${angle} ${x} ${y})">${char}</text>`;
      });
      
      return svgElements;
      
    } finally {
      // Clean up temporary SVG
      document.body.removeChild(tempSvg);
    }
  };

  // Function to create jersey-style metallic design using SVG textPath
  const createJerseyDesign = async (number, text, textPosition, canvasWidth, canvasHeight) => {
    try {
      console.log('Creating jersey design:', { number, text, textPosition, canvasWidth, canvasHeight });
      
      // Clear all other style states when creating jersey design
      setSelectedPresetDesign(null);
      // Clear text states
      setCustomText('');
      // Clear logo states
      setLogoProcessingState('idle');
      setProcessedLogoUrl(null);
      setOriginalLogoUrl(null);
      setProcessedBlob(null);
      setOriginalLogoBase64(null);
      processedLogoCanvasRef.current = null;
      presetDesignCanvasRef.current = null;
      
      // Get dynamic styles based on selected product and text length first
      const productType = selectedProduct?.collectionHandle || 'traveler-40oz';
      const totalTextLength = (number + text).length;
      const styles = getCustomElementStyles(productType, 'text', totalTextLength);
      
                // Convert percentage dimensions to pixel values for SVG generation
                const baseWidth = 350; // Increased width to reduce whitespace
                const baseHeight = 400;
      
      // Generate SVG with curved text using textPath
      const svgString = await createSVGJersey(number, text, textPosition, baseWidth, baseHeight, jerseyCurveText);
      
      // Convert SVG string to blob
      const svgBlob = new Blob([svgString], { type: 'image/svg+xml' });
      const svgUrl = URL.createObjectURL(svgBlob);

      // Preserve the user's current rotation across color-change regenerations.
      const priorRotation = customElementsRef.current[0]?.rotation || 0;

      // Create the jersey element with the SVG
      const newElement = {
        type: 'image',
        designType: 'jersey',
        imageUrl: svgUrl,
        width: styles.width,
        height: styles.height,
        top: styles.top,
        left: styles.left,
        right: styles.right,
        margin: styles.margin,
        rotation: priorRotation,
        isPresetDesign: false,
        textLength: totalTextLength,
        imageAspectRatio: baseWidth / baseHeight,
      };
      
      console.log('New SVG jersey element created:', newElement);
      console.log('Styles from getCustomElementStyles:', styles);
      console.log('Product type:', productType, 'Text length:', totalTextLength);
      
      // Replace all existing elements with the new SVG jersey element
      setCustomElements([newElement]);
      console.log("Added SVG jersey element:", newElement);
      
    } catch (error) {
      console.error('Error in createJerseyDesign:', error);
    }
  };

  // Auto-update jersey design when inputs change (debounced)
  useEffect(() => {
    // Only auto-update if we have a jersey number and we're on the jersey tab
    if (!jerseyNumber.trim() || activeTab !== 'jersey') {
      return;
    }

    // Clear any existing debounce timeout
    if (jerseyDebounceRef.current) {
      clearTimeout(jerseyDebounceRef.current);
    }

    // Debounce the jersey design creation
    jerseyDebounceRef.current = setTimeout(() => {
      const canvasWidth = window.innerWidth <= 768 ? 400 : 600;
      const canvasHeight = window.innerWidth <= 768 ? 400 : 600;
      createJerseyDesign(jerseyNumber, jerseyText, jerseyTextPosition, canvasWidth, canvasHeight);
    }, 300);

    // Cleanup on unmount or when dependencies change
    return () => {
      if (jerseyDebounceRef.current) {
        clearTimeout(jerseyDebounceRef.current);
      }
    };
  }, [jerseyNumber, jerseyText, jerseyTextPosition, jerseyCurveText, activeTab]);

  // new function for upload
 /**
  * Laser-safe Jersey design generator
  * Directly replicates the UI positioning logic using the same calculations
  */
  async function createJerseyDesignForUpload(
   number,
   name,
   position = "below",
   curved = false
 ) {
   // Use larger dimensions for better quality after Cloudinary processing
   const width = 350, height = 400;
   
  console.log("➡️ Creating Jersey design with TTF font");

  try {
    // Load font from Cloudinary (guarantees TTF format - no auto-conversion)
    const fontUrl = "https://res.cloudinary.com/dgepygozk/raw/upload/v1763674796/Jersey_M54_z4zv8g.ttf";
    
    let font;
    try {
      font = await opentype.load(fontUrl);
      console.log('✅ Font loaded successfully from Cloudinary');
    } catch (fontError) {
      console.error('❌ Error loading font from Cloudinary:', fontError);
      // Fallback to Shopify CDN (may fail in incognito with WOFF2 issue)
      console.log('⚠️ Trying Shopify CDN as fallback...');
      const shopifyFontUrl = "https://cdn.shopify.com/s/files/1/0110/7827/1033/files/Jersey_M54.ttf?v=1758053820";
      font = await opentype.load(shopifyFontUrl);
    }
     
     // === REPLICATE EXACT UI POSITIONING LOGIC FROM createSVGJersey ===
     
     // 1. Number positioning (EXACT same as createSVGJersey)
     const tempDiv = document.createElement('div');
     tempDiv.style.position = 'absolute';
     tempDiv.style.visibility = 'hidden';
     tempDiv.style.fontFamily = 'Jersey M54, Arial, sans-serif';
     tempDiv.style.fontSize = '220px'; // Increased from 180px for larger etch
     tempDiv.style.fontWeight = 'bold';
     tempDiv.textContent = number;
     document.body.appendChild(tempDiv);
     
     const actualNumberHeight = tempDiv.offsetHeight;
     document.body.removeChild(tempDiv);
     
     const numberY = (height / 2) + (actualNumberHeight * 0.225);
     
     // 2. Text measurements (EXACT same as createSVGJersey)
     const tempTextDiv = document.createElement('div');
     tempTextDiv.style.position = 'absolute';
     tempTextDiv.style.visibility = 'hidden';
     tempTextDiv.style.fontFamily = 'Jersey M54, Arial, sans-serif';
     tempTextDiv.style.fontWeight = 'bold';
     tempTextDiv.textContent = name;
     document.body.appendChild(tempTextDiv);
     
     // Dynamic font sizing based on text length (EXACT same as createSVGJersey)
     const textLength = name.length;
     let textFontSize;
     if (textLength <= 4) {
       textFontSize = 90;
       tempTextDiv.style.fontSize = '90px';
     } else if (textLength <= 8) {
       textFontSize = 70;
       tempTextDiv.style.fontSize = '70px';
     } else if (textLength <= 12) {
       textFontSize = 50;
       tempTextDiv.style.fontSize = '50px';
     } else {
       textFontSize = 40;
       tempTextDiv.style.fontSize = '40px';
     }
     
     const actualTextHeight = tempTextDiv.offsetHeight;
     document.body.removeChild(tempTextDiv);
     
     // 3. Text positioning calculations (EXACT same as createSVGJersey)
     let textHeightPercent;
     if (textLength <= 4) {
       textHeightPercent = 0.25;
     } else if (textLength <= 8) {
       textHeightPercent = 0.20;
     } else if (textLength <= 12) {
       textHeightPercent = 0.14;
     } else {
       textHeightPercent = 0.11;
     }
     
     const actualTextHeightScaled = height * textHeightPercent;
     let gapPercent = 0.10; // Increased from 0.08 for larger number
     
     if (position === 'above') {
       gapPercent = 0.35; // Increased from 0.275 for larger number
      } 
      const gap = height * gapPercent;
     
     
     // Calculate textY using the EXACT same logic as UI createSVGJersey
     const textY = position === 'above' ? 
       numberY - (height * 0.55) : // Above: fixed position (same as UI)
       numberY + (height * 0.125); // Below: compensate for font size (same as UI)
     
     // 4. Curve calculations (EXACT same as createSVGJersey)
     const startX = width * 0.1;
     const endX = width * 0.9;
     const curveAmount = width * 0.10;
     
     // Calculate control point based on the actual curved line position (EXACT same as createSVGJersey)
     const curvedLineY = position === 'above' ? 
       textY + (actualTextHeightScaled * 0.2) : // Above: use textY as is
       textY + (actualTextHeightScaled * 0.55); // Below: same compensation as straight text
     
     const controlY = position === 'above' ? curvedLineY - curveAmount : curvedLineY + curveAmount;
     
     // === GENERATE PATHS WITH EXACT UI POSITIONING ===
     
     let paths = '';
     
     // Number path - use exact UI positioning with proper baseline
     const numberPath = font.getPath(number, 0, 0, 220); // Increased from 180px
     const numberBounds = numberPath.getBoundingBox();
     const numberWidth = numberBounds.x2 - numberBounds.x1;
     
     // Center horizontally like SVG text-anchor="middle"
     const numberX = (width / 2) - (numberWidth / 2) - numberBounds.x1;
     // Use baseline positioning like SVG text-baseline="middle" 
     const adjustedNumberY = numberY;  // Use numberY directly as baseline
     
     const centeredNumberPath = font.getPath(number, numberX, adjustedNumberY, 220); // Increased from 180px
     paths += `<path d="${centeredNumberPath.toPathData(2)}" fill="black" stroke="black" stroke-width="3" stroke-linejoin="round"/>`;
     
     // Text paths
     if (name && curved) {
       console.log("Creating curved text with natural character spacing");
       console.log("Curve positioning:", { curvedLineY, controlY, startX, endX });
       
       // === REPLICATE EXACT UI TEXTPATH SPACING ===
       // The UI uses SVG textPath which includes natural font advance widths (not just bounding box)
       // Let's measure the actual text width the same way the browser would
       
       // Create a temporary SVG to measure actual textPath spacing
       const tempSvg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
       tempSvg.style.position = 'absolute';
       tempSvg.style.visibility = 'hidden';
       tempSvg.setAttribute('width', '1000');
       tempSvg.setAttribute('height', '200');
       
       const tempText = document.createElementNS('http://www.w3.org/2000/svg', 'text');
       tempText.setAttribute('font-family', 'Jersey M54, Arial, sans-serif');
       tempText.setAttribute('font-size', textFontSize);
       tempText.setAttribute('font-weight', 'bold');
       tempText.textContent = name;
       
       tempSvg.appendChild(tempText);
       document.body.appendChild(tempSvg);
       
       // Get the actual rendered text width (includes natural font spacing)
       const actualTextWidth = tempText.getBBox().width;
       document.body.removeChild(tempSvg);
       
       console.log(`UI text measurement: "${name}" at ${textFontSize}px = ${actualTextWidth}px width`);
       
       // Hybrid: browser-measured total width as spacing BUDGET (matches
       // what the UI's <textPath> renders end-to-end) + opentype.js kerning
       // tables to distribute the budget PER PAIR (tightens AT / AV / LT / Ty
       // etc. by the font-designer-specified amount).
       const glyphs = font.stringToGlyphs(name);
       const unitScale = textFontSize / font.unitsPerEm;
       const characterWidths = [];
       for (let i = 0; i < glyphs.length; i++) {
         const bbox = glyphs[i].getBoundingBox();
         characterWidths.push((bbox.x2 - bbox.x1) * unitScale);
       }
       const totalCharacterWidth = characterWidths.reduce((sum, w) => sum + w, 0);

       // Pair-by-pair kerning deltas (negative for tight pairs like AT).
       const pairKernings = [];
       let sumKerning = 0;
       for (let i = 0; i < glyphs.length - 1; i++) {
         const k = font.getKerningValue(glyphs[i], glyphs[i + 1]) * unitScale;
         pairKernings.push(k);
         sumKerning += k;
       }
       const avgKerning = glyphs.length > 1 ? sumKerning / (glyphs.length - 1) : 0;

       // Baseline uniform gap (this alone would match total width but ignore
       // per-pair kerning).
       const naturalSpacingRaw =
         glyphs.length > 1
           ? (actualTextWidth - totalCharacterWidth) / (glyphs.length - 1)
           : 0;

       // Compression factor: the straight-line browser bbox reads a little
       // wider than the same text laid along a curve, so tighten the baseline
       // gap. Kerning deltas are applied at full strength on top.
       const CURVE_TIGHTEN = 0.7;
       const naturalSpacing = naturalSpacingRaw * CURVE_TIGHTEN;

       // Per-pair spacing = compressed baseline + full-strength kerning delta.
       const pairSpacings = pairKernings.map((k) => naturalSpacing + (k - avgKerning));
       const totalTextWidthWithSpacing =
         totalCharacterWidth + naturalSpacing * (glyphs.length - 1);

       console.log(`Kerning-hybrid: total ${totalTextWidthWithSpacing.toFixed(1)}px, base gap ${naturalSpacing.toFixed(1)}px, avg kern ${avgKerning.toFixed(2)}px`);

       // Calculate curve length (approximate)
       const curveLength = Math.sqrt(
         Math.pow(endX - startX, 2) +
         Math.pow(controlY - curvedLineY, 2)
       ) * 1.3; // Approximate quadratic Bézier length

       // Center the text on the curve (like startOffset="50%")
       const textStartOffset = (curveLength - totalTextWidthWithSpacing) / 2;

       console.log("Natural spacing:", {
         totalTextWidthWithSpacing,
         curveLength,
         textStartOffset,
         characterWidths
       });

       // Position characters using cumulative widths with proper letter spacing
       let currentOffset = textStartOffset;

       for (let i = 0; i < name.length; i++) {
         const char = name[i];
         const charWidth = characterWidths[i];

         // Position character at its center point
         const charCenterOffset = currentOffset + (charWidth / 2);

         // Convert offset to curve parameter t
         const t = Math.max(0, Math.min(1, charCenterOffset / curveLength));

         // Calculate position on curve
         const x = (1 - t) ** 2 * startX + 2 * (1 - t) * t * (width/2) + t ** 2 * endX;
         const y = (1 - t) ** 2 * curvedLineY + 2 * (1 - t) * t * controlY + t ** 2 * curvedLineY;

         // Calculate tangent angle
         const dx = 2 * (1 - t) * (width/2 - startX) + 2 * t * (endX - width/2);
         const dy = 2 * (1 - t) * (controlY - curvedLineY) + 2 * t * (curvedLineY - controlY);
         const angle = Math.atan2(dy, dx) * (180 / Math.PI);

         // Position character centered on its position (like text-anchor="middle")
         const charX = -(charWidth / 2) - font.getPath(char, 0, 0, textFontSize).getBoundingBox().x1;
         const charY = 0;  // Use baseline like textPath

         const centeredCharPath = font.getPath(char, charX, charY, textFontSize);
         const d = centeredCharPath.toPathData(2);

         paths += `
           <g transform="translate(${x},${y}) rotate(${angle})">
             <path d="${d}" fill="black" stroke="black" stroke-width="1.5" stroke-linejoin="round"/>
           </g>`;

         // Advance to next character using per-pair spacing (baseline + kerning
         // delta). Deltas sum to zero across the word so total width matches.
         if (i < glyphs.length - 1) {
           currentOffset += charWidth + pairSpacings[i];
         }
       }
     } else if (name) {
       // Straight text - use the same textY as curved text for consistency
       const straightLineY = position === "above" ? 
         textY + (actualTextHeightScaled * 0.2) : // Same as curvedLineY calculation
         textY + (actualTextHeightScaled * 0.55); // Same as curvedLineY calculation
       
       const textPath = font.getPath(name, 0, 0, textFontSize);
       const textBounds = textPath.getBoundingBox();
       const textWidth = textBounds.x2 - textBounds.x1;
       
       // Center horizontally like SVG text-anchor="middle"
       const textX = (width / 2) - (textWidth / 2) - textBounds.x1;
       // Use baseline positioning like SVG
       const adjustedTextY = straightLineY;
       
       const centeredTextPath = font.getPath(name, textX, adjustedTextY, textFontSize);
       paths += `<path d="${centeredTextPath.toPathData(2)}" fill="black" stroke="black" stroke-width="1.5" stroke-linejoin="round"/>`;
     }
     
     // Explicit width + height (in addition to viewBox) so browsers that don't
     // derive intrinsic size from viewBox alone still measure this SVG at
     // 350×400 when loaded into <img> for canvas rasterisation. Without them,
     // img.naturalWidth/naturalHeight can come back as 300/150 (the default for
     // a size-less replaced element), which distorted the rotated jersey PNG.
     const svg = `
       <svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}" viewBox="0 0 ${width} ${height}">
        <path d="M0,0 L0.001,0.001" />
        <path d="M${width},${height} L${width}.001,${height}.001" />
        ${paths}
       </svg>
     `;
    //  const svg = `
    //    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${width} ${height}">
    //     ${paths}
    //    </svg>
    //  `;
     
     console.log("✅ Jersey SVG created with exact UI positioning");
     return svg;
     
   } catch (err) {
     console.error("❌ Error creating Jersey SVG:", err);
     
     // Provide user-friendly error message
     if (err.message && err.message.includes('wOF2')) {
       throw new Error('Font loading error. Please try again or use a different browser.');
     }
     
     throw err;
   }
 }

 // Helper function to analyze canvas and find text positions
 function findTextPositions(imageData, width, height) {
   const data = imageData.data;
   const positions = {
     number: null,
     text: null
   };
   
   // Simple approach: find the center of mass of dark pixels
   // This is a basic implementation - could be made more sophisticated
   let totalX = 0, totalY = 0, pixelCount = 0;
   let minX = width, maxX = 0, minY = height, maxY = 0;
   
   for (let y = 0; y < height; y++) {
     for (let x = 0; x < width; x++) {
       const index = (y * width + x) * 4;
       const r = data[index];
       const g = data[index + 1];
       const b = data[index + 2];
       const a = data[index + 3];
       
       // Check if pixel is dark (text)
       if (a > 128 && (r + g + b) < 384) { // Not white
         totalX += x;
         totalY += y;
         pixelCount++;
         
         minX = Math.min(minX, x);
         maxX = Math.max(maxX, x);
         minY = Math.min(minY, y);
         maxY = Math.max(maxY, y);
       }
     }
   }
   
   if (pixelCount > 0) {
     const centerX = totalX / pixelCount;
     const centerY = totalY / pixelCount;
     
     // Assume the larger text area is the number, smaller is the name
     const textHeight = maxY - minY;
     if (textHeight > 100) {
       positions.number = { x: centerX, y: centerY };
     } else {
       positions.text = { x: centerX, y: centerY, fontSize: Math.max(40, textHeight * 0.8) };
     }
   }
   
   return positions;
 }



  // Helper function to draw curved text on canvas using quadratic curve
  const drawCurvedText = (ctx, text, startX, endX, baseY, controlY) => {
    const characters = text.split('');
    
    // Calculate total text width with natural spacing
    const totalTextWidth = ctx.measureText(text).width;
    const startOffset = (endX - startX - totalTextWidth) / 2;
    
    let currentX = startX + startOffset;
    
    characters.forEach((char, index) => {
      const charWidth = ctx.measureText(char).width;
      const x = currentX + (charWidth / 2); // Center of character
      
      // Use linear distribution for symmetry (0 to 1) regardless of character widths
      const t = index / (characters.length - 1);
      
      // Calculate Y position using quadratic curve formula with proper control point
      // B(t) = (1-t)²P₀ + 2(1-t)tP₁ + t²P₂
      // Where P₀ = startY, P₁ = controlY, P₂ = endY
      const startY = baseY;
      const endY = baseY;
      const y = Math.pow(1 - t, 2) * startY + 2 * (1 - t) * t * controlY + Math.pow(t, 2) * endY;
      
      // Calculate the tangent angle at this point for character rotation
      // For quadratic curve: B'(t) = 2(1-t)(P₁-P₀) + 2t(P₂-P₁)
      // Simplified: B'(t) = 2(1-t)(controlY-startY) + 2t(endY-controlY)
      const dy = 2 * (1 - t) * (controlY - startY) + 2 * t * (endY - controlY);
      const dx = endX - startX; // Horizontal distance
      const angle = Math.atan2(dy, dx);
      
      // Save context state
      ctx.save();
      
      // Move to character position and rotate
      ctx.translate(x, y);
      ctx.rotate(angle);
      
      // Draw character at the curved position with rotation
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(char, 0, 0);
      
      // Restore context state
      ctx.restore();
      
      // Move to next character position with natural spacing
      currentX += charWidth;
    });
  };

  // Helper function to process the jersey canvas after font is loaded
  const processJerseyCanvas = (canvas, number, text, textPosition, baseWidth, baseHeight) => {
    const ctx = canvas.getContext('2d');
    
    try {
      // Get image data for processing - use the actual canvas dimensions (scaled)
      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const data = imageData.data;
      
      console.log('Processing jersey image data, pixels:', data.length / 4);
      
      // Apply metallic effect to text pixels with better detection
      for (let i = 0; i < data.length; i += 4) {
        const r = data[i];
        const g = data[i + 1];
        const b = data[i + 2];
        const a = data[i + 3];
        
        // More precise text detection - look for dark pixels with sufficient opacity
        // This helps preserve crisp edges and avoid including anti-aliased artifacts
        const isTextPixel = a > 50 && (r + g + b) < 200; // More restrictive than before
        
        if (isTextPixel) {
          // Calculate position for metallic effect - use actual canvas dimensions
          const x = (i / 4) % canvas.width / canvas.width;
          const y = Math.floor((i / 4) / canvas.width) / canvas.height;
          
          // Create metallic silver with directional sheen
          const t = (Math.sin(x * Math.PI * 2) * 0.5 + 0.5) * 0.8 + 0.1;
          const metallicR = Math.round(130 * (1 - t) + 248 * t);
          const metallicG = Math.round(135 * (1 - t) + 249 * t);
          const metallicB = Math.round(140 * (1 - t) + 252 * t);
          
          data[i] = metallicR;
          data[i + 1] = metallicG;
          data[i + 2] = metallicB;
          data[i + 3] = 255; // Keep fully opaque
        } else {
          // Make background transparent
          data[i + 3] = 0;
        }
      }
      
      // Add subtle noise for brushed metal effect
      const noiseAmount = 1; // Reduced noise for text
      for (let i = 0; i < data.length; i += 4) {
        if (data[i + 3] === 255) {
          const n = (Math.random() - 0.5) * noiseAmount;
          data[i] = Math.max(0, Math.min(255, data[i] + n));
          data[i + 1] = Math.max(0, Math.min(255, data[i + 1] + n));
          data[i + 2] = Math.max(0, Math.min(255, data[i + 2] + n));
        }
      }
      
      // Put the processed image data back
      ctx.putImageData(imageData, 0, 0);
      
      console.log('Metallic effect applied, converting to blob...');
      
      // Convert to blob and create URL
      canvas.toBlob((blob) => {
        if (!blob) {
          console.error('Failed to create blob from canvas');
          return;
        }
        
        console.log('Blob created:', blob.size, 'bytes');
        const metallicJerseyUrl = URL.createObjectURL(blob);
        console.log('Metallic jersey URL created:', metallicJerseyUrl);
        
        // Get dynamic styles based on selected product and text length
        const productType = selectedProduct?.collectionHandle || 'traveler-40oz';
        const totalTextLength = (number + text).length;
        const styles = getCustomElementStyles(productType, 'text', totalTextLength);
        
        // Create the jersey element with the metallic image
        const newElement = {
          type: 'image',
          imageUrl: metallicJerseyUrl,
          width: styles.width,
          height: styles.height,
          top: styles.top,
          left: styles.left,
          right: styles.right,
          margin: styles.margin,
          rotation: 0,
          isPresetDesign: false,
          textLength: totalTextLength
        };
        
        console.log('New metallic jersey element created:', newElement);
        
        // Replace all existing elements with the new metallic jersey element
        setCustomElements([newElement]);
        console.log("Added metallic jersey element:", newElement);
      }, 'image/png');
      
    } catch (error) {
      console.error("Error processing jersey canvas:", error);
      
      // Fallback to regular text if metallic processing fails
      const newElement = {
        type: 'text',
        text: `${number} ${text}`.trim(),
        fontSize: 24,
        color: '#000000',
        fontFamily: 'Jersey M54',
        rotation: 0,
        isPresetDesign: false,
        textLength: (number + text).length
      };
      
      setCustomElements([newElement]);
      console.log("Fallback jersey element created:", newElement);
    }
  };

  // const updateElement = (index, updatedElement) => {
  //   console.log("Updating element:", index, updatedElement);
  //   setCustomElements(prev => 
  //     prev.map((element, i) => onElementUpdate
  //   );
  // };

  // const deleteSelectedElement = () => {
  //   console.log("Deleting element");
  //   setCustomElements([]);
  //   setSelectedElement(null);
  // };

  const rotateElementLeft = () => {
    if (customElements.length > 0) {
      const currentElement = customElements[0];
      const currentRotation = currentElement.rotation || 0;
      // Rotate left: 0 → 270, 90 → 0, 180 → 90, 270 → 180
      let newRotation = currentRotation - 90;
      if (newRotation < 0) newRotation = 270; // Wrap to 270 if going below 0
      
      console.log(`Rotating element left: ${currentRotation}° → ${newRotation}°`);
      
      setCustomElements(prev => 
        prev.map((element, index) => 
          index === 0 
            ? { ...element, rotation: newRotation }
            : element
        )
      );
    }
  };

  const rotateElementRight = () => {
    if (customElements.length > 0) {
      const currentElement = customElements[0];
      const currentRotation = currentElement.rotation || 0;
      // Rotate right: 0 → 90, 90 → 180, 180 → 270, 270 → 0
      let newRotation = currentRotation + 90;
      if (newRotation >= 360) newRotation = 0; // Wrap to 0 if going above 270
      
      console.log(`Rotating element right: ${currentRotation}° → ${newRotation}°`);
      
      setCustomElements(prev => 
        prev.map((element, index) => 
          index === 0 
            ? { ...element, rotation: newRotation }
            : element
        )
      );
    }
  };

  const resetSelections = () => {
    setSelectedProduct(null);
    setSelectedColor(null);
    setProductImageUrl(null);
    setCustomElements([]);
    // setSelectedElement(null); // Commented out for static positioning
    setCustomText("");
    setSelectedFont("Arial");

    // Reset jersey state
    setJerseyNumber("");
    setJerseyText("");
    setJerseyTextPosition("below");
    setJerseyCurveText(false);
    
    // Reset preset design state
    setSelectedPresetDesign(null);
    setSelectedPresetCategory(null);

    setActiveTab("text");
    setIsDrawerOpen(false);
    setFocusMode(false);
    // Reset logo processing state
    setLogoProcessingState('idle');
    setProcessedLogoUrl(null);
    setOriginalLogoUrl(null);
    setProcessingProgress(0);
    setThresholdValue(128);
    setInvertEtch(false);
    setQuantity(6); // Reset to minimum quantity (wholesale)

    // Clear quality warning + SVG source flag
    setQualityWarning(null);
    setQualityAcknowledged(false);
    setIsSourceSVG(false);
  };

  // Check if current design requires terms acceptance
  const requiresTermsAcceptance = () => {
    // Jersey design
    if (activeTab === "jersey" && (jerseyNumber?.trim() || jerseyText?.trim())) {
      console.log('✅ Jersey design detected - requires terms');
      return true;
    }

    // Custom text
    if (activeTab === "text" && customText?.trim()) {
      console.log('✅ Custom text detected - requires terms');
      return true;
    }

    // Uploaded logo
    if (activeTab === "upload" && customElements.length > 0) {
      console.log('✅ Upload logo detected - requires terms');
      return true;
    }

    // Fallback: any custom elements regardless of tab
    if (customElements.length > 0) {
      console.log('✅ Custom elements detected - requires terms');
      return true;
    }

    console.log('❌ No custom designs detected - no terms required');
    return false;
  };

  // Handle terms modal for Add to Cart
  const handleAddToCartWithTerms = () => {
    const needsTerms = requiresTermsAcceptance();
    
    if (needsTerms) {
      setShowTermsModal(true);
      setTermsAccepted(false);
    } else {
      // Proceed directly to cart if no custom designs
      handleAddToCart();
    }
  };

  const handleTermsAccept = () => {
    setShowTermsModal(false);
    setTermsAccepted(false);
    // Proceed with the original Add to Cart function
    handleAddToCart();
  };

  const handleTermsCancel = () => {
    setShowTermsModal(false);
    setTermsAccepted(false);
  };


  // Deprecated local cart builder - superseded by handleAddToCart above
  // const handleAddToCart = () => {}

  // Function to process preset design with metallic effect
  const processPresetDesignWithMetallic = async (imageUrl, options = {}) => {
    const { storeBaseCanvas = false } = options;
    try {
      console.log('Processing preset design with metallic effect:', imageUrl);
      
      const img = new Image();
      img.crossOrigin = 'anonymous';
      
      return new Promise((resolve, reject) => {
        img.onload = () => {
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');
          
          // Use high resolution for crisp results (4x scale)
          const scale = 4;
          const baseWidth = img.width;
          const baseHeight = img.height;
          
          // Set canvas size with ultra-high resolution
          canvas.width = baseWidth * scale;
          canvas.height = baseHeight * scale;
          
          // Scale the context for ultra-high resolution
          ctx.scale(scale, scale);
          
          // Enable high-quality image rendering
          ctx.imageSmoothingEnabled = false;
          
          // Draw the original image
          ctx.drawImage(img, 0, 0, baseWidth, baseHeight);
          
          // Store base canvas BEFORE applying metallic effect (for fast color updates)
          if (storeBaseCanvas) {
            const baseCanvas = document.createElement('canvas');
            baseCanvas.width = canvas.width;
            baseCanvas.height = canvas.height;
            const baseCtx = baseCanvas.getContext('2d');
            baseCtx.drawImage(canvas, 0, 0);
            presetDesignCanvasRef.current = baseCanvas;
            console.log('💾 Stored base canvas for preset design:', baseCanvas.width, 'x', baseCanvas.height);
          }
          
          // Get image data for processing
          const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
          const data = imageData.data;
          
          console.log('Processing preset design image data, pixels:', data.length / 4);
          
          // Apply metallic effect to non-transparent pixels
          for (let i = 0; i < data.length; i += 4) {
            const r = data[i];
            const g = data[i + 1];
            const b = data[i + 2];
            const a = data[i + 3];
            
            // Check if this pixel has content (not transparent)
            if (a > 0) {
              // Calculate position for metallic effect
              const x = (i / 4) % canvas.width / canvas.width;
              const y = Math.floor((i / 4) / canvas.width) / canvas.height;
              
              // Check if selected jug has bronze-etch tag
              const isBronzeEtchValue = isBronzeEtch();
              
              // Create metallic effect - bronze or silver based on jug tag (centered gradient like Jersey)
              const distanceFromCenter = Math.abs(x - 0.5) * 2; // 0 at center, 1 at edges
              const t = (1 - distanceFromCenter) * 0.8 + 0.1; // Light in center, darker at edges
              
              let metallicR, metallicG, metallicB;
              if (isBronzeEtchValue) {
                // Bronze metallic effect - balanced bronze-gold with even lighter base
                const bronzeBase = { r: 155, g: 120, b: 85 }; // Even lighter bronze-gold base
                const bronzeHighlight = { r: 230, g: 190, b: 130 }; // Balanced bronze-gold highlight
                metallicR = Math.round(bronzeBase.r * (1 - t) + bronzeHighlight.r * t);
                metallicG = Math.round(bronzeBase.g * (1 - t) + bronzeHighlight.g * t);
                metallicB = Math.round(bronzeBase.b * (1 - t) + bronzeHighlight.b * t);
              } else {
                // Silver metallic effect (original)
                metallicR = Math.round(130 * (1 - t) + 248 * t);
                metallicG = Math.round(135 * (1 - t) + 249 * t);
                metallicB = Math.round(140 * (1 - t) + 252 * t);
              }
              
              data[i] = metallicR;
              data[i + 1] = metallicG;
              data[i + 2] = metallicB;
              data[i + 3] = a; // Keep original alpha
            }
          }
          
          // Add subtle noise for brushed metal effect
          const noiseAmount = 1;
          for (let i = 0; i < data.length; i += 4) {
            if (data[i + 3] > 0) {
              const n = (Math.random() - 0.5) * noiseAmount;
              data[i] = Math.max(0, Math.min(255, data[i] + n));
              data[i + 1] = Math.max(0, Math.min(255, data[i + 1] + n));
              data[i + 2] = Math.max(0, Math.min(255, data[i + 2] + n));
            }
          }
          
          // Put the processed image data back
          ctx.putImageData(imageData, 0, 0);
          
          console.log('Metallic effect applied to preset design, converting to blob...');
          
          // Convert to blob and create URL
          canvas.toBlob((blob) => {
            if (blob) {
              const metallicUrl = URL.createObjectURL(blob);
              console.log('Metallic preset design URL created:', metallicUrl);
              resolve(metallicUrl);
            } else {
              reject(new Error('Failed to create blob from canvas'));
            }
          }, 'image/png');
        };
        
        img.onerror = reject;
        img.src = imageUrl;
      });
      
    } catch (error) {
      console.error('Error processing preset design with metallic effect:', error);
      throw error;
    }
  };

  return (
    <div
      className={`hj-customizer-root nc-customizer-container hydrojug-customizer-main ${isMobileLayout ? "customizer--mobile" : ""}`}
      style={
        isMobileLayout
          ? {
              background: selectedColor
                ? `color-mix(in srgb, ${getColorPattern(selectedColor)?.color || "#F5F4F2"} 12%, #F5F4F2)`
                : "#F5F4F2",
              transition: "background 0.4s ease",
            }
          : undefined
      }
    >
      <GlobalScrollLock />
      <div
        className="nc-canvas-section customizer-canvas-section"
        style={
          isMobileLayout
            ? {
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                height: "calc(100% - 108px)", // minus tab bar + cart bar
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                padding: 8,
                backgroundColor: selectedColor
                  ? `color-mix(in srgb, ${getColorPattern(selectedColor)?.color || "#F5F4F2"} 12%, #F5F4F2)`
                  : undefined,
                transition: "background-color 0.4s ease",
              }
            : undefined
        }
      >
        {/* Mobile-only Reset — anchored to the canvas section (replaces the
            removed mobile canvas header). */}
        {isMobileLayout && (
          <button
            className="customizer-mobile-reset"
            onClick={resetSelections}
            style={{
              position: "absolute",
              // Clear the theme's sticky header (uses its --header-height var).
              top: "calc(var(--header-height, 60px) + 12px)",
              right: 12,
              zIndex: 5,
              fontSize: 10,
              fontWeight: 600,
              letterSpacing: "0.06em",
              textTransform: "uppercase",
              fontFamily: "var(--font-sans, 'Inter', sans-serif)",
              color: "var(--color-warm-gray, #9A9A96)",
              background: "rgba(245,244,242,0.9)",
              border: "1px solid var(--color-stone, #E8E6E1)",
              borderRadius: 6,
              padding: "6px 12px",
              cursor: "pointer",
            }}
          >
            Reset
          </button>
        )}
        <div className="nc-canvas-wrapper customizer-canvas-wrapper">
          {productImageUrl ? (
            <ProductCanvas
              productImageUrl={productImageUrl}
              customElements={customElements}
              focusMode={focusMode}
              productType={selectedProduct?.collectionHandle}
              hasDesign={customElements.length > 0}
              etchZoneOverride={
                hasNewPhotosTag()
                  ? getCustomElementStyles(
                      selectedProduct?.collectionHandle,
                      "design",
                    )
                  : undefined
              }
            />
          ) : (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                minHeight: 300,
              }}
            >
              <div
                style={{
                  width: 32,
                  height: 32,
                  border: "3px solid var(--color-stone, #E8E6E1)",
                  borderTopColor: "var(--color-rich-black, #1A1A1A)",
                  borderRadius: "50%",
                  animation: "hjSpinner 0.8s linear infinite",
                }}
              />
            </div>
          )}
        </div>

        {/* Rotate controls — text + jersey-text design modes only.
            Per the May 2026 audit, rotate previously appeared on every
            mode except jersey, but rotating an uploaded image / preset
            artwork doesn't make sense in the customizer's UX (the user
            already chose the orientation when uploading/picking). Limiting
            to text + jersey-text matches the audit spec. Buttons match
            HJ brand tokens: rich-black fill, 6 px radius, Inter 600 /
            13 px / 0.08 em, 44 px height. `whiteSpace: nowrap` keeps the
            label on a single line at any viewport width. */}
        {customElements.length > 0 &&
          (activeTab === "text" || activeTab === "jersey") && (
            <div
              className="customizer-rotation-controls customizer-rotation-controls--mobile"
              style={{
                marginTop: "1rem",
                display: "flex",
                gap: "0.5rem",
              }}
            >
              <button
                className="nc-button customizer-rotate-button customizer-rotate-left"
                onClick={rotateElementLeft}
                style={{
                  background: "var(--color-rich-black, #1A1A1A)",
                  color: "var(--color-off-white, #F5F4F2)",
                  border: "none",
                  height: 44,
                  padding: "0 22px",
                  borderRadius: "var(--radius-sm, 6px)",
                  fontFamily: "var(--font-sans, 'Inter', sans-serif)",
                  fontSize: 13,
                  fontWeight: 600,
                  letterSpacing: "0.08em",
                  textTransform: "uppercase",
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 6,
                  whiteSpace: "nowrap",
                  cursor: "pointer",
                }}
              >
                Rotate Left
              </button>
              <button
                className="nc-button customizer-rotate-button customizer-rotate-right"
                onClick={rotateElementRight}
                style={{
                  background: "var(--color-rich-black, #1A1A1A)",
                  color: "var(--color-off-white, #F5F4F2)",
                  border: "none",
                  height: 44,
                  padding: "0 22px",
                  borderRadius: "var(--radius-sm, 6px)",
                  fontFamily: "var(--font-sans, 'Inter', sans-serif)",
                  fontSize: 13,
                  fontWeight: 600,
                  letterSpacing: "0.08em",
                  textTransform: "uppercase",
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 6,
                  whiteSpace: "nowrap",
                  cursor: "pointer",
                }}
              >
                Rotate Right
              </button>
            </div>
          )}
      </div>

      <div
        className="nc-controls-section customizer-controls-section"
        style={
          isMobileLayout
            ? {
                display: "contents", // Dissolves this wrapper on mobile so drawer is a direct child of root
              }
            : undefined
        }
      >
        {/* Header — V3 layout: name + color + reset / price / add-to-bag / trust */}
        <div
          className="customizer-controls-header customizer-v3-header"
          style={{ padding: "20px 16px 16px" }}
        >
          {/* Product name + Reset */}
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
              marginBottom: 4,
            }}
          >
            <div>
              <p
                style={{
                  fontFamily: "var(--font-display)",
                  fontSize: 18,
                  fontWeight: 900,
                  textTransform: "uppercase",
                  letterSpacing: "0.03em",
                  color: "var(--color-rich-black, #1A1A1A)",
                  margin: "0 0 4px",
                  lineHeight: 1.1,
                  whiteSpace: "nowrap",
                }}
              >
                {selectedProduct
                  ? selectedProduct.productType
                  : "Select Product"}
              </p>
              <p
                style={{
                  fontFamily: "var(--font-sans, 'Inter', sans-serif)",
                  fontSize: 11,
                  color: "var(--color-warm-gray, #9A9A96)",
                  margin: 0,
                  textTransform: "capitalize",
                }}
              >
                {selectedColor?.title || "Choose a color"}
              </p>
            </div>
            <button
              className="nc-reset-button"
              onClick={resetSelections}
              style={{
                fontFamily: "var(--font-sans, 'Inter', sans-serif)",
                fontSize: 11,
                fontWeight: 500,
                letterSpacing: "0.06em",
                textTransform: "uppercase",
                color: "var(--color-warm-gray, #9A9A96)",
                cursor: "pointer",
                padding: "6px 14px",
                border: "1px solid var(--color-stone, #E8E6E1)",
                borderRadius: 6,
                background: "transparent",
              }}
            >
              Reset
            </button>
          </div>

          {/* Price + Add to Bag */}
          <div style={{ marginTop: 16, marginBottom: 8 }}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: 10,
              }}
            >
              <span
                style={{
                  fontFamily: "var(--font-sans, 'Inter', sans-serif)",
                  fontSize: 18,
                  fontWeight: 600,
                  color: "var(--color-rich-black, #1A1A1A)",
                }}
              >
                {selectedColor?.variants?.[0]?.price
                  ? fmtMoney(customizerTotal)
                  : "$--"}
              </span>
            </div>

            {/* Wholesale quantity stepper (minimum order of 6) */}
            <div
              className="customizer-v3-qty"
              style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}
            >
              <span style={{ fontFamily: "var(--font-sans, 'Inter', sans-serif)", fontSize: 11, fontWeight: 600, letterSpacing: "0.06em", textTransform: "uppercase", color: "var(--color-warm-gray, #9A9A96)" }}>
                Quantity (min 6)
              </span>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <button
                  type="button"
                  aria-label="Decrease quantity"
                  onClick={() => setQuantity(Math.max(6, quantity - 1))}
                  disabled={quantity <= 6}
                  style={{ width: 34, height: 34, borderRadius: 6, border: "1px solid var(--color-stone, #E8E6E1)", background: "var(--color-off-white, #F5F4F2)", fontSize: 18, lineHeight: 1, cursor: quantity <= 6 ? "not-allowed" : "pointer", opacity: quantity <= 6 ? 0.4 : 1 }}
                >−</button>
                <span style={{ minWidth: 28, textAlign: "center", fontSize: 15, fontWeight: 700 }}>{quantity}</span>
                <button
                  type="button"
                  aria-label="Increase quantity"
                  onClick={() => setQuantity(quantity + 1)}
                  style={{ width: 34, height: 34, borderRadius: 6, border: "1px solid var(--color-stone, #E8E6E1)", background: "var(--color-off-white, #F5F4F2)", fontSize: 18, lineHeight: 1, cursor: "pointer" }}
                >+</button>
              </div>
            </div>

            {cartError && (
              <div className="customizer-cart-error" style={{ marginBottom: 10, color: "#b91c1c", fontSize: 12 }}>
                {cartError}
              </div>
            )}
            <button
              className="customizer-add-to-cart-button customizer-v3-atb"
              onClick={handleAddToCartWithTerms}
              disabled={
                addingToCart ||
                logoProcessingState === "processing" ||
                logoProcessingState === "threshold" ||
                !selectedProduct ||
                !selectedColor ||
                !hasAnyDesign
              }
              style={{
                width: "100%",
                height: 52,
                background:
                  addingToCart ||
                  logoProcessingState === "processing" ||
                  logoProcessingState === "threshold" ||
                  !selectedProduct ||
                  !selectedColor
                    ? "#9ca3af"
                    : "var(--color-rich-black, #1A1A1A)",
                color: "var(--color-off-white, #F5F4F2)",
                borderRadius: 8,
                border: "none",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontFamily: "var(--font-sans, 'Inter', sans-serif)",
                fontSize: 13,
                fontWeight: 600,
                letterSpacing: "0.10em",
                textTransform: "uppercase",
                cursor:
                  addingToCart ||
                  logoProcessingState === "processing" ||
                  logoProcessingState === "threshold"
                    ? "not-allowed"
                    : "pointer",
                boxShadow: "0 1px 3px rgba(26,26,26,0.06)",
                marginBottom: 8,
                transition: "background 0.15s ease, transform 0.1s ease",
              }}
            >
              {logoProcessingState === "processing" ||
              logoProcessingState === "threshold"
                ? "⏳ Processing..."
                : addingToCart
                  ? "⏳ Adding..."
                  : `Add to Bag — ${selectedColor?.variants?.[0]?.price ? fmtMoney(customizerTotal) : ""}`}
            </button>
            {/* Trust Badges */}
            <div style={{ display: "flex", justifyContent: "center", gap: 16 }}>
              {["Limited Lifetime", "Final Sale", "BPA-Free"].map((t, i) => (
                <div
                  key={i}
                  style={{ display: "flex", alignItems: "center", gap: 4 }}
                >
                  <div
                    style={{
                      width: 4,
                      height: 4,
                      borderRadius: "50%",
                      background: "var(--color-warm-gray, #9A9A96)",
                    }}
                  />
                  <span
                    style={{
                      fontFamily: "var(--font-sans, 'Inter', sans-serif)",
                      fontSize: 10,
                      color: "var(--color-warm-gray, #9A9A96)",
                    }}
                  >
                    {t}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Divider */}
          <div
            style={{
              height: 1,
              background: "var(--color-stone, #E8E6E1)",
              marginTop: 16,
            }}
          />
        </div>

        {/* Product Type Selection — horizontal scroll */}
        <div
          className="nc-step-container customizer-step-container customizer-product-selection"
          style={{ padding: "16px" }}
        >
          <p
            style={{
              fontFamily: "var(--font-sans, 'Inter', sans-serif)",
              fontSize: 10,
              fontWeight: 600,
              letterSpacing: "0.12em",
              textTransform: "uppercase",
              color: "var(--color-warm-gray, #9A9A96)",
              margin: "0 0 10px",
            }}
          >
            Select Product Type
          </p>
          {loading ? (
            <div>Loading products...</div>
          ) : (
            <div
              style={{
                display: "flex",
                gap: 8,
                overflowX: "auto",
                paddingBottom: 4,
              }}
            >
              {collections.map((collection, idx) => {
                const active =
                  selectedProduct?.collectionHandle === collection.handle;
                const applyProductSwitch = () => {
                  document.body.classList.remove("ps-scroll-lock");
                  document.documentElement.classList.remove("ps-scroll-lock");
                  const newProduct = {
                    ...collection.products[0],
                    collectionHandle: collection.handle,
                    allProducts: collection.products,
                  };
                  setSelectedProduct(newProduct);
                  if (onProductChange) onProductChange(collection.handle);
                  if (selectedColor) {
                    const matchingColor = newProduct.allProducts.find(
                      (product) => product.title === selectedColor.title,
                    );
                    setSelectedColor(
                      matchingColor || pickBlackOrFirst(newProduct.allProducts),
                    );
                  } else {
                    setSelectedColor(pickBlackOrFirst(newProduct.allProducts));
                  }
                };
                return (
                  <div
                    key={`${collection.handle}-${idx}`}
                    className="hj-type customizer-product-card"
                    onClick={() => {
                      if (active) return;
                      applyProductSwitch();
                    }}
                    style={{
                      minWidth: 88,
                      padding: "10px 8px",
                      borderRadius: 6,
                      border: `1.5px solid ${active ? "var(--color-rich-black, #1A1A1A)" : "var(--color-stone, #E8E6E1)"}`,
                      background: active
                        ? "rgba(26,26,26,0.04)"
                        : "var(--color-white, #FFFFFF)",
                      textAlign: "center",
                      cursor: "pointer",
                      flexShrink: 0,
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "center",
                        marginBottom: 6,
                      }}
                    >
                      <img
                        src={
                          collection.products[0]?.variants[0]?.featured_image
                            ?.src
                        }
                        alt={collection.title}
                        style={{
                          width: 32,
                          height: 32,
                          objectFit: "contain",
                          filter: "brightness(0) saturate(100%)",
                          opacity: 0.5,
                        }}
                      />
                    </div>
                    <p
                      style={{
                        fontFamily: "var(--font-sans, 'Inter', sans-serif)",
                        fontSize: 10,
                        fontWeight: 600,
                        color: "var(--color-rich-black, #1A1A1A)",
                        margin: "0 0 1px",
                        lineHeight: 1.2,
                      }}
                    >
                      {collection.title}
                    </p>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Color Selection — V3 layout with name labels */}
        {selectedProduct && (
          <div
            className="nc-step-container customizer-step-container customizer-color-selection"
            style={{ padding: "0 16px 16px" }}
          >
            <p
              style={{
                fontFamily: "var(--font-sans, 'Inter', sans-serif)",
                fontSize: 10,
                fontWeight: 600,
                letterSpacing: "0.12em",
                textTransform: "uppercase",
                color: "var(--color-warm-gray, #9A9A96)",
                margin: "0 0 12px",
              }}
            >
              Color —{" "}
              <span
                style={{
                  color: "var(--color-charcoal, #333333)",
                  textTransform: "capitalize",
                }}
              >
                {selectedColor?.title || "Select"}
              </span>
            </p>
            <div
              className="customizer-v3-color-grid"
              style={{ display: "flex", overflowX: "scroll", gap: "8px" }}
            >
              {selectedProduct.allProducts?.map((product) => {
                const colorPattern = getColorPattern(product);
                const hasImage =
                  colorPattern?.image &&
                  colorPattern.image !== "null" &&
                  colorPattern.image !== "";
                const hexColor = colorPattern?.color;
                const sel = selectedColor?.id === product.id;

                return (
                  <div
                    key={product.id}
                    onClick={() => {
                      document.body.classList.remove("ps-scroll-lock");
                      document.documentElement.classList.remove(
                        "ps-scroll-lock",
                      );
                      setSelectedColor(product);
                    }}
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      gap: 4,
                      cursor: "pointer",
                      padding: "6px 2px",
                      borderRadius: 8,
                      background: sel ? "rgba(232,230,225,0.5)" : "transparent",
                      border: sel
                        ? "2px solid var(--color-rich-black, #1A1A1A)"
                        : "2px solid transparent",
                      transition: "all 0.15s ease",
                    }}
                    title={product.title}
                  >
                    <div
                      style={{
                        width: 44,
                        height: 44,
                        borderRadius: "50%",
                        flexShrink: 0,
                        // Use the longhand `backgroundColor` instead of the
                        // `background` shorthand so it composes cleanly with
                        // the explicit `backgroundImage` / `backgroundSize` /
                        // `backgroundPosition` longhands below. Mixing
                        // shorthand + longhand on the same element triggers
                        // React 19's "Updating a style property during
                        // rerender" warning because the shorthand resets all
                        // background-* descendants on each commit, racing
                        // with the longhand updates.
                        backgroundColor: hasImage
                          ? "transparent"
                          : hexColor || "#cccccc",
                        backgroundImage: hasImage
                          ? `url(${colorPattern.image})`
                          : "none",
                        backgroundSize: "cover",
                        backgroundPosition: "center",
                        backgroundRepeat: "no-repeat",
                        border: sel
                          ? "2px solid var(--color-rich-black, #1A1A1A)"
                          : "2px solid var(--color-stone, #E8E6E1)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        overflow: "hidden",
                      }}
                    >
                      {hasImage && (
                        <img
                          src={colorPattern.image}
                          alt={product.title}
                          style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "contain",
                          }}
                        />
                      )}
                      {!hasImage && !hexColor && (
                        <span style={{ fontSize: 10, color: "#666" }}>?</span>
                      )}
                    </div>
                    <span
                      style={{
                        fontFamily: "var(--font-sans, 'Inter', sans-serif)",
                        fontSize: 9,
                        fontWeight: sel ? 600 : 400,
                        color: sel
                          ? "var(--color-rich-black, #1A1A1A)"
                          : "var(--color-dark-gray, #6B6B6B)",
                        textAlign: "center",
                        lineHeight: 1.2,
                        maxWidth: 60,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {product.title}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Customization Options — V3 card layout */}
        {selectedColor && (
          <div
            className="nc-step-container customizer-step-container customizer-design-selection"
            style={{ padding: "0 16px 16px" }}
          >
            {/* Divider */}
            <div
              style={{
                height: 1,
                background: "var(--color-stone, #E8E6E1)",
                margin: "0 0 16px",
              }}
            />
            <p
              style={{
                fontFamily: "var(--font-sans, 'Inter', sans-serif)",
                fontSize: 10,
                fontWeight: 600,
                letterSpacing: "0.12em",
                textTransform: "uppercase",
                color: "var(--color-warm-gray, #9A9A96)",
                margin: "0 0 12px",
              }}
            >
              Add Customization
            </p>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {/* Wholesale (B2B): upload-logo is the only design option. */}
              {[
                {
                  id: "upload",
                  icon: "https://cdn.shopify.com/s/files/1/0110/7827/1033/files/customizer-upload-icon.png?v=1759423083",
                  title: "Upload Logo",
                  desc: "Add your own logo or image",
                  price: pricing.uploadLogoPrice || "$12.99",
                  hasDesign: customizerDesignFlags.hasUploadLogo,
                  tab: "upload",
                },
              ].map((opt) => (
                <div
                  key={opt.id}
                  className="nc-customization-card customizer-option-card"
                  data-has-design={opt.hasDesign ? "true" : undefined}
                  onClick={() => {
                    setActiveTab(opt.tab);
                    setIsDrawerOpen(true);
                    setFocusMode(true);
                  }}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 14,
                    padding: "14px 16px",
                    borderRadius: 6,
                    border: `1px solid ${opt.hasDesign ? "var(--color-rich-black, #1A1A1A)" : "var(--color-stone, #E8E6E1)"}`,
                    background: opt.hasDesign
                      ? "rgba(26,26,26,0.03)"
                      : "var(--color-white, #FFFFFF)",
                    cursor: "pointer",
                    position: "relative",
                    transition: "all 0.2s ease",
                  }}
                >
                  <div
                    className="icon customizer-option-icon"
                    style={{
                      width: 40,
                      height: 40,
                      borderRadius: 6,
                      background: "var(--color-stone, #E8E6E1)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    <img
                      src={opt.icon}
                      alt={opt.title}
                      style={{ width: 20, height: 20, objectFit: "contain" }}
                    />
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <p
                      style={{
                        fontFamily: "var(--font-sans, 'Inter', sans-serif)",
                        fontSize: 13,
                        fontWeight: 600,
                        color: "var(--color-rich-black, #1A1A1A)",
                        margin: "0 0 2px",
                      }}
                    >
                      {opt.title}
                    </p>
                    <p
                      style={{
                        fontFamily: "var(--font-sans, 'Inter', sans-serif)",
                        fontSize: 11,
                        color: "var(--color-warm-gray, #9A9A96)",
                        margin: 0,
                      }}
                    >
                      {opt.desc}
                    </p>
                  </div>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      flexShrink: 0,
                    }}
                  >
                    {opt.hasDesign && (
                      <span
                        style={{
                          fontFamily: "var(--font-sans, 'Inter', sans-serif)",
                          fontSize: 9,
                          fontWeight: 600,
                          letterSpacing: "0.06em",
                          textTransform: "uppercase",
                          background: "var(--color-rich-black, #1A1A1A)",
                          color: "var(--color-off-white, #F5F4F2)",
                          padding: "3px 8px",
                          borderRadius: 4,
                        }}
                      >
                        Added
                      </span>
                    )}
                    <span
                      style={{
                        fontFamily: "var(--font-sans, 'Inter', sans-serif)",
                        fontSize: 12,
                        fontWeight: 600,
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 6,
                      }}
                    >
                      {/* Wholesale: etch is free — slash the price, show FREE */}
                      <span
                        style={{
                          textDecoration: "line-through",
                          color: "var(--color-warm-gray, #9A9A96)",
                          fontWeight: 500,
                        }}
                      >
                        {fmtMoney(opt.price)}
                      </span>
                      <span style={{ color: "#4a7c59", fontWeight: 700, letterSpacing: "0.04em" }}>
                        FREE
                      </span>
                    </span>
                    <svg
                      width="14"
                      height="14"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="var(--color-warm-gray, #9A9A96)"
                      strokeWidth="2"
                      strokeLinecap="round"
                    >
                      <polyline points="9 18 15 12 9 6" />
                    </svg>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Price Breakdown — V3 */}
        {selectedProduct && selectedColor && (
          <div
            className="customizer-price-breakdown"
            style={{ padding: "0 16px 20px" }}
          >
            <div
              style={{
                height: 1,
                background: "var(--color-stone, #E8E6E1)",
                margin: "0 0 16px",
              }}
            />
            <div>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  padding: "4px 0",
                }}
              >
                <span
                  style={{
                    fontFamily: "var(--font-sans, 'Inter', sans-serif)",
                    fontSize: 12,
                    color: "var(--color-charcoal, #333333)",
                  }}
                >
                  Base price {selectedColor?.variants?.[0]?.price ? `(${fmtMoney(selectedColor.variants[0].price)} × ${quantity})` : ""}
                </span>
                <span
                  style={{
                    fontFamily: "var(--font-sans, 'Inter', sans-serif)",
                    fontSize: 12,
                  }}
                >
                  {selectedColor?.variants?.[0]?.price
                    ? fmtMoney(customizerTotal)
                    : "--"}
                </span>
              </div>
              {customizerDesignFlags.hasUploadLogo && (
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    padding: "4px 0",
                  }}
                >
                  <span
                    style={{
                      fontFamily: "var(--font-sans, 'Inter', sans-serif)",
                      fontSize: 12,
                      color: "var(--color-charcoal, #333333)",
                    }}
                  >
                    Etch / Upload Logo
                  </span>
                  {/* Wholesale: etch is free — slash the retail price, show FREE */}
                  <span
                    style={{
                      fontFamily: "var(--font-sans, 'Inter', sans-serif)",
                      fontSize: 12,
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 6,
                    }}
                  >
                    <span style={{ textDecoration: "line-through", color: "var(--color-warm-gray, #9A9A96)" }}>
                      {fmtMoney(pricing.uploadLogoPrice || "$12.99")}
                    </span>
                    <span style={{ color: "#4a7c59", fontWeight: 700, letterSpacing: "0.04em" }}>FREE</span>
                  </span>
                </div>
              )}
              {customizerDesignFlags.hasCustomText && (
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    padding: "4px 0",
                  }}
                >
                  <span
                    style={{
                      fontFamily: "var(--font-sans, 'Inter', sans-serif)",
                      fontSize: 12,
                      color: "var(--color-charcoal, #333333)",
                    }}
                  >
                    Custom Text
                  </span>
                  <span
                    style={{
                      fontFamily: "var(--font-sans, 'Inter', sans-serif)",
                      fontSize: 12,
                    }}
                  >
                    +{fmtMoney(pricing.customTextPrice || "$9.99")}
                  </span>
                </div>
              )}
              {customizerDesignFlags.hasJersey && (
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    padding: "4px 0",
                  }}
                >
                  <span
                    style={{
                      fontFamily: "var(--font-sans, 'Inter', sans-serif)",
                      fontSize: 12,
                      color: "var(--color-charcoal, #333333)",
                    }}
                  >
                    Jersey Style
                  </span>
                  <span
                    style={{
                      fontFamily: "var(--font-sans, 'Inter', sans-serif)",
                      fontSize: 12,
                    }}
                  >
                    +{fmtMoney(pricing.jerseyPrice || "$9.99")}
                  </span>
                </div>
              )}
              {customizerDesignFlags.hasGalleryDesign && (
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    padding: "4px 0",
                  }}
                >
                  <span
                    style={{
                      fontFamily: "var(--font-sans, 'Inter', sans-serif)",
                      fontSize: 12,
                      color: "var(--color-charcoal, #333333)",
                    }}
                  >
                    Gallery
                  </span>
                  <span
                    style={{
                      fontFamily: "var(--font-sans, 'Inter', sans-serif)",
                      fontSize: 12,
                    }}
                  >
                    +{fmtMoney(pricing.galleryPrice || "$9.99")}
                  </span>
                </div>
              )}
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  padding: "10px 0 0",
                  borderTop: "1px solid var(--color-stone, #E8E6E1)",
                  marginTop: 6,
                }}
              >
                <span
                  style={{
                    fontFamily: "var(--font-sans, 'Inter', sans-serif)",
                    fontSize: 15,
                    fontWeight: 600,
                  }}
                >
                  Total
                </span>
                <span
                  style={{
                    fontFamily: "var(--font-sans, 'Inter', sans-serif)",
                    fontSize: 15,
                    fontWeight: 600,
                  }}
                >
                  {selectedColor?.variants?.[0]?.price
                    ? fmtMoney(customizerTotal)
                    : "--"}
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Slide Drawer */}
        <Overlay
          $isOpen={isDrawerOpen}
          onClick={() => {
            setIsDrawerOpen(false);
            setFocusMode(false);
          }}
          style={
            isMobileLayout
              ? {
                  pointerEvents: isDrawerOpen ? "auto" : "none",
                  visibility: "visible",
                }
              : undefined
          }
        />

        <SlideDrawer
          ref={drawerRef}
          className="customizer-slide-drawer"
          $isOpen={isDrawerOpen}
          $isMobile={isMobileLayout}
          style={
            isMobileLayout
              ? { pointerEvents: "auto", visibility: "visible" }
              : undefined
          }
        >
          <div className="nc-drawer-header customizer-drawer-header">
            <button
              className="nc-back-button customizer-back-button"
              onClick={() => {
                // On mobile, if we're inside a specific design sub-tool,
                // Back returns to the Step 2 menu instead of closing the
                // drawer — otherwise the user has no way to switch between
                // Upload/Text/Jersey/Gallery without leaving Step 2.
                const inDesignSubTab =
                  isMobileLayout &&
                  (activeTab === "upload" ||
                    activeTab === "text" ||
                    activeTab === "jersey" ||
                    activeTab === "preset");
                if (inDesignSubTab) {
                  setActiveTab("design");
                } else {
                  setIsDrawerOpen(false);
                  setFocusMode(false);
                }
              }}
            >
              ← Back
            </button>
            <h3
              className="customizer-drawer-title"
              style={{
                fontSize: "16px",
                fontWeight: "600",
                color: "#374151",
                margin: 0,
              }}
            >
              {activeTab === "upload"
                ? "UPLOAD LOGO"
                : activeTab === "text"
                  ? "CUSTOM TEXT"
                  : activeTab === "jersey"
                    ? "JERSEY STYLE"
                    : activeTab === "preset"
                      ? "PRESET DESIGNS"
                      : activeTab === "product"
                        ? "PRODUCT & COLOR"
                        : activeTab === "design"
                          ? "STEP 2: DESIGN"
                          : "CUSTOMIZE"}
            </h3>
            <div
              className="customizer-drawer-spacer"
              style={{ width: "60px" }}
            ></div>{" "}
            {/* Spacer for centering */}
          </div>

          <div className="nc-drawer-content customizer-drawer-content">
            {isMobileLayout && activeTab === "design" && (
              <div
                className="customizer-design-menu"
                role="menu"
                aria-label="Design options"
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 10,
                }}
              >
                {[
                  {
                    id: "upload",
                    label: "Upload Logo",
                    description: "Add your own PNG, JPG, or SVG artwork",
                    dot: customizerDesignFlags.hasUploadLogo,
                    icon: (
                      <svg
                        width="22"
                        height="22"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                        <polyline points="17 8 12 3 7 8" />
                        <line x1="12" y1="3" x2="12" y2="15" />
                      </svg>
                    ),
                  },
                ].map((option) => (
                  <button
                    key={option.id}
                    type="button"
                    role="menuitem"
                    onClick={() => setActiveTab(option.id)}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 14,
                      width: "100%",
                      minHeight: 64,
                      padding: "12px 16px",
                      borderRadius: 12,
                      border: "1px solid var(--color-stone, #E8E6E1)",
                      background: "var(--color-off-white, #F5F4F2)",
                      color: "var(--color-rich-black, #1A1A1A)",
                      fontFamily: "var(--font-sans, 'Inter', sans-serif)",
                      cursor: "pointer",
                      textAlign: "left",
                    }}
                  >
                    <span
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: 40,
                        height: 40,
                        borderRadius: 10,
                        background: "var(--color-stone, #E8E6E1)",
                        color: "var(--color-rich-black, #1A1A1A)",
                        flexShrink: 0,
                      }}
                    >
                      {option.icon}
                    </span>
                    <span
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 2,
                        flex: 1,
                        minWidth: 0,
                      }}
                    >
                      <span
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 8,
                          fontSize: 12,
                          fontWeight: 700,
                          letterSpacing: "0.04em",
                          textTransform: "uppercase",
                        }}
                      >
                        {option.label}
                        {option.dot && (
                          <span
                            aria-hidden="true"
                            style={{
                              width: 7,
                              height: 7,
                              borderRadius: "50%",
                              background: "#10B981",
                              display: "inline-block",
                            }}
                          />
                        )}
                      </span>
                      <span
                        style={{
                          fontSize: 12,
                          fontWeight: 500,
                          color: "var(--color-dark-gray, #6B7280)",
                          letterSpacing: "0.01em",
                          textTransform: "none",
                        }}
                      >
                        {option.description}
                      </span>
                    </span>
                    <span
                      aria-hidden="true"
                      style={{
                        color: "var(--color-warm-gray, #9A9A96)",
                        fontSize: 18,
                        lineHeight: 1,
                        marginLeft: 4,
                      }}
                    >
                      ›
                    </span>
                  </button>
                ))}
              </div>
            )}
            {activeTab === "upload" && (
              <div className="customizer-upload-section">
                {/* Logo Upload Section */}
                <div className="nc-upload-guidelines">
                  {/* <div className="nc-guidelines-header">
                    <div className="icon">📋</div>
                    <div className="title">Upload Requirements</div>
                  </div> */}

                  <div className="nc-guidelines-list">
                    <div className="nc-guideline-item">
                      <div className="icon">
                        <svg
                          width="16"
                          height="16"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="#6b7280"
                          strokeWidth="2"
                        >
                          <rect
                            x="3"
                            y="3"
                            width="18"
                            height="18"
                            rx="2"
                            ry="2"
                          />
                          <path d="M9 9h6v6H9z" />
                        </svg>
                      </div>
                      <div className="text">
                        <span className="size-requirement">
                          Image must be 300px (W or H)
                        </span>
                        <br />
                        Minimum size requirement for quality
                      </div>
                    </div>

                    <div className="nc-guideline-item">
                      <div className="icon">
                        <svg
                          width="16"
                          height="16"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="#6b7280"
                          strokeWidth="2"
                        >
                          <rect
                            x="3"
                            y="3"
                            width="18"
                            height="18"
                            rx="2"
                            ry="2"
                          />
                          <circle cx="8.5" cy="8.5" r="1.5" />
                          <polyline points="21,15 16,10 5,21" />
                        </svg>
                      </div>
                      <div className="text">
                        <span className="size-requirement">No photographs</span>
                        <br />
                        Use logos, graphics, or artwork only
                      </div>
                    </div>

                    <div className="nc-guideline-item">
                      <div className="icon">
                        <svg
                          width="16"
                          height="16"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="#6b7280"
                          strokeWidth="2"
                        >
                          <circle cx="12" cy="12" r="10" />
                          <line x1="4.93" y1="4.93" x2="19.07" y2="19.07" />
                        </svg>
                      </div>
                      <div className="text">
                        <span className="size-requirement">
                          No copyright infringement
                        </span>
                        <br />
                        Only upload content you own or have permission to use
                      </div>
                    </div>

                    <div className="nc-guideline-item">
                      <div className="icon">
                        <svg
                          width="16"
                          height="16"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="#6b7280"
                          strokeWidth="2"
                        >
                          <path d="M10 2v20M14 2v20M4 7h16M4 17h16" />
                        </svg>
                      </div>
                      <div className="text">
                        <span className="size-requirement">
                          No trademark infringement
                        </span>
                        <br />
                        Avoid using protected brand names, logos, or symbols
                      </div>
                    </div>
                  </div>
                </div>

                {/* Fallback Processing Button */}
                {logoProcessingState === "error" && originalLogoUrl && (
                  <div style={{ marginTop: "1rem" }}>
                    <p
                      style={{
                        fontSize: "12px",
                        color: "#6b7280",
                        marginBottom: "0.5rem",
                      }}
                    >
                      Cloudinary processing failed. Try local processing
                      instead:
                    </p>
                    <button
                      className="nc-button"
                      onClick={() =>
                        processImageLocally(originalLogoUrl, {
                          preview: true,
                          isSVG: isSourceSVG,
                        })
                      }
                      style={{ background: "#10b981" }}
                    >
                      Process Locally
                    </button>
                  </div>
                )}

                {/* Processing State */}
                {logoProcessingState === "processing" && (
                  <div className="nc-processing-container customizer-processing-container">
                    <div
                      className="customizer-processing-status"
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "0.5rem",
                        marginBottom: "0.5rem",
                      }}
                    >
                      <div
                        className="customizer-processing-spinner"
                        style={{
                          width: "16px",
                          height: "16px",
                          border: "2px solid #3b82f6",
                          borderTop: "2px solid transparent",
                          borderRadius: "50%",
                          animation: "spin 1s linear infinite",
                        }}
                      ></div>
                      <span
                        className="customizer-processing-text"
                        style={{ fontSize: "14px", color: "#374151" }}
                      >
                        Processing logo for etching...
                      </span>
                    </div>
                    <div className="nc-progress-bar customizer-progress-bar">
                      <ProgressFill
                        className="customizer-progress-fill"
                        progress={processingProgress}
                      />
                    </div>
                    <p
                      className="customizer-processing-description"
                      style={{
                        fontSize: "12px",
                        color: "#6b7280",
                        marginTop: "0.5rem",
                      }}
                    >
                      Removing background, converting to black & white, and
                      creating SVG for etching
                    </p>
                  </div>
                )}

                {/* Processed Logo - Show small preview with button to review */}
                {logoProcessingState === "processed" && !showReviewModal && (
                  <div className="nc-processing-container customizer-processed-container">
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "1rem",
                      }}
                    >
                      {processedLogoUrl && (
                        <img
                          src={processedLogoUrl}
                          alt="Processed logo"
                          style={{
                            width: "60px",
                            height: "60px",
                            objectFit: "contain",
                            border: "1px solid #e2e8f0",
                            borderRadius: "0.25rem",
                            background: "white",
                          }}
                        />
                      )}
                      <div style={{ flex: 1 }}>
                        <p
                          style={{
                            margin: 0,
                            fontSize: "13px",
                            fontWeight: "600",
                            color: "#374151",
                          }}
                        >
                          Logo processed
                        </p>
                        <button
                          onClick={() => setShowReviewModal(true)}
                          style={{
                            marginTop: "0.25rem",
                            padding: 0,
                            background: "none",
                            border: "none",
                            color: "#3b82f6",
                            fontSize: "13px",
                            cursor: "pointer",
                            textDecoration: "underline",
                          }}
                        >
                          Review & adjust
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {/* Error State */}
                {logoProcessingState === "error" && (
                  <div className="nc-error-message">
                    <strong>Processing Error:</strong> There was an issue
                    processing your logo. Please try uploading a different image
                    or contact support.
                  </div>
                )}
                <label
                  className="nc-upload-label customizer-upload-label"
                  htmlFor="logo-upload"
                >
                  Click to upload logo
                  <input
                    id="logo-upload"
                    className="nc-upload-button customizer-upload-button"
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    disabled={logoProcessingState === "processing"}
                  />
                </label>
                <p
                  style={{
                    fontSize: "13px",
                    color: "#6b7280",
                    marginTop: "0.5rem",
                  }}
                >
                  Supported formats: PNG, JPG, SVG (recommended for best
                  quality)
                </p>

                {/* Upload Guidelines */}
              </div>
            )}

            {activeTab === "text" && (
              <div className="customizer-text-section">
                {/* Text Input */}
                <div
                  className="customizer-text-input-container"
                  style={{ marginBottom: "1.5rem" }}
                >
                  <label
                    className="customizer-text-input-label"
                    style={{
                      display: "block",
                      fontSize: "14px",
                      fontWeight: "600",
                      color: "#374151",
                      marginBottom: "0.5rem",
                    }}
                  >
                    Enter Your Text
                  </label>
                  <input
                    className="nc-input"
                    type="text"
                    placeholder="Enter your custom text"
                    value={customText}
                    onChange={(e) => {
                      // Allow letters, numbers, spaces, and common special characters
                      const value = e.target.value.replace(
                        /[^a-zA-Z0-9 !?+\-&@#$%'".,:;]/g,
                        "",
                      );
                      setCustomText(value);
                    }}
                    maxLength={15}
                    style={{
                      width: "100%",
                      padding: "0.75rem",
                      border: "1px solid #d1d5db",
                      borderRadius: "0.375rem",
                      fontSize: "16px",
                      marginBottom: "0.5rem",
                    }}
                  />
                  <p
                    className="customizer-text-input-help"
                    style={{
                      fontSize: "12px",
                      color: "#6b7280",
                      margin: 0,
                    }}
                  >
                    Maximum 15 characters
                  </p>
                </div>

                {/* Font Preview Grid */}
                {customText.trim() && (
                  <div
                    className="customizer-font-preview-container"
                    style={{ marginBottom: "1.5rem" }}
                  >
                    <label
                      className="customizer-font-preview-label"
                      style={{
                        display: "block",
                        fontSize: "14px",
                        fontWeight: "600",
                        color: "#374151",
                        marginBottom: "0.75rem",
                      }}
                    >
                      Choose Font Style
                    </label>
                    <div
                      className="customizer-font-grid"
                      style={{
                        display: "grid",
                        gridTemplateColumns:
                          "repeat(auto-fit, minmax(200px, 1fr))",
                        gap: "0.75rem",
                      }}
                    >
                      {fonts.map((font) => (
                        <FontCard
                          key={font.value}
                          className={`font-card-${font.value}`}
                          $selected={selectedFont === font.value}
                          onClick={() => {
                            console.log(
                              "Font clicked:",
                              font.value,
                              "Currently selected:",
                              selectedFont,
                              "Is selected:",
                              selectedFont === font.value,
                            );
                            setSelectedFont(font.value);
                            // Auto-create text design when font is selected
                            if (customText.trim()) {
                              const canvasWidth =
                                window.innerWidth <= 768 ? 400 : 600;
                              const canvasHeight =
                                window.innerWidth <= 768 ? 400 : 600;
                              createMetallicText(
                                customText,
                                font.value,
                                canvasWidth,
                                canvasHeight,
                              );
                              setIsDrawerOpen(false);
                              setFocusMode(false);
                            }
                          }}
                        >
                          <div
                            className="customizer-font-preview-text"
                            style={{
                              fontFamily: font.value,
                              fontSize: "24px",
                              fontWeight:
                                font.value === "Montserrat" ? "600" : "400",
                              color: "#374151",
                              marginBottom: "0.5rem",
                              lineHeight: "1.2",
                              minHeight: "2.5rem",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              lineBreak: "anywhere",
                            }}
                          >
                            {customText}
                          </div>
                          <div
                            className="customizer-font-preview-name"
                            style={{
                              fontSize: "12px",
                              color: "#6b7280",
                              fontWeight: "500",
                            }}
                          >
                            {font.name}
                          </div>
                        </FontCard>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {activeTab === "jersey" && (
              <div className="customizer-jersey-section">
                {/* Jersey Number Input */}
                <div
                  className="customizer-jersey-numbers"
                  style={{ marginBottom: "1rem" }}
                >
                  <label
                    className="customizer-jersey-label"
                    style={{
                      display: "block",
                      fontSize: "14px",
                      fontWeight: "600",
                      color: "#374151",
                      marginBottom: "0.75rem",
                    }}
                  >
                    Jersey Number
                  </label>
                  <input
                    className="nc-input"
                    type="text"
                    placeholder="Enter jersey number"
                    value={jerseyNumber}
                    onChange={(e) => {
                      // Only allow numbers
                      const value = e.target.value.replace(/[^0-9]/g, "");
                      setJerseyNumber(value);
                    }}
                    maxLength={2}
                    style={{
                      width: "100%",
                      padding: "0.75rem",
                      border: "1px solid #d1d5db",
                      borderRadius: "0.375rem",
                      fontSize: "16px",
                      textAlign: "center",
                    }}
                  />
                </div>

                {/* Jersey Text Input */}
                <div
                  className="customizer-jersey-text"
                  style={{ marginBottom: "1rem" }}
                >
                  <label
                    className="customizer-jersey-label"
                    style={{
                      display: "block",
                      fontSize: "14px",
                      fontWeight: "600",
                      color: "#374151",
                      marginBottom: "0.5rem",
                    }}
                  >
                    Jersey Text
                  </label>
                  <input
                    className="nc-input"
                    type="text"
                    placeholder="Enter jersey text (e.g., team name)"
                    value={jerseyText}
                    onChange={(e) => {
                      // Only allow letters (a-z, A-Z) and hyphens (-)
                      const value = e.target.value.replace(/[^a-zA-Z\-]/g, "");
                      setJerseyText(value.toUpperCase());
                    }}
                    maxLength={12}
                    style={{
                      width: "100%",
                      padding: "0.75rem",
                      border: "1px solid #d1d5db",
                      borderRadius: "0.375rem",
                      fontSize: "16px",
                      textTransform: "uppercase",
                    }}
                  />
                </div>

                {/* Text Position Selection */}
                <div
                  className="customizer-jersey-position"
                  style={{ marginBottom: "1.5rem" }}
                >
                  <label
                    className="customizer-jersey-label"
                    style={{
                      display: "block",
                      fontSize: "14px",
                      fontWeight: "600",
                      color: "#374151",
                      marginBottom: "0.5rem",
                    }}
                  >
                    Text Position
                  </label>
                  <div style={{ display: "flex", gap: "0.5rem" }}>
                    <button
                      className="nc-button"
                      onClick={() => setJerseyTextPosition("above")}
                      style={{
                        flex: 1,
                        padding: "0.75rem",
                        border: "1px solid #d1d5db",
                        borderRadius: "0.375rem",
                        fontSize: "16px",
                        background:
                          jerseyTextPosition === "above" ? "#10b981" : "white",
                        color:
                          jerseyTextPosition === "above" ? "white" : "#374151",
                        cursor: "pointer",
                        transition: "all 0.2s ease",
                      }}
                    >
                      Above Number
                    </button>
                    <button
                      className="nc-button"
                      onClick={() => setJerseyTextPosition("below")}
                      style={{
                        flex: 1,
                        padding: "0.75rem",
                        border: "1px solid #d1d5db",
                        borderRadius: "0.375rem",
                        fontSize: "16px",
                        background:
                          jerseyTextPosition === "below" ? "#10b981" : "white",
                        color:
                          jerseyTextPosition === "below" ? "white" : "#374151",
                        cursor: "pointer",
                        transition: "all 0.2s ease",
                      }}
                    >
                      Below Number
                    </button>
                  </div>
                </div>

                {/* Curve Text Toggle */}
                <div
                  style={{ marginTop: "1rem", display: "flex", gap: "0.5rem" }}
                >
                  <label
                    style={{
                      fontSize: "14px",
                      fontWeight: "600",
                      color: "#374151",
                      marginBottom: "0.5rem",
                      display: "block",
                    }}
                  >
                    Curved Text
                  </label>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      marginBottom: "0.5rem",
                    }}
                  >
                    <button
                      type="button"
                      onClick={() => setJerseyCurveText(!jerseyCurveText)}
                      style={{
                        width: "44px",
                        height: "24px",
                        borderRadius: "12px",
                        border: "none",
                        background: jerseyCurveText ? "#10b981" : "#d1d5db",
                        position: "relative",
                        cursor: "pointer",
                        transition: "background 0.2s ease",
                      }}
                    >
                      <div
                        style={{
                          width: "20px",
                          height: "20px",
                          borderRadius: "50%",
                          background: "white",
                          position: "absolute",
                          top: "2px",
                          left: jerseyCurveText ? "22px" : "2px",
                          transition: "left 0.2s ease",
                          boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
                        }}
                      />
                    </button>
                  </div>
                </div>
              </div>
            )}

            {activeTab === "preset" && (
              <div className="customizer-preset-section">
                {/* Preset Design Categories */}
                {!selectedPresetCategory ? (
                  <div>
                    <label
                      className="customizer-preset-label"
                      style={{
                        display: "block",
                        fontSize: "14px",
                        fontWeight: "600",
                        color: "#374151",
                        marginBottom: "0.75rem",
                      }}
                    >
                      Choose Design Category
                    </label>
                    <div
                      className="customizer-preset-categories"
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "0.75rem",
                      }}
                    >
                      {presetDesignCategories.length === 0 && (
                        <div
                          style={{
                            padding: "1rem",
                            textAlign: "center",
                            color: "#6b7280",
                          }}
                        >
                          <p>No preset design categories available</p>
                          <p
                            style={{ fontSize: "12px", marginTop: "0.5rem" }}
                          >
                            Check console for debugging information
                          </p>
                        </div>
                      )}
                      {presetDesignCategories.map((category, index) => {
                        // Get the first design as preview
                        const previewDesign =
                          category.designs && category.designs.length > 0
                            ? category.designs[0]
                            : null;
                        console.log(`   - Preview design:`, previewDesign);

                        return (
                          <div
                            key={index}
                            onClick={() => setSelectedPresetCategory(category)}
                            style={{
                              padding: "10px",
                              border: "1px solid #e5e7eb",
                              borderRadius: "0.5rem",
                              background: "white",
                              cursor: "pointer",
                              transition: "all 0.2s ease",
                              textAlign: "center",
                              display: "flex",
                              flexDirection: "row",
                              gap: "0.75rem",
                              alignItems: "center",
                              lineHeight: "1",
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.borderColor = "#10b981";
                              e.currentTarget.style.background = "#f0fdf4";
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.borderColor = "#e5e7eb";
                              e.currentTarget.style.background = "white";
                            }}
                          >
                            {/* Preview Image */}
                            {previewDesign && previewDesign.image && (
                              <div
                                style={{
                                  width: "50px",
                                  height: "50px",
                                  borderRadius: "0.375rem",
                                  overflow: "hidden",
                                  border: "1px solid #e5e7eb",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  boxShadow: "0 0 7px 1px rgba(0, 0, 0, 0.1)",
                                }}
                              >
                                <img
                                  src={
                                    previewDesign.image?.src ||
                                    previewDesign.image?.url
                                  }
                                  alt={`${category.name} preview`}
                                  style={{
                                    width: "100%",
                                    height: "100%",
                                    objectFit: "contain",
                                  }}
                                  onError={(e) => {
                                    e.target.style.display = "none";
                                  }}
                                />
                              </div>
                            )}

                            {/* Category Info */}
                            <div
                              className="customizer-preset-category-info"
                              style={{
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "flex-start",
                                gap: "0.25rem",
                              }}
                            >
                              <div
                                style={{
                                  fontSize: "14px",
                                  fontWeight: "600",
                                  color: "#374151",
                                }}
                              >
                                {category.name
                                  .replace("_s_", "'s ")
                                  .replace(/_/g, " ")
                                  .split(" ")
                                  .map(
                                    (word) =>
                                      word.charAt(0).toUpperCase() +
                                      word.slice(1),
                                  )
                                  .join(" ")}
                              </div>
                              <div
                                style={{
                                  fontSize: "12px",
                                  color: "#6b7280",
                                }}
                              >
                                {category.designs.length} designs
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ) : (
                  <div>
                    <div style={{ marginBottom: "1rem" }}>
                      <button
                        className="nc-button"
                        onClick={() => setSelectedPresetCategory(null)}
                        style={{
                          padding: "0.5rem 1rem",
                          border: "1px solid #d1d5db",
                          borderRadius: "0.375rem",
                          background: "white",
                          color: "#374151",
                          cursor: "pointer",
                          fontSize: "14px",
                        }}
                      >
                        ← Back to Categories
                      </button>
                    </div>

                    <label
                      className="customizer-preset-label"
                      style={{
                        display: "block",
                        fontSize: "14px",
                        fontWeight: "600",
                        color: "#374151",
                        marginBottom: "0.75rem",
                      }}
                    >
                      Choose Design from{" "}
                      {selectedPresetCategory.name
                        .replace("_s_", "'s ")
                        .replace(/_/g, " ")
                        .split(" ")
                        .map(
                          (word) =>
                            word.charAt(0).toUpperCase() + word.slice(1),
                        )
                        .join(" ")}
                    </label>

                    <div className="nc-preset-designs-grid customizer-preset-designs">
                      {!selectedPresetCategory.designs ||
                      selectedPresetCategory.designs.length === 0 ? (
                        <div
                          style={{
                            padding: "1rem",
                            textAlign: "center",
                            color: "#6b7280",
                          }}
                        >
                          <p>No designs available in this category</p>
                          <p
                            style={{ fontSize: "12px", marginTop: "0.5rem" }}
                          >
                            Category:{" "}
                            {selectedPresetCategory.name
                              .replace("_s_", "'s ")
                              .replace(/_/g, " ")
                              .split(" ")
                              .map(
                                (word) =>
                                  word.charAt(0).toUpperCase() + word.slice(1),
                              )
                              .join(" ")}
                          </p>
                        </div>
                      ) : (
                        <>
                          {selectedPresetCategory.designs.map(
                            (design, index) => {
                              return (
                                <PresetDesignCard
                                  key={index}
                                  $selected={
                                    selectedPresetDesign &&
                                    selectedPresetDesign.title === design.title
                                  }
                                  onClick={async () => {
                                    // Clear all other style states when selecting preset design
                                    setCustomElements([]);
                                    // Clear logo states
                                    setLogoProcessingState("idle");
                                    setProcessedLogoUrl(null);
                                    setOriginalLogoUrl(null);
                                    setProcessedBlob(null);
                                    setOriginalLogoBase64(null);
                                    processedLogoCanvasRef.current = null;
                                    presetDesignCanvasRef.current = null;
                                    setQualityWarning(null);
                                    setQualityAcknowledged(false);
                                    // Clear text states
                                    setCustomText("");
                                    // Clear jersey states
                                    setJerseyNumber("");
                                    setJerseyText("");
                                    setJerseyTextPosition("below");

                                    setSelectedPresetDesign(design);

                                    try {
                                      // Process the preset design with metallic effect
                                      const imageUrl =
                                        design.image?.src || design.image?.url;
                                      if (imageUrl) {
                                        // Store base canvas for fast color updates
                                        const metallicImageUrl =
                                          await processPresetDesignWithMetallic(
                                            imageUrl,
                                            { storeBaseCanvas: true },
                                          );

                                        // Create element with proper aspect ratio for rotation support
                                        const newElement =
                                          await createImageElementWithAspectRatio(
                                            metallicImageUrl,
                                            {
                                              isPresetDesign: true,
                                              rotation: 0,
                                            },
                                          );
                                        console.log(
                                          "Created preset design element with AR:",
                                          newElement.imageAspectRatio,
                                        );

                                        setCustomElements([newElement]);
                                        setIsDrawerOpen(false);
                                        setFocusMode(false);
                                      }
                                    } catch (error) {
                                      console.error(
                                        "Error processing preset design:",
                                        error,
                                      );

                                      // Fallback to original image if processing fails
                                      const fallbackUrl =
                                        design.image?.src || design.image?.url;
                                      const newElement =
                                        await createImageElementWithAspectRatio(
                                          fallbackUrl,
                                          {
                                            isPresetDesign: true,
                                            rotation: 0,
                                          },
                                        );

                                      setCustomElements([newElement]);
                                      setIsDrawerOpen(false);
                                      setFocusMode(false);
                                    }
                                  }}
                                >
                                  <img
                                    src={design.image?.src || design.image?.url}
                                    alt={design.title}
                                    style={{
                                      width: "100%",
                                      height: "auto",
                                      borderRadius: "0.25rem",
                                      marginBottom: "0.5rem",
                                    }}
                                  />
                                  <div
                                    style={{
                                      fontSize: "12px",
                                      fontWeight: "500",
                                      color: "#374151",
                                    }}
                                  >
                                    {design.title}
                                  </div>
                                  {/* <div style={{ fontSize: '0.625rem', color: '#10b981', fontWeight: '600' }}>
                            ${design.price}
                          </div> */}
                                </PresetDesignCard>
                              );
                            },
                          )}
                        </>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}
            {/* Mobile-only: Product & Color tab in drawer */}
            {activeTab === "product" && isMobileLayout && (
              <div className="customizer-product-tab-mobile">
                {/* Product Type */}
                <p
                  style={{
                    fontFamily: "var(--font-sans, 'Inter', sans-serif)",
                    fontSize: 10,
                    fontWeight: 600,
                    letterSpacing: "0.12em",
                    textTransform: "uppercase",
                    color: "var(--color-warm-gray, #9A9A96)",
                    margin: "0 0 10px",
                  }}
                >
                  Select Product Type
                </p>
                {loading ? (
                  <div>Loading products...</div>
                ) : (
                  <div
                    style={{
                      display: "flex",
                      gap: 8,
                      overflowX: "auto",
                      paddingBottom: 8,
                    }}
                  >
                    {collections.map((collection, idx) => {
                      const active =
                        selectedProduct?.collectionHandle === collection.handle;
                      return (
                        <div
                          key={`mobile-${collection.handle}-${idx}`}
                          className={`hj-type customizer-product-card ${active ? "customizer-product-card--active" : ""}`}
                          onClick={() => handleSelectProductType(collection)}
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            gap: 4,
                            padding: "8px 10px",
                            borderRadius: 8,
                            cursor: "pointer",
                            flexShrink: 0,
                            minWidth: 72,
                            border: `1.5px solid ${active ? "var(--color-rich-black, #1A1A1A)" : "var(--color-stone, #E8E6E1)"}`,
                            background: active
                              ? "rgba(26,26,26,0.04)"
                              : "var(--color-white, #FFFFFF)",
                            transition: "all 0.15s ease",
                          }}
                        >
                          {(() => {
                            const thumbSrc =
                              collection.image?.src ||
                              collection.products?.[0]?.images?.[0]?.src;
                            return thumbSrc ? (
                              <img
                                src={thumbSrc}
                                alt={collection.title}
                                style={{
                                  width: 28,
                                  height: 28,
                                  objectFit: "contain",
                                }}
                              />
                            ) : (
                              <svg
                                width="28"
                                height="28"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="#999"
                                strokeWidth="1.5"
                              >
                                <path d="M12 2L2 7l10 5 10-5-10-5z" />
                                <path d="M2 17l10 5 10-5" />
                                <path d="M2 12l10 5 10-5" />
                              </svg>
                            );
                          })()}
                          <span
                            style={{
                              fontSize: 9,
                              fontWeight: active ? 600 : 400,
                              textAlign: "center",
                              lineHeight: 1.2,
                            }}
                          >
                            {collection.title}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Divider */}
                <div
                  style={{
                    height: 1,
                    background: "var(--color-stone, #E8E6E1)",
                    margin: "12px 0",
                  }}
                />

                {/* Color Selection */}
                {selectedProduct && (
                  <>
                    <p
                      style={{
                        fontFamily: "var(--font-sans, 'Inter', sans-serif)",
                        fontSize: 10,
                        fontWeight: 600,
                        letterSpacing: "0.12em",
                        textTransform: "uppercase",
                        color: "var(--color-warm-gray, #9A9A96)",
                        margin: "0 0 10px",
                      }}
                    >
                      Color —{" "}
                      <span style={{ fontWeight: 400, textTransform: "none" }}>
                        {selectedColor?.color || "Select"}
                      </span>
                    </p>
                    <div
                      className="nc-color-grid customizer-v3-color-grid"
                      style={{
                        display: "grid",
                        gridTemplateColumns: "repeat(5, 1fr)",
                        gap: 6,
                      }}
                    >
                      {(selectedProduct?.allProducts || []).map(
                        (product, idx) => {
                          const colorPattern = getColorPattern(product);
                          const hexColor = colorPattern?.color;
                          // Phase 8C.6 (2026-05-21): fix latent key mismatch.
                          // The color-patterns API emits the image URL under
                          // the `image` key (see `app/api/customizer/
                          // color-patterns/route.ts` + HydroCustomizer.jsx
                          // ~ line 2085) — `imageUrl` was never populated,
                          // so the mobile bottom-sheet swatch grid silently
                          // fell back to the hex/no-image branch even for
                          // colors that had a perfectly good image swatch.
                          // Aligns this site with the desktop grid at
                          // ~ line 7180 which already uses `image`.
                          const hasImage = colorPattern?.image;
                          const sel = selectedColor?.title === product.title;
                          return (
                            <div
                              key={`mobile-color-${idx}`}
                              onClick={() => handleSelectColor(product)}
                              className={`customizer-v3-color-swatch ${sel ? "customizer-v3-color-swatch--active" : ""}`}
                              style={{
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                                gap: 3,
                                cursor: "pointer",
                                padding: 4,
                                borderRadius: 8,
                                border: sel
                                  ? "2px solid var(--color-rich-black, #1A1A1A)"
                                  : "2px solid transparent",
                              }}
                            >
                              <div
                                style={{
                                  width: 32,
                                  height: 32,
                                  borderRadius: "50%",
                                  border: "1px solid rgba(0,0,0,0.08)",
                                  overflow: "hidden",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  background: hasImage
                                    ? "transparent"
                                    : hexColor || "#eee",
                                }}
                              >
                                {hasImage && (
                                  <img
                                    src={colorPattern.image}
                                    alt=""
                                    style={{
                                      width: "100%",
                                      height: "100%",
                                      objectFit: "cover",
                                    }}
                                  />
                                )}
                              </div>
                              <span
                                style={{
                                  fontSize: 8,
                                  fontWeight: sel ? 600 : 400,
                                  textAlign: "center",
                                  lineHeight: 1.1,
                                  maxWidth: 52,
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                {product.title
                                  ?.replace(selectedProduct.productType, "")
                                  .replace(/[-–]/g, "")
                                  .trim() || product.color}
                              </span>
                            </div>
                          );
                        },
                      )}
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        </SlideDrawer>
      </div>

      {/* ═══ MOBILE-ONLY: Bottom Tab Bar + Sticky Cart ═══ */}
      {isMobileLayout && (
        <>
          {/* Step-based Tab Bar — two big labeled buttons instead of icons. */}
          <MobileStepTabBar
            activeTab={activeTab}
            isOpen={isDrawerOpen}
            onStepSelect={(step) => {
              if (step === "product") {
                setActiveTab("product");
                setIsDrawerOpen(true);
                setFocusMode(false);
              } else {
                // Step 2: Design → the menu screen. The user picks
                // Upload / Text / Jersey / Gallery from the stacked list
                // inside the sheet. `activeTab = "design"` is a menu-only
                // state that doesn't match any of the auto-update effects
                // gated on the specific sub-tab names.
                setActiveTab("design");
                setIsDrawerOpen(true);
                setFocusMode(true);
              }
            }}
            hasAnyDesign={hasAnyDesign}
          />

          {/* Sticky Add to Bag — wholesale variant carries the qty-6 stepper */}
          <MobileStickyCart
            price={customizerTotal}
            currencyCode={customizerCurrency}
            quantity={quantity}
            onQtyChange={setQuantity}
            onAddToCart={handleAddToCartWithTerms}
            disabled={
              addingToCart ||
              logoProcessingState === "processing" ||
              logoProcessingState === "threshold" ||
              !hasAnyDesign
            }
            processing={logoProcessingState}
            addingToCart={addingToCart}
          />
        </>
      )}

      {/* Terms and Conditions Modal — portaled to body so transformed
          ancestors in the customizer tree don't clip the stacking context
          and let the site header bleed through. */}
      {showTermsModal &&
        typeof document !== "undefined" &&
        createPortal(
          <div className="nc-terms-modal-overlay" onClick={handleTermsCancel}>
            <div
              className="nc-terms-modal-content"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="nc-terms-modal-header">
                <h2>Please confirm</h2>
                <button
                  className="nc-terms-modal-close-button"
                  onClick={handleTermsCancel}
                >
                  ×
                </button>
              </div>

              <div className="nc-terms-modal-body">
                <h3>All sales of customized products are final:</h3>
                <ul>
                  <li>
                    Customized items are non-refundable and cannot be canceled
                    once you have submitted the order.
                  </li>
                  <li>
                    No edits or changes can be made once you have submitted your
                    order.
                  </li>
                  <li>
                    Any adjustments or resizing should be made prior to
                    submitting the order.
                  </li>
                </ul>

                <h3>
                  "Upload Design" customization quality is dependent on the
                  artwork submitted:
                </h3>
                <ul>
                  <li>
                    Photographs, AI-artwork, and QR codes are not compatible
                    with laser engraving and are not recommended.
                  </li>
                  <li>
                    Check for stray marks or dots and crop these out or they
                    will be included in your customization.
                  </li>
                </ul>

                <h3>
                  By using this service, you confirm that your submitted text
                  and artwork you have selected for customization:
                </h3>
                <ul>
                  <li>
                    does not infringe on any trademark, copyright, design
                    rights, or other proprietary rights of any third party and
                    does not otherwise violate anyone's rights or other
                    categories listed in our FAQ.
                  </li>
                  <li>
                    is not unlawful, harmful, threatening, tortious, defamatory,
                    vulgar, obscene, libelous, invasive of another's privacy,
                    hateful, or racially, ethnically, or otherwise
                    objectionable.
                  </li>
                  <li>
                    does not include any sensitive personal information (e.g.,
                    driver's license, SSN, payment card info, etc.).
                  </li>
                  <li>
                    does not otherwise violate anyone's rights or other
                    categories listed in our FAQ.
                  </li>
                </ul>

                <p>
                  All custom orders will be reviewed. If any of the above terms
                  are violated, your full custom order will be cancelled.
                </p>

                <p>
                  If you need any assistance with a custom drinkware order,
                  including if you want to place an order for a trademark or
                  copyright image that you have authorization for or have
                  questions, please reach out to our service team prior to
                  placing your order.{" "}
                  <a
                    href="mailto:service@thehydrojug.com"
                    style={{ color: "#0066cc", textDecoration: "underline" }}
                  >
                    service@thehydrojug.com
                  </a>
                </p>

                <p>
                  For bulk orders, please check out our{" "}
                  <a href="https://wholesale.thehydrojug.com" target="_blank">
                    Corporate Sales website
                  </a>{" "}
                  for info including quotes.
                </p>
              </div>

              <div className="nc-terms-modal-footer">
                <div className="nc-terms-checkbox-container">
                  <input
                    type="checkbox"
                    id="terms-checkbox"
                    checked={termsAccepted}
                    onChange={(e) => setTermsAccepted(e.target.checked)}
                  />
                  <label htmlFor="terms-checkbox">
                    I accept these terms and conditions
                  </label>
                </div>

                <div className="nc-terms-button-container">
                  <button
                    className="nc-terms-button cancel"
                    onClick={handleTermsCancel}
                  >
                    Cancel
                  </button>
                  <button
                    className="nc-terms-button accept"
                    disabled={!termsAccepted}
                    onClick={handleTermsAccept}
                  >
                    Accept
                  </button>
                </div>
              </div>
            </div>
          </div>,
          document.body,
        )}

      {/* Moderation Overlay */}
      {/* {isModerating && (
        <ModerationOverlay>
          <ModerationContent>
            <Spinner />
            <h2 style={{ margin: '0 0 1rem 0', fontSize: '1.5rem', fontWeight: '600', color: '#333' }}>
              Moderating Image
            </h2>
            <p style={{ margin: '0', fontSize: '1rem', color: '#666', lineHeight: '1.6' }}>
              Please wait while we review your image for compliance. This may take a few moments.
            </p>
            <p style={{ margin: '1rem 0 0 0', fontSize: '0.875rem', color: '#999', fontStyle: 'italic' }}>
              Please do not refresh or navigate away from this page.
            </p>
          </ModerationContent>
        </ModerationOverlay>
      )} */}

      {/* Moderation Error Modal — portaled to body so it floats above the
          customizer's stacking context. */}
      {showModerationErrorModal &&
        typeof document !== "undefined" &&
        createPortal(
          <div
            className="nc-terms-modal-overlay"
            onClick={() => setShowModerationErrorModal(false)}
          >
            <div
              className="nc-terms-modal-content"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="nc-terms-modal-header">
                <h2>⚠️ Image Flagged</h2>
                <button
                  className="nc-terms-modal-close-button"
                  onClick={() => setShowModerationErrorModal(false)}
                >
                  ×
                </button>
              </div>

              <div className="nc-terms-modal-body">
                <p
                  style={{
                    fontSize: "16px",
                    lineHeight: "1.6",
                    marginBottom: "1rem",
                  }}
                >
                  This image was flagged for violating our terms. The product
                  was not added to cart.
                </p>
                <p
                  style={{
                    fontSize: "16px",
                    lineHeight: "1.6",
                    marginBottom: "1rem",
                  }}
                >
                  Please try again with a different image.
                </p>
                <p style={{ fontSize: "16px", lineHeight: "1.6" }}>
                  If you think this was a mistake, reach out to us at{" "}
                  <a
                    href="mailto:service@thehydrojug.com"
                    style={{ color: "#0066cc", textDecoration: "underline" }}
                  >
                    service@thehydrojug.com
                  </a>
                </p>
              </div>

              <div className="nc-terms-modal-footer">
                <div
                  className="nc-terms-button-container"
                  style={{ justifyContent: "center" }}
                >
                  <button
                    className="nc-terms-button accept"
                    onClick={() => setShowModerationErrorModal(false)}
                    style={{ minWidth: "120px" }}
                  >
                    OK
                  </button>
                </div>
              </div>
            </div>
          </div>,
          document.body,
        )}

      {/* Loading Modal */}
      {showLoadingModal && (
        <div className="nc-loading-modal-overlay">
          <div className="nc-loading-modal-content">
            <div className="nc-loading-modal-header">
              <h2>Saving your upload, please wait</h2>
            </div>

            {(() => {
              const displayUploadProgress = Math.round(Math.max(uploadProgress, smoothUploadProgress));
              return (
                <div className="nc-loading-progress-container">
                  <div className="nc-loading-percentage">{displayUploadProgress}%</div>
                  {/* Track = solid stone backgroundColor + 1px border (always
                      visible). Fill = a backgroundImage gradient layer painted
                      dark 0→NN%, transparent after (revealing the stone track).
                      No child element / no height:100% to collapse. */}
                  <div
                    className="nc-loading-progress-bar"
                    role="progressbar"
                    aria-valuenow={displayUploadProgress}
                    aria-valuemin={0}
                    aria-valuemax={100}
                    style={{
                      width: "100%",
                      height: 14,
                      borderRadius: 7,
                      overflow: "hidden",
                      border: "1px solid #d4d2cd",
                      backgroundColor: "#e8e6e1",
                      backgroundImage: `linear-gradient(to right, #1a1a1a, #1a1a1a ${displayUploadProgress}%, transparent ${displayUploadProgress}%, transparent 100%)`,
                      backgroundRepeat: "no-repeat",
                      transition: "background-image 0.2s ease",
                    }}
                  />
                </div>
              );
            })()}
          </div>
        </div>
      )}

      {/* Logo Review Modal */}
      {showReviewModal && processedLogoUrl && (
        <div className="nc-review-overlay">
          <div className="nc-review-header">
            <p>
              Review your design carefully. Everything shown here will be
              printed.
            </p>
          </div>

          {/* Threshold Controls (Invert button temporarily disabled) */}
          <div
            style={{
              padding: "0.75rem 1rem",
              background: "#e8f0f5",
              display: "flex",
              justifyContent: "center",
            }}
          >
            <div
              style={{
                background: "white",
                padding: "0.75rem 1rem",
                borderRadius: "0.5rem",
                boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                width: "100%",
                maxWidth: "500px",
                display: "flex",
                alignItems: "center",
                gap: "1rem",
              }}
            >
              {/* Invert Button - Temporarily disabled, keeping logic intact for future use
              <button
              onClick={() => {
                const newInvert = !invertEtch;
                setInvertEtch(newInvert);
                if (originalLogoUrl) {
                  requestAnimationFrame(() => {
                    processImageLocally(originalLogoUrl, { 
                      thresholdValue, 
                      invert: newInvert,
                      keepDrawerOpen: true,
                        preview: true,
                        isSVG: isSourceSVG
                    });
                  });
                }
              }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.375rem',
                  padding: '0.5rem 0.75rem',
                  background: invertEtch ? '#1e3a5f' : '#f3f4f6',
                  border: `1px solid ${invertEtch ? '#1e3a5f' : '#d1d5db'}`,
                  borderRadius: '0.375rem',
                  fontSize: '0.75rem',
                  fontWeight: '600',
                  color: invertEtch ? 'white' : '#374151',
                  cursor: 'pointer',
                  textTransform: 'uppercase',
                  letterSpacing: '0.025em',
                  whiteSpace: 'nowrap',
                  flexShrink: 0,
                  transition: 'all 0.15s'
                }}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M7 16V4m0 0L3 8m4-4l4 4M17 8v12m0 0l4-4m-4 4l-4-4" />
              </svg>
                Invert
              </button>
              */}

              {/* Threshold Slider */}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    marginBottom: "0.375rem",
                  }}
                >
                  <span style={{ fontSize: "12px", color: "#9ca3af" }}>
                    Light
                  </span>
                  <span
                    style={{
                      fontSize: "13px",
                      color: "#374151",
                      fontWeight: "600",
                    }}
                  >
                    Adjust Contrast
                  </span>
                  <span style={{ fontSize: "12px", color: "#9ca3af" }}>
                    Heavy
                  </span>
                </div>
                <input
                  type="range"
                  min="20"
                  max="250"
                  step="10"
                  value={thresholdValue}
                  onChange={(e) => {
                    const newValue = Number(e.target.value);
                    setThresholdValue(newValue);
                    handleThresholdChangeFullResDebounced(newValue);
                  }}
                  style={{
                    width: "100%",
                    height: "6px",
                    borderRadius: "3px",
                    background: "#e5e7eb",
                    outline: "none",
                    cursor: "pointer",
                  }}
                />
              </div>
            </div>
          </div>

          {/* Compact Quality Warning Toast - floats over image area */}
          {qualityWarning && !qualityAcknowledged && (
            <div
              style={{
                position: "absolute",
                top: "220px",
                left: "50%",
                transform: "translateX(-50%)",
                zIndex: 100,
                width: "90%",
                maxWidth: "420px",
                background: "white",
                borderRadius: "0.75rem",
                boxShadow: "0 10px 40px rgba(0,0,0,0.25)",
                overflow: "hidden",
                border: `2px solid ${qualityWarning.type === "photo_detected" || qualityWarning.type === "fine_detail" ? "#ef4444" : "#f59e0b"}`,
                animation: "slideDown 0.3s ease-out",
              }}
            >
              {/* Warning Header */}
              <div
                style={{
                  background:
                    qualityWarning.type === "photo_detected" ||
                    qualityWarning.type === "fine_detail"
                      ? "#fef2f2"
                      : "#fffbeb",
                  padding: "0.75rem 1rem",
                  display: "flex",
                  alignItems: "center",
                  gap: "0.625rem",
                  borderBottom: `1px solid ${qualityWarning.type === "photo_detected" || qualityWarning.type === "fine_detail" ? "#fecaca" : "#fde68a"}`,
                }}
              >
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill={
                    qualityWarning.type === "photo_detected" ||
                    qualityWarning.type === "fine_detail"
                      ? "#ef4444"
                      : "#f59e0b"
                  }
                  style={{ flexShrink: 0 }}
                >
                  <path d="M12 2L1 21h22L12 2zm0 3.5L19.5 19h-15L12 5.5zM11 10v4h2v-4h-2zm0 6v2h2v-2h-2z" />
                </svg>
                <span
                  style={{
                    fontWeight: "600",
                    fontSize: "14px",
                    color:
                      qualityWarning.type === "photo_detected" ||
                      qualityWarning.type === "fine_detail"
                        ? "#b91c1c"
                        : "#b45309",
                  }}
                >
                  {qualityWarning.type === "photo_detected" &&
                    "Photograph/AI Image Detected"}
                  {qualityWarning.type === "fine_detail" &&
                    "Fine Line Work / High Detail Detected"}
                  {qualityWarning.type === "complex_detail" &&
                    "Complex Image Detected"}
                  {qualityWarning.type === "very_low_res" &&
                    "Very Low Resolution Image"}
                  {qualityWarning.type === "low_res" &&
                    "Low Resolution Image Detected"}
                </span>
                {/* {qualityWarning.originalSize && (
                  <span style={{ 
                    marginLeft: 'auto',
                    fontSize: '0.75rem',
                    color: '#6b7280',
                    background: '#f3f4f6',
                    padding: '0.125rem 0.5rem',
                    borderRadius: '1rem'
                  }}>
                    {qualityWarning.originalSize}
                  </span>
                )} */}
              </div>

              {/* Warning Body */}
              <div style={{ padding: "0.875rem 1rem" }}>
                <p
                  style={{
                    margin: "0 0 0.75rem 0",
                    color: "#4b5563",
                    fontSize: "13px",
                    lineHeight: "1.5",
                  }}
                >
                  {qualityWarning.message}
                </p>

                {/* Compact Checkbox */}
                <label
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "0.625rem",
                    cursor: "pointer",
                    padding: "0.625rem 0.75rem",
                    background: qualityAcknowledged ? "#ecfdf5" : "#f9fafb",
                    borderRadius: "0.5rem",
                    border: `1px solid ${qualityAcknowledged ? "#10b981" : "#e5e7eb"}`,
                    transition: "all 0.15s",
                  }}
                >
                  <input
                    type="checkbox"
                    checked={qualityAcknowledged}
                    onChange={(e) => setQualityAcknowledged(e.target.checked)}
                    style={{
                      width: "16px",
                      height: "16px",
                      cursor: "pointer",
                      accentColor: "#10b981",
                    }}
                  />
                  <span
                    style={{
                      fontSize: "13px",
                      color: qualityAcknowledged ? "#047857" : "#4b5563",
                      fontWeight: qualityAcknowledged ? "500" : "400",
                    }}
                  >
                    {qualityWarning.type === "photo_detected" &&
                      "I understand and want to proceed"}
                    {qualityWarning.type === "fine_detail" &&
                      "I understand details may blend together"}
                    {qualityWarning.type !== "photo_detected" &&
                      qualityWarning.type !== "fine_detail" &&
                      "I understand and accept"}
                  </span>
                  {qualityAcknowledged && (
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="#10b981"
                      style={{ marginLeft: "auto" }}
                    >
                      <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z" />
                    </svg>
                  )}
                </label>
              </div>
            </div>
          )}

          <div className="nc-review-image-container">
            {/* Small acknowledged indicator */}
            {qualityWarning && qualityAcknowledged && (
              <div
                style={{
                  position: "absolute",
                  top: "0.75rem",
                  left: "50%",
                  transform: "translateX(-50%)",
                  background: "#fef3c7",
                  border: "1px solid #f59e0b",
                  borderRadius: "2rem",
                  padding: "0.375rem 1rem",
                  display: "flex",
                  alignItems: "center",
                  gap: "0.5rem",
                  fontSize: "12px",
                  color: "#92400e",
                  fontWeight: "500",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                  zIndex: 50,
                  whiteSpace: "nowrap",
                }}
              >
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="#f59e0b"
                  style={{ flexShrink: 0 }}
                >
                  <path d="M12 2L1 21h22L12 2zm0 3.5L19.5 19h-15L12 5.5zM11 10v4h2v-4h-2zm0 6v2h2v-2h-2z" />
                </svg>
                <span>Quality notice acknowledged</span>
                <button
                  onClick={() => {
                    setQualityAcknowledged(false);
                  }}
                  style={{
                    background: "none",
                    border: "none",
                    padding: "0",
                    cursor: "pointer",
                    color: "#92400e",
                    fontSize: "14px",
                    lineHeight: 1,
                    flexShrink: 0,
                  }}
                  title="Show warning again"
                >
                  ⓘ
                </button>
              </div>
            )}
            <div className="nc-review-image-wrapper">
              {/* canvas instead of img: renderThresholdFast writes directly here,
                  eliminating toDataURL/PNG-encoding overhead entirely. */}
              <canvas className="nc-review-image" ref={previewCanvasRef} />
            </div>
          </div>

          <div className="nc-review-footer">
            <ReviewButton
              onClick={async () => {
                // Cancel - close modal and reset settings, but keep image in drawer
                // The logo should NOT appear on the bottle until Accept is clicked
                setShowReviewModal(false);
                setShowThresholdSlider(false);

                // Clear any rendering from the bottle (nothing should show until Accept)
                setCustomElements([]);

                // Reset threshold and invert to defaults
                const defaultThreshold = 128;
                const defaultInvert = false;
                setThresholdValue(defaultThreshold);
                setInvertEtch(defaultInvert);

                // Clear quality warning state
                setQualityWarning(null);
                setQualityAcknowledged(false);

                // Re-process the image with default settings so drawer preview matches defaults
                // skipModal prevents the review modal from re-opening immediately
                if (originalLogoUrl) {
                  await processImageLocally(originalLogoUrl, {
                    thresholdValue: defaultThreshold,
                    invert: defaultInvert,
                    keepDrawerOpen: true,
                    preview: true, // Use preview mode for consistent sizing
                    skipModal: true, // Don't re-open the modal
                    isSVG: isSourceSVG, // Pass SVG flag for appropriate handling
                  });
                }
              }}
            >
              CANCEL
            </ReviewButton>
            <ReviewButton
              $primary
              disabled={
                (qualityWarning && !qualityAcknowledged) || isAcceptingLogo
              }
              style={{
                opacity:
                  (qualityWarning && !qualityAcknowledged) || isAcceptingLogo
                    ? 0.5
                    : 1,
                cursor:
                  (qualityWarning && !qualityAcknowledged) || isAcceptingLogo
                    ? "not-allowed"
                    : "pointer",
              }}
              onClick={async () => {
                // Don't proceed if quality warning not acknowledged
                if (qualityWarning && !qualityAcknowledged) return;

                // Run a full-quality render now so processedBlob (used at cart time)
                // and the canvas URL reflect the user's chosen threshold.
                setIsAcceptingLogo(true);
                try {
                  const result = await processImageLocally(originalLogoUrl, {
                    thresholdValue:
                      pendingThresholdValueRef.current ?? thresholdValue,
                    keepDrawerOpen: true,
                    invert: invertEtch,
                    skipModal: true,
                    isSVG: isSourceSVG,
                  });
                  setShowReviewModal(false);
                  setShowThresholdSlider(false);
                  const finalUrl = result?.displayUrl || processedLogoUrl;
                  if (finalUrl) {
                    addProcessedLogoToCanvas(finalUrl, {
                      keepDrawerOpen: true,
                      preserveTransform: true,
                    });
                  }
                  // Mobile: close the design drawer so the user can see the
                  // logo they just accepted actually rendered on the bottle.
                  // Desktop keeps the drawer as-is — its side rail doesn't
                  // cover the canvas.
                  if (isMobileLayout) {
                    setIsDrawerOpen(false);
                    setFocusMode(false);
                  }
                } catch (e) {
                  console.error("Error finalising logo:", e);
                  setShowReviewModal(false);
                  setShowThresholdSlider(false);
                } finally {
                  setIsAcceptingLogo(false);
                }
              }}
            >
              {isAcceptingLogo ? "Processing..." : "ACCEPT"}
            </ReviewButton>
          </div>
        </div>
      )}
    </div>
  );
}

export default component((node, ctx) => {
  const element = <HydroCustomizer node={node} ctx={ctx} />;
  const root = createRoot(node);
  root.render(element);

  return function () {
    root.unmount();
  };
});
